// The fleet layer of the WebGL2 client (R431): the port of
// `render/fleets.ts` onto instance batches and a structure of arrays.
//
// The public surface is the PixiJS layer's, name for name — `applyState`,
// `own`, `describe`, `info`, `at`, `pick`, `count`, `setGroups`,
// `setSquadIds`, `selectedId`, `aimingId`, `home`, `gunRangeOf`,
// `baseAimRange`, `viewerFaction` — so the handler code of
// `main.ts` and of `arcade/match.ts` copies over unchanged.
//
// What changed is underneath. The PixiJS layer holds one object and one
// `Sprite` per fleet and re-tessellates five `Graphics` every frame
// (`fleets.ts:956`, `:1000`, `:1101`, `:1136`, `:1316`). This holds one
// typed array per field, a dense prefix `0..n`, and a swap-remove when a
// contact has finished fading out; and it writes ONE set of instance
// buffers in `pack()`, six draw calls for the whole layer (the plan's A.5
// row 12).
//
// The sixth call is the readout. The bars and the contact frame were flat
// quads on a solid white texel, so their edge was the quad's geometry and
// only MSAA made it smooth. They are segments now — a butt-capped line of
// thickness `h` IS the rectangle — and the segment shader ramps its own
// edge. The batch is flushed between the rings and the marks, which is the
// place the bars held inside the mark batch, so the paint order does not
// move.
//
// Everything a mark is drawn at is the figure the PixiJS layer draws it at.
// Where that figure lives in `presentation.toml` it is imported; where the
// layer of before R505 states it as a literal it is a named constant here that
// cites the line it came from, because a port must not move a value.

import {
  outside, type FrameInfo, type Layer, type DrawCtx,
  type ViewRect, F_ADD, F_ROUND, F_SCREEN,
} from '../layer';
import {
  HULL_CLASSES, badgeKey, damageAdvancePx, damageGlyphKey, factionPipKey, hullKey, liveryHullKey,
  liveryMaskKey, npcKey, pirateHullKey, pirateLightKey,
} from '../gfx/atlas';
import {
  BOARDING_LOOK, boardingShare, cropOf, drainedShare, packGrapple, packRing, packSnap,
  packWipeEdge, wipeX, type Pose,
} from './boarding';
import { pirateLightAlpha, pirateLightOf, pirateLightXs, type PirateLight } from '../../render/npc-marks';
import { reducedMotion } from '../../render/motion';
// The charge pip of card 02 of round 075 (R1568, ui-map-interface.md §6.1).
import { wearsPip } from '../../render/faction-marks';
import type { HoldEra } from '../../render/hold-marks';
// The livery of the Fleet Designer (R1252, §6.1.6).
import { liveryOfWire, liveryPlan } from '../../render/livery';
import { cornerBrackets, growColumns, grown, moveSlot, zoomOf } from './shared';
import { glyphKeys } from './damage';
import { AUTO_SCENE_LOOK, type SceneLook } from '../../render/scene-look';
import type { BatchSource, CircleSink, QuadSink, SegmentSink, SpriteEntry } from './batches';
import { PivotCircles, PivotQuads, PivotSegments, type Pivot } from './pivotscale';
import { factionColor, MPX_PER_PX, mpxToPx } from '../../world';
import { NUM_FACTIONS } from '../../gen/balance';
import type { Condition, FleetState, SeaObjectStock, Waypoint } from '../../gen/proto/aquaphobia';
import { AnchorState, ConditionKey, HitAspect, Mission, NpcKind, ShipClass } from '../../gen/proto/aquaphobia';
import type { WardAura } from './sites';
// The pose the gun-shot layer reads, filled into one reused record (R131).
import type { CombatHullPose } from './combat';
import type { FleetApproach, UIFleet } from '../../ui/overlay';
import { bowOfWire, turnToward } from '../../render/bow';
import { rearEdges, rearHatch, type RearEdges } from '../../render/reararc';
// The signature order looks (R2408, §6.1.12): the shares this layer reads.
import { floorHeldOrder, type SignatureMarks, type SignaturePose } from '../../render/signature-looks';
// The aspect chevrons of round 104 card 10 (R2407, §6.1.11).
import {
  CHEVRON_FLASH_KEY, CHEVRON_KEY, arcCentre, beamSide, chevronCount, chevronInk,
  chevronSetKey, firstChevronR, chevronTarget, chevronsReadAt, gapOf, inHostileReach, readsBowAllRound,
  type ChevronGap,
} from '../../render/aspect-chevrons';
import type { FleetPresence } from '../../ui/siegewindow';
import { holdTotal, hullBand } from '../../ui/format';
import { countOf, firingKind, totalOf, type Stock } from '../../ui/rounds';
import { lift } from '../../render/marks';
// The ship icon set a class wears under the look in force (R835).
import { shipStyleOf } from '../../render/hull-styles';
import {
  OWN_FLEET_MARKS, SLOT_NUMERAL_MAX, SQUAD_FLEET_MARKS,
  type FleetMarkStyle, type SlotNumeral,
} from '../../render/fleet-marks';
import type { FleetInfo } from '../../render/fleet-info';
import type { GatherCut, GatherHull } from '../../render/gather-state';
import type { ChartFleet } from '../chart/context';
import {
  FACTION_MARKS, FLEET_ANCHOR_GOLD, FLEET_BARS, FLEET_BEAM,
  FLEET_CARGO_COLOR, FLEET_GROUP_BADGE_ALPHA, FLEET_GROUP_BADGE_GAP,
  FLEET_GROUP_BADGE_H,
  FLEET_COURSE, FLEET_ORDER_COLORS, FLEET_ORDER_DRAG, FLEET_OWN_HULL_SCALE, FLEET_REACH_RING, FLEET_STACK,
  FLEET_AURA, FLEET_ASPECT, FLEET_PRESSURE_ARC,
  FLEET_SPEED_SMOOTH_PER_SEC, FLEET_TENDER, DAMAGE_FIGURE, FLEET_MARKS,
  GATHER_FIGURE, GATHER_LIVE_LANE, ANCHOR_LIMITS, HOLD_FIRE_LIMITS, RELOAD_LIMITS,
  NPC_INK, SIGNATURE, MARKER_NEUTRAL, SEA_OBJECTS, HOVER_DASHES, HOVER_DASH_FILL,
} from '../../gen/presentation';
import { primaryReachOf, primaryReachPx, reachInk } from './primary-reach';
import type { FleetBars } from '../../gen/presentation';
import { lighten } from '../../render/gather-recipe-kit';
import { clamp01, ease } from '../../render/recipe-kit';
import { out as easeOutQuad } from '../../render/combat-recipe-kit';
import { GatherFigures, GatherFigureKind } from '../../render/gather-figures';
import { depositInk } from '../../render/marks';
import { BAR_STYLES, type BarValues, type RectSink } from '../../render/bar-styles';
import {
  inkOf, ringPassed, waveU, wavePhase, type Breath,
} from '../../render/tender';
import { BEAM_STYLES, type BeamCtx } from '../../render/beam-styles';
import {
  AURA_STYLES, auraCells, auraR, type AuraCtx,
} from '../../render/aura-styles';
import {
  ANCHOR_SINCE_UNSEEN, anchorAlpha, anchorLook, type AnchorCtx,
} from '../../render/anchor-styles';
import { holdFireLook, type HoldFireCtx } from '../../render/holdfire-styles';
import { reloadLook, shapeOf, type ReloadCtx } from '../../render/reload-styles';
import { ChartGlyphs, type RunOpts } from '../chart/glyphs';
import { card as CARD_S, chart as CHART_S } from '../../gen/strings';
import { stepPx } from '../chart/steps';
import type { OwnHull } from '../touchgeom';

/**
 * The tubes of one class loaded with one kind, as the tube reload look
 * reads them (R1474): the magazine the fleet card's TUBES bar divides by
 * (R836), the kind the tubes hold (the class's first kind when the fleet
 * sailed with none) and the catalog's name of that kind.
 */
export interface ReloadRound { mag: number; kind: number; name: string }

/**
 * What `firedAt` and `readyAt` hold for a moment the client never saw: far
 * enough back that no beat of any look is still running (R1474).
 */
const RELOAD_UNSEEN = 1e6;

/** What this layer reads back out of the gather tracker (R1120). */
interface GatherReader { get(fleetId: number): GatherCut | null }

/**
 * The sinks that draw the marks of ONE hull (R1981, ui-map-interface.md
 * §6.1): the hull, its bloom, its bracket, its readout, its own marks and
 * the anchoring, hold-fire and reload looks. The mark scale draws each of
 * them through a pivot wrapper; every other sink of the layer states a
 * map length and keeps it.
 */
interface HullSinks {
  markQuads: QuadSink;
  bloomQuads: QuadSink;
  rings: CircleSink;
  barSegs: SegmentSink;
  bracketSegs: SegmentSink;
  anchorQuads: QuadSink;
  anchorSegs: SegmentSink;
  holdRings: CircleSink;
  holdSegs: SegmentSink;
  reloadUnderRings: CircleSink;
  reloadUnderSegs: SegmentSink;
  reloadGround: CircleSink;
  reloadSegs: SegmentSink;
  reloadRings: CircleSink;
  reloadQuads: QuadSink;
}

/** The hull sinks of `raw`, each wrapped to scale about `p`. */
function pivotSinks(raw: HullSinks, p: Pivot): HullSinks {
  return {
    markQuads: new PivotQuads(raw.markQuads, p),
    bloomQuads: new PivotQuads(raw.bloomQuads, p),
    rings: new PivotCircles(raw.rings, p),
    barSegs: new PivotSegments(raw.barSegs, p),
    bracketSegs: new PivotSegments(raw.bracketSegs, p),
    anchorQuads: new PivotQuads(raw.anchorQuads, p),
    anchorSegs: new PivotSegments(raw.anchorSegs, p),
    holdRings: new PivotCircles(raw.holdRings, p),
    holdSegs: new PivotSegments(raw.holdSegs, p),
    reloadUnderRings: new PivotCircles(raw.reloadUnderRings, p),
    reloadUnderSegs: new PivotSegments(raw.reloadUnderSegs, p),
    reloadGround: new PivotCircles(raw.reloadGround, p),
    reloadSegs: new PivotSegments(raw.reloadSegs, p),
    reloadRings: new PivotCircles(raw.reloadRings, p),
    reloadQuads: new PivotQuads(raw.reloadQuads, p),
  };
}

// ---------------------------------------------------------------------------
// The figures the PixiJS layer states as literals
// ---------------------------------------------------------------------------

/** The selection bracket's radius and arm, in map px (`fleets.ts:186`, `:1269-1274`). */
const BRACKET_R = 14;
const BRACKET_ARM = 5;
const BRACKET_WIDTH = 1.6;
const BRACKET_ALPHA = 0.9;

/** The wake ledger (`fleets.ts:841-850`, `:955-971`). */
const WAKE_POINTS = 9;
const WAKE_INTERVAL_S = 0.12;
const WAKE_MIN_STEP_PX = 0.4;
const WAKE_WIDTH = 2;
/** A contact under this reveal alpha shows no wake and no anchor ring (§5). */
const FAINT_ALPHA = 0.3;

/** The mission overlay's ink (`fleets.ts:1002-1015`, `:1062`). */
export const ORDER_WIDTH = 1.2;
/** Dashes per turn and the lit fraction of one, from `dashCircle` (`:985-991`). */
export const ORDER_DASHES = 8;
export const ORDER_DASH_FILL = 0.6;
/** The ring of a hull an order names, the escort's ring (R544). A command
 *  ship whose formation is on wears it one time (R2617). */
const HULL_RING_R = 16;
/** The ring of an order point, the move's ring. The formation station of
 *  a member that stands off it wears it (R2617). */
const POINT_RING_R = 7;

/** The laden hauler's ring (`fleets.ts:1116-1119`). */
// Exported for one reader: the `hold-ring` gather look draws this ring in its
// place, so a test pins its row to these three (R1120).
export const LADEN_R = 16;
export const LADEN_WIDTH = 1.5;
export const LADEN_ALPHA = 0.7;

/**
 * The charge pip key of every faction in one reading (R1568), by faction id.
 * Slot 0 and the slots past the roster stay empty: no faction, no pip.
 */
function pipKeysOf(era: HoldEra): string[] {
  const out: string[] = [];
  for (let f = 1; f <= NUM_FACTIONS; f++) out[f] = factionPipKey(era, f);
  return out;
}

/** The glyph key of one numeral of an anchoring look, built once a
 *  character, so a frame builds no key string (R1125). */
const anchorGlyphKeys = new Map<string, string>();
function anchorGlyphKey(ch: string): string {
  let k = anchorGlyphKeys.get(ch);
  if (k === undefined) {
    k = damageGlyphKey(DAMAGE_FIGURE.weight, ch);
    anchorGlyphKeys.set(ch, k);
  }
  return k;
}

/** The legend of the hold-fire look: the bare cells, centred (R1439). */
const HOLD_LEGEND_OPTS: RunOpts = { anchor: 'centre', halo: false };

/** The one word the gather figure prints, built once (R1123). */
const FULL_KEYS = glyphKeys(DAMAGE_FIGURE.weight, 'FULL');

/** The atlas key of each light of a brotherhood mark, built once, so a
 *  pirate hull builds no key string a frame (§10.4). */
const PIRATE_LIGHT_KEYS: Readonly<Record<PirateLight, string>> = {
  lantern: pirateLightKey('lantern'), knot: pirateLightKey('knot'),
};

/** The atlas key of each rest set of the aspect chevrons, built once (§10.4). */
const CHEVRON_SET_KEYS: Readonly<Record<ChevronGap, string>> = {
  none: chevronSetKey('none'), stern: chevronSetKey('stern'),
  'side+': chevronSetKey('side+'), 'side-': chevronSetKey('side-'),
};

/** The two chip cells the gather figure wears: filled, and hollow in the
 *  shaft (`fast/gfx/atlas.ts gatherSpecFor`). */
const GATHER_CHIP_KEY = 'gather:chip';
const GATHER_CHIP_HOLLOW_KEY = 'gather:chip-hollow';

/**
 * The box a dash of the live cargo lane is cut against, and the cut it
 * answers with (R1122).
 *
 * It is ONE reused record: `clipSegment` writes the surviving ends into
 * `x0..y1` and the caller reads them at once, so the march allocates nothing
 * however many dashes it walks (§10.4).
 */
export interface ClipBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * Cuts one segment against the box, the Liang-Barsky way, and answers
 * whether anything survived (R1122).
 *
 * The page clips the march with a canvas clip path. An instanced batch has
 * none, so the dash is cut by arithmetic instead: the answer is the same
 * picture and it costs no state change and no allocation.
 */
export function clipSegment(
  b: ClipBox, x0: number, y0: number, x1: number, y1: number,
): boolean {
  const dx = x1 - x0;
  const dy = y1 - y0;
  let t0 = 0;
  let t1 = 1;
  for (let side = 0; side < 4; side++) {
    // p is the outward rate along this side, q how far inside the start is.
    const p = side === 0 ? -dx : side === 1 ? dx : side === 2 ? -dy : dy;
    const q = side === 0 ? x0 - b.left : side === 1 ? b.right - x0
      : side === 2 ? y0 - b.top : b.bottom - y0;
    if (p === 0) {
      // Parallel to this side: it survives only where it starts inside.
      if (q < 0) return false;
      continue;
    }
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
  }
  if (t1 <= t0) return false;
  b.x0 = x0 + dx * t0;
  b.y0 = y0 + dy * t0;
  b.x1 = x0 + dx * t1;
  b.y1 = y0 + dy * t1;
  return true;
}

/**
 * The entry an aura context holds before the pack resolves the real one.
 *
 * A style that draws no sprite never reads it, and a style that does gets
 * the baked cell handed in at the top of `packAuras`.
 */
const NO_ENTRY: SpriteEntry = {
  key: '', u0: 0, v0: 0, u1: 0, v1: 0, w: 0, h: 0, ox: 0, oy: 0,
};

/** The r19 anchor ring (`fleets.ts:1334-1352`). The ring itself is the look
 *  `anchor-ring` now (R1125); the aura and the regen wave still start at this
 *  radius. */
const ANCHOR_R = 19;

/**
 * The readout stack, in map px against the hull (R425, R829).
 *
 * The figures were literals here until R829. They are
 * `presentation.fleets.bars` now, because the `shield` field of a Marks
 * theme picks between six drawings of the same three lanes and the doc
 * renders the figures they share.
 */

/** The hull bar's traffic light: `presentation.fleets.bars.hull_bands`,
 *  three inks of the palette. The middle band is the palette amber that
 *  the DOM hull figure wears, because gold is never a signal (R1798). */
const HULL_BAND_COLOR = FLEET_BARS.hullBands;

/** The captain's chevron (`fleets.ts:1224-1230`). */
const CHEVRON_ALPHA = 0.95;

/** How many fleets the arrays hold before the first doubling (the plan's C.3). */
const START_CAPACITY = 256;

/** The digits the badge alphabet holds (`fleets.ts:196` `BADGE_DIGITS`). */
const BADGE_SLOTS = 6;

/** The share past which a boarded hull the wire drops is a taken hull. */
const TAKEN_SHARE = 0.8;
/** How far from the taken hull its new NPC hull may stand, in map px. */
const TAKEN_MATCH_PX = 40;
/** How long a taken hull waits for its new NPC hull, in wall s. */
const TAKEN_WAIT_SEC = 2;

/** A hull that a pirate group took, until its new hull arrives (R2721). */
interface TakenHull { x: number; y: number; cls: number; pirate: number; at: number; key: string; color: number }
/** The hand-over of a taken hull on its new hull (round 111 card 01). */
interface Handover { key: string; color: number; pirate: number; at: number }
/** A boarding that stopped: the lines of a pirate, or the ring of a hull. */
interface BoardingSnap { pirate: number; target: number; p: number; at: number; ring: boolean }

/**
 * Every column of one number a slot, by field name: the list the grow and
 * the swap-remove share (R2212). The wake ring has `WAKE_POINTS * 2`
 * numbers a slot, so the layer grows and moves it itself.
 */
export const FLEET_COLUMNS = [
  'id', 'flags', 'faction', 'cls', 'mission', 'anchor', 'groupBadge',
  'stackCount', 'hullPct', 'shieldPct', 'readinessPct', 'readinessFloorPct',
  'noiseMilli', 'regenIn', 'regenAt', 'weapType', 'shieldType', 'firingKind',
  'x', 'y', 'sx', 'sy', 'tx', 'ty', 'pxx', 'pyy', 'alpha', 'speed', 'heading',
  'bow', 'hold', 'cargoCap', 'captureLeftSec', 'anchorLeftSec', 'beamLeftSec',
  'anchorSince', 'reloadSec', 'torpedoRange', 'sonarPx', 'shareSec',
  'scanReadySec', 'decoyReadySec', 'torpedoes', 'reloadFull', 'reloadAt',
  'firedAt', 'readyAt', 'wasReloading', 'escorting', 'formationOf', 'focus', 'chase', 'pledgeSite',
  'tending', 'aura', 'captainId', 'ownerPlayer', 'effect', 'livery', 'npc',
  'brotherhood', 'boardTarget', 'boardedBy', 'boardMilli', 'boardLeft', 'boardAt', 'boardSince',
  'wakeHead', 'wakeLen', 'wakeTimer',
] as const;

// ---------------------------------------------------------------------------
// The flag word
// ---------------------------------------------------------------------------

const F_OWN = 1;
const F_FADING = 2;
const F_CLASSIFIED = 4;
const F_SLOWED = 8;
const F_DEEP = 16;
const F_SQUAD = 32;
const F_SELECTED = 64;
/** The cull bit. `hit.ts` never reads it (the plan's C.4, §10 rule 7). */
export const F_HIDDEN = 128;

/** The hull AND its whole trail are off screen (`fleets.ts:853`). */
const F_WAKE_HIDDEN = 256;
/** The hull lends its array to the faction (R453; `render/fleets.ts:606`). */
const F_SHARING = 512;
/** The hull runs the A8 deep-run gait (R721). Own hulls only: the server
 * sends the bit on the own block, the way it sends `slowed`. */
const F_DEEP_RUN = 1024;
/** Own haulers only: the hull works a shaft on worked-out ground (I1,
 * R715). Only the DOM panels read it. */
const F_SHAFT = 2048;
/** A Tender tends this hull (R725). The layer sets it from `tending`. */
const F_TENDED = 4096;
/** This hull stands in a set Tender's aura (R725), from `aura`. */
const F_AURA = 8192;
/**
 * Another layer draws this hull's MARK, and the hull is alive (R1012).
 *
 * The silhouette and its own bloom go; the pair of bars, the group badge, the
 * fleet mark, the wake, the order line and the selection bracket all stay,
 * because the fleet is still in the fight. A hull hit lives over a second and
 * a HIT event arrives every second, so `F_HIDDEN` here would take the bars off
 * a hull for the whole fight, which is where a reader looks first.
 */
const F_HULL_HIDDEN = 16384;
/** Own haulers only: the owner switched the dredger brain off for this
 * hull (R1226). Only the DOM panels read it. */
const F_AUTO_FIELD_OFF = 32768;
/** Own armed hulls only: the fleet holds fire (R1437). The DOM panels read
 * it, and the hold-fire look draws round the hull while it is set (R1439).
 * It is the first bit past 16, so the flag word is a 32-bit column. */
const F_HOLD_FIRE = 65536;
/** The hull reads the bow aspect all round (R2407, numbers.md §4.1): a
 * set anchor that stays, or DIG IN. It draws no aspect chevron. */
const F_ALL_ROUND = 131072;
/** Own hulls only: a hostile faction hears the hull now (`FleetState.heard`,
 * R2458). The own fleet card reads it as `SONAR: HEARD`. */
const F_HEARD = 262144;
/** Own hulls only: the order is a course of several legs (v2 §7, R2576,
 * R2589). The order line draws the legs of the cold record, and the line
 * of the mission is not drawn. */
const F_COURSE = 524288;
/** Own hulls only (R2584, R2617): the hull is a
 * command ship whose formation is on (`FleetState.formation`). It draws
 * the one ring of its formation (`packFormation`). */
const F_FORMATION = 1048576;
/** Own hulls only: the frame carries the PRESSURE condition, so the hull
 * stands in a band deeper than its depth rating (v2 §3.2). The selected
 * hull with the flag wears the tick arc (`packPressure`, R3129). */
const F_PRESSURE = 2097152;

/** The span of the tick arc in radians, and the cosine and the sine of
 * each tick, worked out once (R3129, §10.4). */
const PRESSURE_FROM = FLEET_PRESSURE_ARC.fromDeg * Math.PI / 180;
const PRESSURE_SWEEP = (FLEET_PRESSURE_ARC.toDeg - FLEET_PRESSURE_ARC.fromDeg) * Math.PI / 180;
const PRESSURE_TICKS: readonly number[] = FLEET_PRESSURE_ARC.ticksDeg.flatMap((d) => {
  const t = d * Math.PI / 180;
  return [Math.cos(t), Math.sin(t)];
});

/** Whether the conditions of an own fleet carry PRESSURE (v2 §3.2). */
function underPressure(conds: readonly Condition[]): boolean {
  for (let k = 0; k < conds.length; k++) {
    if (conds[k].key === ConditionKey.CONDITION_KEY_PRESSURE) return true;
  }
  return false;
}

/** One hit flash of the aspect chevrons (R2407, §6.3.3). */
interface AspectFlash {
  aspect: HitAspect;
  side: 1 | -1;
  /** The layer clock the flash starts at. */
  at: number;
}

/** The nearest hull to a point, with its class (R1009). Reused (R131). */
export interface NearestClass {
  id: number;
  cls: number;
}
/** What only the DOM panels read, so it stays off the hot arrays. */
interface ColdFleet {
  name: string;
  /** The name part is not from the faction pool, so REPORT applies (R1886). */
  customName: boolean;
  ownerName: string;
  hold: { resource: number; amount: number }[];
  /** The hull's own maxima in points, own faction; 0 = not sent (R1278). */
  hullMax: number;
  shieldMax: number;
  /** The load by kind and the active round, own fleets only (R2017). */
  rounds: readonly Stock[];
  activeRound: number;
  /** The approach state of the last frame (R2376), or null while the
   *  approach is off. The server fills the own-only fields on an own hull
   *  alone. */
  approach: FleetApproach | null;
  /** The conditions of an own fleet (v2, R2576): the wire's own list,
   *  held by reference, so a frame allocates nothing. Empty elsewhere. */
  conditions: readonly Condition[];
  /** The course of an own fleet (v2 §7, R2576): the legs with leg 0 at
   *  the order point, the leg it sails to now and the pass of its loop
   *  part. The wire's own list, held by reference. Empty for no course. */
  legs: readonly Waypoint[];
  leg: number;
  pass: number;
  /** The depth rating of an own fleet (v2 §3.2, R2625, R2637), 0 for none. */
  depthRating: number;
  /** The search hint of an own fleet (v2 §9.1, R2643, R2649): 0 for
   *  none, else 1 to 3, far to near. */
  searchHint: number;
  /** The map seconds a SEARCH still works (R2649), as the
   *  last frame stated it at the layer time `workAt`: the card counts it
   *  down between frames. 0 while the fleet sails to the point. */
  workLeft: number;
  workAt: number;
  /** The full map seconds of the work that runs now (R2833, R2834): the
   *  own `work_full_sec` of the last frame. 0 while no work runs. The
   *  cache work look reads its share. */
  workFull: number;
  /** An own Sapper while `sapper` is latched (v2 §11.1, R2654): the stow
   *  aboard (the wire's own list, held by reference), and the map seconds
   *  to the next LAY and of the loud lay, as the last frame stated them at
   *  the layer times `layAt` and `loudAt`: the card counts them down. */
  stow: readonly SeaObjectStock[];
  layReady: number;
  layAt: number;
  loudLeft: number;
  loudAt: number;
}

/**
 * The approach state of one frame (R2376), written into the cold record of
 * the hull so a frame allocates nothing for a hull it saw before. The
 * server sends the own-only fields on an own hull alone, and 0 elsewhere.
 */
function approachOf(w: FleetState, into: FleetApproach | null): FleetApproach {
  const a: FleetApproach = into ?? { headingMrad: 0, quiet: false, charge: 0, active: false, leftSec: 0, floorSec: 0, floorAtMs: 0, floorHeld: false };
  a.headingMrad = w.headingMrad;
  a.quiet = w.quiet;
  a.charge = w.signatureCharge;
  a.active = w.signatureActive;
  a.leftSec = w.signatureLeftSec;
  // The floor counts down on the card from the frame that stated it
  // (R2405). A frame with the same figure came inside the same second,
  // so the older stamp stays. The floor of CANISTER and BROADSIDE stands
  // still while the effect holds (R2412): each frame of the hold stamps
  // it again, and so does the frame that ends the hold, so the card
  // counts from the effect's end.
  const held = w.signatureActive && floorHeldOrder(w.signatureOrder);
  if (w.signatureFloorSec !== a.floorSec || held || a.floorHeld) {
    a.floorSec = w.signatureFloorSec;
    a.floorAtMs = Date.now();
  }
  a.floorHeld = held;
  return a;
}

/**
 * How much cable is out, 0-1 (`fleets.ts:241-248`). Setting fills toward a
 * dropped anchor, weighing empties toward a lifted one, and a settled cable
 * is all the way out.
 */
function anchorFrac(anchor: number, leftSec: number, cycleSec: number): number {
  if (anchor === AnchorState.ANCHOR_STATE_SET) return 1;
  if (leftSec <= 0) return 0;
  const run = clamp01(1 - leftSec / cycleSec);
  return anchor === AnchorState.ANCHOR_STATE_WEIGHING ? 1 - run : run;
}

/** Every class the atlas holds a silhouette for; anything else wears the dart. */
const KNOWN_CLASSES = HULL_CLASSES;

/**
 * The nine slot glyphs of card B09, built once (R830).
 *
 * The key is the damage figure's own glyph cell, so the map bakes no second
 * numeral face. The keys are built here rather than per frame, because
 * `String(n)` inside the pack would allocate on every marked hull.
 */
const SLOT_KEYS: readonly string[] = Array.from(
  { length: SLOT_NUMERAL_MAX },
  (_, i) => damageGlyphKey(DAMAGE_FIGURE.weight, String(i + 1)),
);

/**
 * The digits the stack count prints, built once (R1004).
 *
 * The key is the damage figure's own glyph cell, the way the squad slot's
 * is, so the map bakes no third numeral face. Index 0 is the digit 2,
 * because a count under two draws nothing. `String(n)` inside the pack
 * would allocate on every stacked hull (§10.4).
 */
const STACK_KEYS: readonly string[] = Array.from(
  { length: Math.max(0, FLEET_STACK.max - 1) },
  (_, i) => damageGlyphKey(DAMAGE_FIGURE.weight, String(i + 2)),
);

/** Does a friendly mark value draw anything beside the hull (R830)? */
function marksAnything(s: FleetMarkStyle): boolean {
  return s.cells.length > 0 || s.slot !== undefined;
}

/** One own fleet that works a SEARCH at its point (R2833). */
export interface OwnWork {
  id: number;
  x: number;
  y: number;
  /** The heading in radians: +x is the bow. */
  head: number;
  /** The share of the work done, 0 to 1. */
  share: number;
  /** The wall seconds the work still runs: the timer of the look. */
  wallLeft: number;
}

/** One mission overlay style: the missions it covers, its colour, its alpha. */
export interface OrderStyle {
  readonly missions: readonly Mission[];
  readonly color: number;
  readonly alpha: number;
}

/**
 * The styles, in the paint order of `fleets.ts:1002-1015`. Every colour and
 * alpha is `presentation.fleets.orders`, the table the pick crosshair reads
 * too (R469). The port carried the literal table the PixiJS layer had before
 * R469, so an attack-move drew the retired amber under a red crosshair (R533).
 */
const OC = FLEET_ORDER_COLORS;
export const ORDER_STYLES: readonly OrderStyle[] = [
  // STEER, the order of the helm view of the sea view, sails for a point
  // ahead on its heading, so it draws as a move to tx/ty (R2638).
  { missions: [Mission.MISSION_MOVE, Mission.MISSION_STEER], ...OC.move },
  { missions: [Mission.MISSION_ATTACK_MOVE], ...OC.attack },
  // SEARCH (v2 §9.1, §9.2) sails to its point and works there, in the ink
  // of its own (R2833).
  { missions: [Mission.MISSION_SEARCH], ...OC.search },
  { missions: [Mission.MISSION_PATROL], ...OC.patrol },
  { missions: [Mission.MISSION_GATHER, Mission.MISSION_RETURN], ...OC.gather },
  { missions: [Mission.MISSION_HOME], ...OC.home },
  { missions: [Mission.MISSION_ESCORT], ...OC.escort },
  { missions: [Mission.MISSION_ANCHOR], ...OC.anchor },
];

/**
 * The commit style (R1208). A commit sails on a plain move (R788), so no
 * mission names it: the server marks the hull with `pledgeSite` while the
 * intent stands, and `orderStyleOf` picks this style for such a move. It
 * is the gold of the drag preview, so the line the finger drew stays the
 * line the hull then sails.
 */
const COMMIT_STYLE: OrderStyle = { missions: [], ...OC.commit };

/**
 * The commission style (R2833). The order of a fleet that works a
 * commission (`Commission.fleet_id`, v2 §10) wears the orchid of its own,
 * whatever its mission, as a pledging move wears the commit gold. The
 * scene hands the layer the fleets that work one (`setCommissioned`).
 */
const COMMISSION_STYLE: OrderStyle = { missions: [], ...OC.commission };

/** The size of the pass count of a loop part on its leg back, screen px:
 *  the note step of the chart text (R1796, R2589). */
const COURSE_WORD_PX = stepPx('mono', 'note');
/** The pass count reads over the faint order line: the alpha of the
 *  drag order preview, an order the reader reads now (R2589). */
const ORDER_WORD_ALPHA = FLEET_ORDER_DRAG.alpha;

/** The words of a pass count, built once for each count (§10.3). */
const PASS_WORDS: string[] = [];

/** The pass count of a loop part: `×2`, or `∞` for no end. */
function passWord(passes: number): string {
  if (passes <= 0) return CARD_S.plot_endless;
  return (PASS_WORDS[passes] ??= CARD_S.plot_passes({ n: passes }));
}

/** The first row of `ORDER_STYLES` that names each mission, so
 *  `packOrders` asks a map and walks no row list a hull and a pass. */
const STYLE_OF_MISSION: ReadonlyMap<number, OrderStyle> = (() => {
  const m = new Map<number, OrderStyle>();
  for (const s of ORDER_STYLES) for (const mission of s.missions) if (!m.has(mission)) m.set(mission, s);
  return m;
})();

/** The missions whose lane run rides the course field (v2 §7.2 rule 18,
 *  R3221): a gather run, the run home of a hauler and a Return. Their
 *  course wears the ink of the mission and not the MOVE ink. */
const LANE_RUN_MISSIONS: ReadonlySet<number> = new Set([
  Mission.MISSION_GATHER, Mission.MISSION_RETURN, Mission.MISSION_HOME,
]);

/** The paint order of `packOrders`: the commits right after the moves,
 *  and the commissions last. */
const ORDER_PASSES: readonly OrderStyle[] = [
  ORDER_STYLES[0], COMMIT_STYLE, ...ORDER_STYLES.slice(1), COMMISSION_STYLE,
];

/**
 * The style of one own fleet's standing order. `commissioned` says the
 * fleet works a commission: every mission with a style then wears the
 * commission orchid (R2833). `pledging` is a non-zero `pledgeSite`: a move
 * that carries it is a commit (R1208). Every other mission takes its row
 * of `ORDER_STYLES`, and a mission no row names (idle) takes none.
 */
export function orderStyleOf(mission: number, pledging: boolean, commissioned = false): OrderStyle | undefined {
  const style = STYLE_OF_MISSION.get(mission);
  if (style === undefined) return undefined;
  if (commissioned) return COMMISSION_STYLE;
  if (pledging && mission === Mission.MISSION_MOVE) return COMMIT_STYLE;
  return style;
}

// ---------------------------------------------------------------------------
// The layer
// ---------------------------------------------------------------------------

export class FleetLayer implements Layer {
  readonly name = 'fleets';
  private shown = true;

  // -- the batches, in the paint order of the plan's A.5 row 12 -------------
  private readonly wakeSegs: SegmentSink;
  /**
   * The heal beams (R725). Three additive strokes a beam, drawn over the
   * wakes and under the bloom, so a Tender's own hull still sits on top of
   * the line it throws.
   */
  private readonly tenderSegs: SegmentSink;
  private readonly bloomQuads: QuadSink;
  /**
   * The Tender shield aura (R725). It lies UNDER everything the layer
   * draws but the wakes, because an aura is a field over the water and the
   * hulls inside it must stand on top of their own cover.
   */
  private readonly auraQuads: QuadSink;
  /**
   * The sprite half of a heal beam (R725): what a beam draws that is not a
   * stroke — a pip walking the cable, a ring closing on the patient. It
   * rides beside `tenderSegs`, over the strokes of the same beam.
   */
  private readonly tenderQuads: QuadSink;
  private readonly orderSegs: SegmentSink;
  private readonly rings: CircleSink;
  private readonly markQuads: QuadSink;
  /** The readout: every flat rectangle of the bars and of the contact frame. */
  private readonly barSegs: SegmentSink;
  /** The boarding look (round 111 card 01, R2721): the grapple lines and
   *  the wipe edge, and the ring of the share done. */
  private readonly boardSegs: SegmentSink;
  private readonly boardRings: CircleSink;
  /** The anchoring look's fills and strokes (R1125). They flush after
   *  `rings`, fills first, so a buoy outline stands over its own fill. */
  private readonly anchorQuads: QuadSink;
  private readonly anchorSegs: SegmentSink;
  /** The selection bracket. It flushes after the anchoring look, so the
   *  white corners stand over every anchor ring and frame. */
  private readonly bracketSegs: SegmentSink;
  /**
   * The hold-fire look's circles and strokes (R1439). They flush after the
   * bloom and BEFORE `orderSegs`, so the order lines, the selection bracket, the badge and
   * the bars stand over the look, as the card draws them. Everything else
   * of the layer that draws with the bracket keeps its place against it.
   */
  private readonly holdRings: CircleSink;
  private readonly holdSegs: SegmentSink;
  /**
   * The tube reload look (R1474). A look whose card draws UNDER the hull
   * takes the first two, which flush before the bloom; a look whose card
   * draws OVER the hull takes the other four, which flush last, over the
   * hull, the bracket, the badge and the bars. `reloadGround` holds the
   * circles under the strokes and `reloadRings` the circles over them.
   */
  private readonly reloadUnderRings: CircleSink;
  private readonly reloadUnderSegs: SegmentSink;
  private readonly reloadGround: CircleSink;
  private readonly reloadSegs: SegmentSink;
  private readonly reloadRings: CircleSink;
  private readonly reloadQuads: QuadSink;
  /**
   * The dead zone disc of the selected own hull (R1472, §6.1). Its two
   * sinks are the layer's, so `pack` fills them and the fade dims them,
   * but the `deadzone` pass flushes them in its own slot, under every
   * other mark of the map.
   */
  private readonly deadRings: CircleSink;
  private readonly deadSegs: SegmentSink;
  /** The pass that fills the `deadzone` slot with the two sinks above. */
  readonly deadZone: DeadZonePass;
  /**
   * Every sink of the layer, for the two walks that treat them alike: the
   * `begin` at the head of a pack and the arrival fade. `draw` names them
   * one by one, because the paint order is the whole of its job.
   */
  private readonly sinks: readonly (QuadSink | SegmentSink | CircleSink)[];
  /**
   * The mark scale (R1981, ui-map-interface.md §6.1): the marks of one hull
   * scale about the hull by `pivot.k`, and every map length keeps its size.
   * `raw` is the plain set; `scaled` wraps it about `pivot` and is built on
   * the first scale that is not 1; `h` is the set the pack draws through.
   * At 1 the pack takes `raw`, so the season game pays nothing.
   */
  private readonly pivot: Pivot = { x: 0, y: 0, k: 1 };
  private readonly raw: HullSinks;
  private scaled: HullSinks | null = null;
  private h: HullSinks;
  /** What one anchoring look draws against: ONE object, rewritten a hull
   *  (R131). */
  private readonly anchorCtx: AnchorCtx;
  /** The `#anchoring=<id>` knob: one look every anchor hull wears instead of
   *  the theme's, '' for none (R1125). */
  private anchorForced = '';
  /** What one hold-fire look draws against: ONE object, rewritten a hull
   *  (R131, R1439). */
  private readonly holdCtx: HoldFireCtx;
  /** The `#holdfire=<id>` knob: one look every own hull that holds fire
   *  wears instead of the theme's, '' for none (R1439). */
  private holdForced = '';
  /** The chart's glyph cells, for the legend of `restricted-line` (R1439)
   *  and the words of the tube reload looks (R1474). */
  private readonly glyphs: ChartGlyphs;
  /** What one tube reload look draws against: ONE object, rewritten a hull
   *  (R131, R1474). */
  private readonly reloadCtx: ReloadCtx;
  /** The run options of a tube reload word, rewritten per word (§10.4). */
  private readonly reloadOpts: RunOpts = {};
  /** The `#reloadlook=<id>` knob: one look every own hull with tubes wears
   *  instead of the theme's, '' for none (R1474). */
  private reloadForced = '';
  /** The `#reload=0` knob takes the tube reload look off (R1474). */
  private reloadOn = true;
  /** The tubes by `cls * 64 + kind`, filled from `roundOf` on first use and
   *  dropped when the wiring hands a new catalog (R1474). */
  private readonly rounds = new Map<number, ReloadRound>();
  private readonly sprite: (key: string) => SpriteEntry;
  /**
   * The entries of the fixed marks, looked up at the head of `pack()`. The
   * atlas rewrites an entry in place when it grows, so a lookup hands back
   * the same object every frame; the key STRINGS are built once, so the
   * lookup costs no allocation.
   */
  private bloom!: SpriteEntry;
  private chevron!: SpriteEntry;
  /**
   * The travelling pip of the control layer (R827). Two heal beams carry
   * one, so the entry is fetched once a frame beside the bloom rather than
   * once a beam.
   */
  private pip!: SpriteEntry;
  /**
   * `hull:<set>:<cls>:<hollow>` by `cls * 2 + hollow`, and the group badge
   * `badge:<value>:<n>` by slot. The badge keys carry the value of the
   * `digits` field and the hull keys the ship icon set in force for that
   * class, so `setLook` rebuilds both (R833, R835).
   */
  private readonly hullKeys = new Map<number, string>();
  /**
   * The liveried hull keys (R1252) by `(wire * 64 + cls) * 3 + master`,
   * where master 0 is the filled cell, 1 the hollow cell and 2 the mask
   * cell. Built on first use, so a frame allocates no key; `setLook` drops
   * them with the ship icon set.
   */
  private readonly liveryKeys = new Map<number, string>();
  private readonly badgeKeys: string[] = [];
  /**
   * The canvas half of the look (themes.md §12, R814).
   *
   * The layer stores it and draws the AUTO marks whatever it says. Every
   * mark of this layer that a theme can change — the bars, the badge, the
   * beam, the aura, the own and squadmate hull marks — reads it from here,
   * so the layer asks one object and never a caller.
   */
  private look: SceneLook = AUTO_SCENE_LOOK;

  /**
   * The charge pip keys of the reading in force (R1568), by faction id: one
   * string per faction, built when the reading changes, so `packHulls`
   * allocates no key. The pip is drawn in the chart reading the holds
   * wear: a roundel in the modern one, the shield in the historical one.
   */
  private pipKeys: string[] = pipKeysOf('m');
  private pipEra: HoldEra = 'm';

  /** The stack count's own clock and the roster length it last read. */
  private stackAgeMS = 0;
  private stackN = -1;

  // -- the structure of arrays (the plan's C.3) ----------------------------
  private cap = START_CAPACITY;
  private n = 0;
  private idToIndex = new Map<number, number>();
  private cold = new Map<number, ColdFleet>();
  /** The own fleets whose SEARCH works at its point now (R2833). */
  private readonly working = new Set<number>();
  /** The own fleets that work a commission (R2833): their order wears the
   *  commission ink. The scene writes it (`setCommissioned`). */
  private commissioned: ReadonlySet<number> = new Set<number>();
  /** The work list `ownWorks` fills and the entries it fills it with,
   *  both reused (R131). */
  private readonly worksBuf: OwnWork[] = [];
  private readonly worksPool: OwnWork[] = [];

  private id = new Uint32Array(START_CAPACITY);
  private flags = new Uint32Array(START_CAPACITY);
  private faction = new Uint8Array(START_CAPACITY);
  private cls = new Uint8Array(START_CAPACITY);
  private mission = new Uint8Array(START_CAPACITY);
  private anchor = new Uint8Array(START_CAPACITY);
  private groupBadge = new Uint8Array(START_CAPACITY);
  /**
   * How many own or friendly marks rest on this one (R1004). Zero means
   * the count has not been taken yet, one means the mark stands alone or
   * is a MEMBER of a stack another mark leads, and two or more is the
   * digit the leader prints. countStacks() rewrites the whole prefix.
   */
  private stackCount = new Uint8Array(START_CAPACITY);
  private hullPct = new Uint8Array(START_CAPACITY);
  private shieldPct = new Uint8Array(START_CAPACITY);
  private readinessPct = new Uint8Array(START_CAPACITY);
  // The lowest readiness this hull can reach (R769). It moves only when
  // the captain aboard changes or trains, and the card prints it beside
  // the current figure.
  private readinessFloorPct = new Uint8Array(START_CAPACITY);
  // The hull's live noise in thousandths (R1500), own fleets only; 0 is
  // "not sent". The server moves it on a stop, a trench edge, a set
  // fixture and a launch, and the panel's speed row prints it.
  private noiseMilli = new Uint16Array(START_CAPACITY);
  // The wait until the shield regen starts (R1503), own fleets only: map
  // seconds as the last frame stated them, and the layer time of that
  // frame. The view runs it down at the tactical scale, as `reloadAt` does.
  private regenIn = new Float32Array(START_CAPACITY);
  private regenAt = new Float32Array(START_CAPACITY);
  private weapType = new Uint8Array(START_CAPACITY);
  private shieldType = new Uint8Array(START_CAPACITY);
  /** The kind the next shot fires (R2017): the active round while it holds
   *  a round, else the next kind of the fixed order (`ui/rounds.ts`). */
  private firingKind = new Uint8Array(START_CAPACITY);

  private x = new Float32Array(START_CAPACITY);
  private y = new Float32Array(START_CAPACITY);
  private sx = new Float32Array(START_CAPACITY);
  private sy = new Float32Array(START_CAPACITY);
  private tx = new Float32Array(START_CAPACITY);
  private ty = new Float32Array(START_CAPACITY);
  private pxx = new Float32Array(START_CAPACITY);
  private pyy = new Float32Array(START_CAPACITY);
  private alpha = new Float32Array(START_CAPACITY);
  private speed = new Float32Array(START_CAPACITY);
  private heading = new Float32Array(START_CAPACITY);
  /** The bow heading the last frame stated, in radians (R2376), while the
   *  approach is on; `heading` turns toward it a frame at a time. */
  private bow = new Float32Array(START_CAPACITY);
  private hold = new Float32Array(START_CAPACITY);
  private cargoCap = new Float32Array(START_CAPACITY);
  private captureLeftSec = new Float32Array(START_CAPACITY);
  private anchorLeftSec = new Float32Array(START_CAPACITY);
  private beamLeftSec = new Float32Array(START_CAPACITY);
  /**
   * The layer clock at which this hull's anchor state began, or
   * `-ANCHOR_SINCE_UNSEEN` for a state the client never saw begin, and NaN
   * on a slot no frame has filled yet (R1125). A SET look's landing beat
   * reads it, so a hull that came into view already SET shows no beat.
   */
  private anchorSince = new Float32Array(START_CAPACITY);
  private reloadSec = new Float32Array(START_CAPACITY);
  private torpedoRange = new Float32Array(START_CAPACITY);
  private sonarPx = new Float32Array(START_CAPACITY);
  /** How long this hull keeps broadcasting its array, in map seconds (R453). */
  private shareSec = new Float32Array(START_CAPACITY);
  private scanReadySec = new Float32Array(START_CAPACITY);
  private decoyReadySec = new Float32Array(START_CAPACITY);
  private torpedoes = new Float32Array(START_CAPACITY);
  /**
   * The tube reload look's own state (R1474). `reloadFull` is the wire's
   * whole reload in map seconds, 0 on a hull with no tubes. `reloadAt` is
   * the layer clock when `reload_sec` last arrived, so the look runs the
   * reload down between two frames. `firedAt` and `readyAt` are the layer
   * clock at the shot and at the ready moment: NaN on a slot no frame has
   * filled, and `-RELOAD_UNSEEN` for a moment the client never saw.
   */
  private reloadFull = new Float32Array(START_CAPACITY);
  private reloadAt = new Float32Array(START_CAPACITY);
  private firedAt = new Float32Array(START_CAPACITY);
  private readyAt = new Float32Array(START_CAPACITY);
  private wasReloading = new Uint8Array(START_CAPACITY);

  private escorting = new Uint32Array(START_CAPACITY);
  /** Own hulls only (R2584): the command ship this hull keeps its
   *  formation station by, 0 for none. */
  private formationOf = new Uint32Array(START_CAPACITY);
  /** The hostile this hull chases and fires at alone; 0 = none (R544). */
  private focus = new Uint32Array(START_CAPACITY);
  /** Own hulls only: the hostile the chase follows now, the focus or the
   *  prey of an attack-move or a patrol; 0 = none (R2754). */
  private chase = new Uint32Array(START_CAPACITY);
  /** The object a commit sails to; 0 = none. Own fleets only (R1208). */
  private pledgeSite = new Uint32Array(START_CAPACITY);
  /** The hull this Tender tends; 0 = none. Own faction only (R723). */
  private tending = new Uint32Array(START_CAPACITY);
  /** The set Tender whose aura this hull stands in; 0 = none (R723). */
  private aura = new Uint32Array(START_CAPACITY);
  private captainId = new Uint32Array(START_CAPACITY);
  private ownerPlayer = new Uint32Array(START_CAPACITY);
  /**
   * The owner's effect on this hull, a catalog wire id of
   * `data/contracts/effects.toml`; 0 = none (R910).
   *
   * The server resolves the pick and the class-reveal gate, so the client
   * stores the number and never looks an owner up.
   */
  private effect = new Uint32Array(START_CAPACITY);
  /**
   * The owner's livery, a wire number of `LIVERIES`, 0 = NONE (R1252). Every
   * viewer that sees the hull receives it.
   */
  private livery = new Uint32Array(START_CAPACITY);
  /**
   * The NPC kind of a hull of the NPC side (`FleetState.npc`, v2 §8.1,
   * R2622, R2637), 0 for a hull of a faction. Public, as the faction is.
   */
  private npc = new Uint8Array(START_CAPACITY);
  /** The brotherhood of a pirate hull (`FleetState.brotherhood`, R2720),
   *  0 for every other hull. */
  private brotherhood = new Uint8Array(START_CAPACITY);
  /**
   * The boarding of a hull (`FleetState.boarding_*`, v2 §8.6, R2721): on
   * a pirate the fleet it boards, on a boarded hull the pirate, both with
   * the share done (0 to 1000) and the map ms left at the time of the
   * frame (`boardAt`, layer time). `boardSince` is the layer time the look
   * first saw the boarding, for the shot of the grapples.
   */
  private boardTarget = new Uint32Array(START_CAPACITY);
  private boardedBy = new Uint32Array(START_CAPACITY);
  private boardMilli = new Uint16Array(START_CAPACITY);
  private boardLeft = new Float32Array(START_CAPACITY);
  private boardAt = new Float32Array(START_CAPACITY);
  private boardSince = new Float32Array(START_CAPACITY);

  /** Nine trail points per fleet, as a ring: `x, y` interleaved. */
  private wake = new Float32Array(START_CAPACITY * WAKE_POINTS * 2);
  private wakeHead = new Uint8Array(START_CAPACITY);
  private wakeLen = new Uint8Array(START_CAPACITY);
  private wakeTimer = new Float32Array(START_CAPACITY);

  // -- what the wiring sets ------------------------------------------------

  anchorCycleSec = 600;
  /**
   * How long the beam order of a SET Beacon runs, in map seconds, from
   * `Catalog.beacon_beam_sec` (R1125, R2858). The anchoring look reads the
   * share of the beam left against it.
   */
  beaconBeamSec = 54000;
  /**
   * Map seconds into WORLD seconds, for a time a look prints (R1125). The
   * wiring points it at `GameState.mapToWorldSec`, as `ui/format.ts
   * anchorLabel` reads it; the default leaves a figure as it is.
   */
  worldSecOf: (mapSec: number) => number = (s) => s;
  /**
   * How wide a set Tender's aura is, in world px, from
   * `Hello.catalog.rules.tender_aura_px` (R723). It is 0 until a hello
   * lands, and 0 draws no regen wave: the client measures no distance of its
   * own and it mirrors no rule of the trade.
   */
  tenderAuraPx = 0;
  /**
   * How wide a held Coral Ward's aura is, in world px, from
   * `Hello.catalog.rules.ward_aura_px` (R1082). It is 0 until a hello
   * lands, and 0 raises no plus: the client measures no distance of its
   * own and it mirrors no rule of the object.
   */
  wardAuraPx = 0;
  /**
   * Where a plus goes when a Tender's work lands (R725).
   *
   * The wiring points it at the damage layer, which owns every figure that
   * stands beside a hull. The layer fires it from `applyState` for a hull
   * plus and from the wave walk for a shield plus; the default swallows
   * both, so a host that wires nothing draws no plus.
   */
  onHeal: (
    fleetId: number, x: number, y: number,
    kind: 'hull' | 'shield', faction: number,
  ) => void = () => {};
  /** The player's home, for gather and return legs; set by the wiring. */
  home: { x: number; y: number } | null = null;
  selectedId = 0;
  aimingId = 0;
  /**
   * The dotted order lines are HANDED OVER for one pack (R548, §6.1).
   *
   * The chart's lines of bearing (card 42) draw the same information with a
   * bearing and an arrival time on it, so the two must never stand on the
   * water together. The piece re-arms this on every frame it draws and
   * `packOrders` clears it again, so a chart that stops drawing — the
   * reader switched it off, the zoom left the band, a film took the glass —
   * gives the dotted lines back on the next frame and no handover can
   * stick. The course of an own fleet is not handed over: it still draws
   * its legs, and the piece draws the line from the hull to the leg it
   * sails now (R2704).
   */
  ordersOff = false;
  /**
   * The own fleet the plot line plots a new course for, or 0 (R2704).
   * `fast/main.ts` writes it each frame. Its set course does not draw,
   * because the draft of the plot layer takes its place.
   */
  plotFleet = 0;
  /**
   * The lit runs of a course (R3075, §6.1): for each leg of the wire list
   * from leg 1 on, the runs (x0, y0, x1, y1, world px) of the leg from the
   * point before it where a lift reaches the hull; null on a page with no
   * lanes. `fast/main.ts` fills it in and caches the answer on the list,
   * so a pack walks no water. `courseLitInk` is the faction ink.
   */
  courseLit: ((legs: readonly Waypoint[]) => readonly (readonly number[])[] | null) | null = null;
  courseLitInk = 0;
  /** Arcade keeps every owned hull's bar drawn (R285). Core leaves it false. */
  alwaysShowOwnHullBar = false;
  /**
   * Every hull carries the pair of contact bars, whatever `viewerFaction`
   * says (R531).
   *
   * The replay film is the one caller: a film is a picture of a fight that
   * is over, nobody is flying in it, and the condition of BOTH sides is the
   * point of watching. The live map leaves it false, so the fog-of-war
   * reading of R425 is untouched.
   */
  showAllBars = false;
  /**
   * Whether the hull readouts draw: the pair of bars, the cargo lane, the
   * group badge, the stack count and the gather figures (R1535). The reel
   * page sets it false, because a mode reel shows the fight and no figure.
   * Every other page leaves it true.
   */
  labels = true;
  /** The banner this viewer sails under, from `Hello.faction`; 0 until it lands. */
  viewerFaction = 0;
  /**
   * The bow heading of the approach (R2371, R2376): every hull draws
   * along the bow heading of the wire, and the DOM reads the approach
   * state of each hull. The hello sets it (`MapScene.applyHello`), because
   * every world plays the approach (R2400). A host with no hello leaves it
   * off, and the heading is the step between two records.
   */
  bowHeading = false;
  /**
   * The gun reach of one hull, given its class, its anchor state and
   * whether its signature order holds.
   *
   * The state is a parameter because a planted tower reaches further than
   * the same hull under way (R661), and so does a Basilisk under DIG IN
   * (R2394). The layer mirrors no rule table: the host reads every figure
   * out of the hello catalog (`gunRange` of `ui/format.ts`).
   */
  gunRangeOf: (cls: ShipClass, anchor: AnchorState, orderActive: boolean) => number = () => 0;
  /**
   * The collect radius of a Sapper in world px (`CatalogRules.sea_objects
   * .collect_px`, R2711), or 0 for none. `MapScene.frame` writes it from
   * the hello while the world latched `sapper`, so a world without the
   * Sapper draws no ring.
   */
  collectPx = 0;
  /**
   * The heal beam reach of a Tender in world px
   * (`CatalogRules.tender_repair_range_px`, numbers.md §3, R2738), or 0
   * for none. `MapScene.applyHello` writes it from the hello catalog.
   */
  beamPx = 0;
  /**
   * The fleet under the pointer, 0 for none (R2738). `MapScene.frame`
   * copies it from the hover of the sonar layer, which the hit test
   * writes. The hull draws its primary reach as a dashed ring.
   */
  hoverId = 0;
  /**
   * The dead zone of one class in world px: the gun cannot engage a
   * hostile inside it (numbers.md §4, R407). 0 is no dead zone. It does
   * not change with the anchor state, because the sim reads `min_range`
   * alone. The host reads it out of the hello catalog.
   */
  deadZoneOf: (cls: ShipClass) => number = () => 0;
  /**
   * The gun arc in degrees for which a hull shows a rear dead zone now
   * (R2391), given its class, its anchor state, whether its signature
   * order holds and the share of the DIG IN close (R2408; -1 follows the
   * bit); 0 is none. The host reads the arc out of the hello catalog and
   * applies the gates of `rearArcDeg`.
   */
  gunArcOf: (cls: ShipClass, anchor: AnchorState, orderActive: boolean, digClose: number) => number = () => 0;
  /**
   * The signature order looks (R2408, §6.1.12): the shares this layer
   * reads for the iris of CANISTER, the close of the rear dead zone under
   * DIG IN and the fade of CRASH DIVE. The scene points it at the
   * `signature` layer; null draws none of the three.
   */
  signature: SignatureMarks | null = null;
  baseAimRange: () => number = () => 0;

  /** Every held Coral Ward, from the Sieges frame by way of the scene (R1082). */
  private wards: readonly WardAura[] = [];

  private squadIds = new Set<number>();
  /** The squad slot of every squadmate, by account id (R185), or null. */
  private squadSlots: ReadonlyMap<number, number> | null = null;
  /**
   * When the lift of a squad change began, on the layer's own clock
   * (R1571, mockup round 075 card 18), or -Infinity for no lift.
   */
  private squadLiftAt = -Infinity;
  private groups: ReadonlyMap<number, number> = new Map();

  /**
   * The rect the last `advance` was given, kept for the pack.
   *
   * The regen wave reaches `tenderAuraPx` past its Tender, so the wave test
   * needs the window widened by that radius rather than the `F_HIDDEN` bit,
   * which asks only whether the hull itself is in the picture.
   */
  private packCull: ViewRect = { x0: 0, y0: 0, x1: 0, y1: 0 };

  /** The layer's own clock, in seconds, and where it stood a frame ago. */
  private time = 0;
  private timePrev = 0;
  /** The two results the marks are read into, reused so no frame allocates. */
  private readonly breathOut: Breath = { alpha: 0, width: 0 };
  /**
   * What one heal beam is drawn against (R827). ONE object, rewritten per
   * beam, because a frame draws up to one beam a Tender and §10.1 forbids
   * a per-frame allocation. `pip` is filled by `pack()`.
   */
  private readonly beamCtx: BeamCtx;
  /**
   * The shield aura's context (R831), built once and rewritten for every
   * set Tender of the frame.
   *
   * The two walks the styles need — the hulls of a berth and the cells
   * under a reach — stay in the layer, because only the layer holds the
   * state and the cull. They reach a style as the two callbacks of this
   * object, and both are bound once here, so a frame allocates neither.
   */
  private readonly auraCtx: AuraCtx;
  /** Which fleet slot the two aura callbacks answer for. */
  private auraHost = 0;
  /** One hex-cells aura's cells, as `x, y, d` triples. Reused every frame. */
  private readonly auraCellBuf: number[] = [];

  constructor(batches: BatchSource) {
    this.wakeSegs = batches.newSegments(START_CAPACITY * (WAKE_POINTS - 1));
    this.bloomQuads = batches.newQuads(64);
    this.orderSegs = batches.newSegments(256);
    this.rings = batches.newCircles(256);
    this.markQuads = batches.newQuads(START_CAPACITY * 4);
    this.barSegs = batches.newSegments(START_CAPACITY * 4);
    // Last, so the batch indices of every earlier sink stay where they are.
    this.tenderSegs = batches.newSegments(64);
    this.auraQuads = batches.newQuads(64);
    this.tenderQuads = batches.newQuads(64);
    // The anchoring look's strokes and fills (R1125). Its circles ride
    // `rings`, where the r19 ring always stood, so ANCHOR RING keeps today's
    // picture; these two flush right after it.
    this.anchorQuads = batches.newQuads(64);
    this.anchorSegs = batches.newSegments(256);
    // The hold-fire look's circles and strokes (R1439), last of all so no
    // earlier batch index moves.
    this.holdRings = batches.newCircles(256);
    this.holdSegs = batches.newSegments(256);
    // The dead zone disc (R1472), last of all so no earlier batch index
    // moves. One disc a frame: a rim and at most `2r / step` hatch lines.
    this.deadRings = batches.newCircles(4);
    this.deadSegs = batches.newSegments(64);
    this.deadZone = new DeadZonePass(this, this.deadRings, this.deadSegs);
    // The tube reload look (R1474), last of all so no earlier batch index
    // moves.
    this.reloadUnderRings = batches.newCircles(256);
    this.reloadUnderSegs = batches.newSegments(256);
    this.reloadGround = batches.newCircles(128);
    this.reloadSegs = batches.newSegments(512);
    this.reloadRings = batches.newCircles(256);
    this.reloadQuads = batches.newQuads(256);
    // The selection bracket, last of all so no earlier batch index moves.
    this.bracketSegs = batches.newSegments(16);
    // The boarding look (R2721), after the bracket so no earlier batch
    // index moves: the grapple lines and the wipe edge, then the ring.
    this.boardSegs = batches.newSegments(128);
    this.boardRings = batches.newCircles(64);
    this.sinks = [
      this.wakeSegs, this.tenderSegs, this.tenderQuads, this.auraQuads,
      this.bloomQuads, this.orderSegs, this.rings, this.barSegs, this.markQuads,
      this.anchorQuads, this.anchorSegs, this.holdRings, this.holdSegs,
      this.deadRings, this.deadSegs,
      this.reloadUnderRings, this.reloadUnderSegs, this.reloadGround, this.reloadSegs,
      this.reloadRings, this.reloadQuads, this.bracketSegs,
      this.boardSegs, this.boardRings,
    ];
    this.raw = {
      markQuads: this.markQuads, bloomQuads: this.bloomQuads, rings: this.rings,
      barSegs: this.barSegs, bracketSegs: this.bracketSegs,
      anchorQuads: this.anchorQuads, anchorSegs: this.anchorSegs,
      holdRings: this.holdRings, holdSegs: this.holdSegs,
      reloadUnderRings: this.reloadUnderRings, reloadUnderSegs: this.reloadUnderSegs,
      reloadGround: this.reloadGround, reloadSegs: this.reloadSegs,
      reloadRings: this.reloadRings, reloadQuads: this.reloadQuads,
    };
    this.h = this.raw;
    this.sprite = (key) => batches.sprite(key);
    this.anchorCtx = {
      t: 0, z: 1, row: anchorLook('').row, round: F_ROUND,
      x: 0, y: 0, head: 0, phase: 'set', p: 0, beam: -1, leftSec: 0, since: 0,
      own: false, ink: 0, a: 1, seed: 0,
      quads: this.anchorQuads, segs: this.anchorSegs, rings: this.rings,
      sprite: this.sprite,
      text: { run: (s, x, y, dx, dy, px, ink, alpha) => this.putAnchorText(s, x, y, dx, dy, px, ink, alpha) },
    };
    // The hold-fire look (R1439) takes its own two batches, which flush
    // before the bracket's, so it draws under the bracket, the badge, the
    // bars and the hulls (R185). Its legend rides the mark batch.
    this.glyphs = new ChartGlyphs(batches);
    this.holdCtx = {
      t: 0, z: 1, row: holdFireLook('').row, round: F_ROUND,
      x: 0, y: 0, selected: false,
      segs: this.holdSegs, rings: this.holdRings,
      legend: (x, y, px, ink, alpha) => {
        // The card writes the legend with no halo, so it takes the bare
        // cells (R1439).
        this.glyphs.run(this.h.markQuads, CHART_S.hold_fire_legend, x, y, 'fell', px, ink, alpha,
          HOLD_LEGEND_OPTS);
      },
    };
    // The tube reload look (R1474). Its words ride the chart's mono cells
    // in a quad batch of its own, over the look's strokes.
    this.reloadCtx = {
      z: 1, row: reloadLook('').row, round: F_ROUND,
      x: 0, y: 0, head: 0,
      empty: false, reloading: false, ready: true, p: 1, leftSec: 0,
      count: 0, mag: 0, shape: 'square', shapes: [], letter: '', name: '',
      firedAgo: RELOAD_UNSEEN, readyAgo: RELOAD_UNSEEN,
      ground: this.reloadGround, segs: this.reloadSegs, rings: this.reloadRings,
      text: (s, x, y, px, ink, alpha, anchor, halo) => {
        const o = this.reloadOpts;
        o.anchor = anchor;
        o.halo = halo;
        return this.glyphs.run(this.h.reloadQuads, s, x, y, 'mono', px, ink, alpha, o);
      },
      // A run at no alpha writes nothing and answers its width.
      measure: (s, px) => this.glyphs.run(this.reloadQuads, s, 0, 0, 'mono', px, 0, 0),
    };
    this.beamCtx = {
      ax: 0, ay: 0, bx: 0, by: 0, t: 0, color: 0, ends: 1,
      segs: this.tenderSegs, quads: this.tenderQuads, rings: this.rings,
      pip: this.sprite('pip'), add: F_ADD, f: FLEET_BEAM, breathOut: this.breathOut,
    };
    this.auraCtx = {
      x: 0, y: 0, R: 0, r0: ANCHOR_R, t: 0, phase: 0, id: 0,
      color: 0, alpha: 1, hidden: false,
      rings: this.rings, quads: this.auraQuads, segs: this.tenderSegs,
      hex: NO_ENTRY, hexRim: NO_ENTRY, pip: NO_ENTRY,
      sprite: this.sprite,
      members: (fn) => this.eachAuraMember(fn),
      cells: (fn) => this.eachAuraCell(fn),
      f: FLEET_AURA,
    };
    this.rebuildKeys();
  }

  /**
   * The atlas keys a look decides, built once per look (R833, R835).
   *
   * `packHulls` then reads a key by `cls * 2 + hollow` with no allocation,
   * and the badge by its slot.
   */
  private rebuildKeys(): void {
    for (const cls of KNOWN_CLASSES) {
      // The dart is the same shape in every set, so it is baked one time
      // under the AUTO set (`atlas.ts hullKey`): `shipStyleOf` answers the
      // AUTO set for a class with no hull field.
      const style = shipStyleOf(this.look, cls);
      this.hullKeys.set(cls * 2, hullKey(style, cls, false));
      this.hullKeys.set(cls * 2 + 1, hullKey(style, cls, true));
    }
    this.liveryKeys.clear();
    for (let n = 1; n <= BADGE_SLOTS; n++) this.badgeKeys[n] = badgeKey(this.look.digits, n);
  }

  /**
   * The ink of hull `i`: the NPC ink for a hull of the NPC side (R1797,
   * R2637), else the colour of its faction. Every mark of the hull reads
   * it, so the NPC side never wears the neutral grey of faction 0.
   */
  private inkAt(i: number): number {
    return this.npc[i] !== NpcKind.NPC_KIND_NONE ? NPC_INK.color : factionColor(this.faction[i]);
  }

  /** The NPC kind of a fleet, or NONE when the layer holds no such hull. */
  npcOf(fleetId: number): NpcKind {
    const i = this.idToIndex.get(fleetId);
    return i === undefined ? NpcKind.NPC_KIND_NONE : this.npc[i];
  }

  get count(): number { return this.n; }
  get visible(): boolean { return this.shown; }
  setVisible(v: boolean): void { this.shown = v; }

  /**
   * The canvas half of the look (R814): every mark of this layer a theme
   * can change reads it.
   *
   * The badge keys and the hull keys are rebuilt here, because both are baked
   * into an atlas cell and a value of the `digits` field, or a ship icon set,
   * bakes a different cell (R833, R835). Nothing else is cached per look, so
   * nothing else has to be dropped.
   */
  setLook(l: SceneLook): void {
    this.look = l;
    this.rebuildKeys();
  }

  /** The look in force. The marks are AUTO's until each package lands. */
  get sceneLook(): SceneLook { return this.look; }

  /**
   * The chart reading the charge pip is drawn in (R1568): the reading the
   * holds wear, `m` under the setting `off`. The scene writes it with the
   * hold style, and nothing happens while it is the reading in force.
   */
  setPipEra(era: HoldEra): void {
    if (era === this.pipEra) return;
    this.pipEra = era;
    this.pipKeys = pipKeysOf(era);
  }

  /**
   * Fades everything the layer draws, as one uniform per batch (R499, R838).
   *
   * Stacked Deck settles the fleets onto the map last, after the sites
   * (`render/spawn.ts:496`), and the reference does it with the alpha of the
   * container they all sit in. There is no container here, so the fade is
   * the batch fade of `batches.ts` — one uniform written on `flush`, and not
   * a scale on the alpha of every instance the pack writes. Every batch of
   * the layer takes it, so the hulls, the wakes, the order legs, the rings,
   * the marks and the readout bars fade as one picture.
   *
   * The arrival puts it back to 1 on every exit path it has, so a batch can
   * never be left dim by a spawn that ended some other way.
   */
  setAlpha(a: number): void {
    for (let i = 0; i < this.sinks.length; i++) this.sinks[i].globalAlpha = a;
  }

  /**
   * The mark scale (R1981, ui-map-interface.md §6.1): every mark of one
   * hull scales about the hull by `k`, and every mark that states a map
   * length or a map place keeps its size. 1 is the season's picture. A
   * value that is not a positive finite number reads as 1.
   */
  setMarkScale(k: number): void {
    this.pivot.k = Number.isFinite(k) && k > 0 ? k : 1;
  }

  /** The mark scale in force. */
  get markScale(): number { return this.pivot.k; }

  /** Pivots the hull sinks on hull `i` (R1981). */
  private pin(i: number): void {
    this.pivot.x = this.x[i];
    this.pivot.y = this.y[i];
  }

  /**
   * The `#anchoring=<id>` knob (R1125): one anchoring look every anchor hull
   * wears instead of the theme's. '' gives the theme its pick back. It is an
   * eye-test knob and never a viewer setting.
   */
  setAnchorForced(id: string): void {
    this.anchorForced = id;
  }

  /**
   * The `#holdfire=<id>` knob (R1439): one hold-fire look every own hull that
   * holds fire wears instead of the theme's. '' gives the theme its pick
   * back. It is an eye-test knob and never a viewer setting.
   */
  setHoldFireForced(id: string): void {
    this.holdForced = id;
  }

  /**
   * The `#reloadlook=<id>` knob (R1474): one tube reload look every own
   * hull with tubes wears instead of the theme's. '' gives the theme its
   * pick back. It is an eye-test knob and never a viewer setting.
   */
  setReloadForced(id: string): void {
    this.reloadForced = id;
  }

  /** The `#reload=0` knob (R1474): false takes the tube reload look off. */
  setReloadOn(on: boolean): void {
    this.reloadOn = on;
  }

  /**
   * The tubes of a class loaded with a kind (R1474). The wiring points it
   * at the hello catalog, the way the fleet card reads the TUBES bar
   * (R836); the default answers no tubes, so a host that wires nothing
   * draws no tube reload look. A new catalog drops what was cached.
   */
  setRounds(fn: (cls: number, kind: number) => ReloadRound): void {
    this.roundOf = fn;
    this.rounds.clear();
  }

  private roundOf: (cls: number, kind: number) => ReloadRound = (_c, kind) => ({ mag: 0, kind, name: '' });

  /** The fixed order of the ship rounds, Harpoon, Remora, Torpor (R2017):
   *  the wiring hands it from the hello catalog (`ui/rounds.ts`). An empty
   *  order fires the active round alone. */
  roundOrder: readonly number[] = [];

  /** The tubes of one slot, cached by class and kind. */
  private roundAt(i: number): ReloadRound {
    const key = this.cls[i] * 64 + this.firingKind[i];
    let r = this.rounds.get(key);
    if (r === undefined) {
      r = this.roundOf(this.cls[i], this.firingKind[i]);
      this.rounds.set(key, r);
    }
    return r;
  }

  /**
   * The map seconds one WALL second runs, for the tube reload look to run
   * `reload_sec` down between two frames (R1474). The wiring points it at
   * the state's tactical scale; 0 holds the wire's figure still.
   */
  mapSecPerWallSec: () => number = () => 0;

  // -------------------------------------------------------------------------
  // Growing the arrays
  // -------------------------------------------------------------------------

  private grow(): void {
    const cap = this.cap * 2;
    growColumns(this, FLEET_COLUMNS, cap);
    this.wake = grown(this.wake, cap, WAKE_POINTS * 2);
    this.cap = cap;
  }

  /** Opens a slot for a fleet the frame has not named before. */
  private add(fleetId: number, wx: number, wy: number): number {
    if (this.n === this.cap) this.grow();
    const i = this.n++;
    this.id[i] = fleetId;
    this.flags[i] = 0;
    this.faction[i] = 0;
    this.cls[i] = ShipClass.SHIP_CLASS_UNSPECIFIED;
    this.mission[i] = Mission.MISSION_UNSPECIFIED;
    this.anchor[i] = AnchorState.ANCHOR_STATE_UNSPECIFIED;
    // The group is read from the roster the match last stated, so a hull
    // that joins AFTER `setGroups` still carries the badge it was given.
    this.groupBadge[i] = this.groups.get(fleetId) ?? 0;
    this.hullPct[i] = 0; this.shieldPct[i] = 0; this.readinessPct[i] = 0;
    this.readinessFloorPct[i] = 0; this.noiseMilli[i] = 0;
    this.regenIn[i] = 0; this.regenAt[i] = 0;
    this.weapType[i] = 0; this.shieldType[i] = 0; this.firingKind[i] = 0;
    this.x[i] = wx; this.y[i] = wy;
    this.sx[i] = wx; this.sy[i] = wy;
    this.tx[i] = wx; this.ty[i] = wy;
    this.pxx[i] = 0; this.pyy[i] = 0;
    // A contact fades in, it never pops (§5).
    this.alpha[i] = 0;
    this.speed[i] = 0;
    this.heading[i] = 0;
    this.bow[i] = 0;
    this.hold[i] = 0; this.cargoCap[i] = 0; this.captureLeftSec[i] = 0;
    this.anchorLeftSec[i] = 0; this.beamLeftSec[i] = 0;
    this.anchorSince[i] = NaN;
    this.reloadSec[i] = 0; this.torpedoRange[i] = 0;
    this.sonarPx[i] = 0; this.shareSec[i] = 0;
    this.scanReadySec[i] = 0; this.decoyReadySec[i] = 0; this.torpedoes[i] = 0;
    this.reloadFull[i] = 0; this.reloadAt[i] = 0;
    this.firedAt[i] = NaN; this.readyAt[i] = -RELOAD_UNSEEN; this.wasReloading[i] = 0;
    this.escorting[i] = 0; this.formationOf[i] = 0; this.focus[i] = 0; this.chase[i] = 0; this.pledgeSite[i] = 0;
    this.tending[i] = 0; this.aura[i] = 0;
    this.captainId[i] = 0; this.ownerPlayer[i] = 0;
    this.effect[i] = 0;
    this.livery[i] = 0;
    this.npc[i] = NpcKind.NPC_KIND_NONE;
    this.brotherhood[i] = 0;
    this.boardTarget[i] = 0; this.boardedBy[i] = 0; this.boardMilli[i] = 0;
    this.boardLeft[i] = 0; this.boardAt[i] = 0; this.boardSince[i] = 0;
    this.wakeHead[i] = 0; this.wakeLen[i] = 0; this.wakeTimer[i] = 0;
    this.idToIndex.set(fleetId, i);
    this.cold.set(fleetId, {
      name: '', customName: false, ownerName: '', hold: [], hullMax: 0, shieldMax: 0, rounds: [], activeRound: 0,
      approach: null, conditions: [], legs: [], leg: 0, pass: 0, depthRating: 0,
      searchHint: 0, workLeft: 0, workAt: 0, workFull: 0,
      stow: [], layReady: 0, layAt: 0, loudLeft: 0, loudAt: 0,
    });
    return i;
  }

  /**
   * Takes the fleet at `i` out by moving the last one into its slot.
   *
   * The wake ring moves with it. A trail left behind in the old slot would
   * be drawn as the next fleet's, which is the one bug a dense prefix can
   * make and the reason the copy is stated here rather than left implied.
   */
  private removeAt(i: number): void {
    const gone = this.id[i];
    this.idToIndex.delete(gone);
    this.cold.delete(gone);
    const last = this.n - 1;
    if (i !== last) {
      moveSlot(this, FLEET_COLUMNS, i, last);
      const stride = WAKE_POINTS * 2;
      this.wake.copyWithin(i * stride, last * stride, (last + 1) * stride);
      this.idToIndex.set(this.id[i], i);
    }
    this.n = last;
  }

  // -------------------------------------------------------------------------
  // The wire
  // -------------------------------------------------------------------------

  /**
   * Applies one state frame. Frames are deltas: `full` replaces the picture
   * and `removed` names the contacts that are gone. A fleet the server stops
   * mentioning has NOT disappeared — it simply has not changed.
   */
  applyState(fleets: FleetState[], removed: number[], full: boolean): void {
    if (full) {
      // A full frame names no drops: a fleet it leaves out is dropped, and
      // a taken hull among them hands over as on a delta (R-g, R3101). The
      // walk runs backwards, because a taken hull leaves its slot at once
      // and the swap-remove moves a slot the walk has read into it.
      const seen = new Set<number>();
      for (const w of fleets) seen.add(w.id);
      for (let i = this.n - 1; i >= 0; i--) {
        if (!seen.has(this.id[i])) this.drop(this.id[i]);
      }
    }
    for (const gone of removed) this.drop(gone);
    for (const w of fleets) {
      const wx = w.xMpx / MPX_PER_PX, wy = w.yMpx / MPX_PER_PX;
      let i = this.idToIndex.get(w.id);
      const fresh = i === undefined;
      if (i === undefined) i = this.add(w.id, wx, wy);
      // The heading is the step between two server places. The eased gap
      // is too short for this: a slow hull trails its record by well under a
      // pixel, so a gate on the gap froze its bearing and the hull drifted
      // sideways. A hull that did not move keeps its last bearing. The step
      // is taken in the store's own Float32 precision: a still hull re-sent
      // on a fractional pixel read a step of about 1e-5 px against its
      // rounded copy, and its bearing turned north or south (R1538).
      // Under the approach the bow is the server's own heading (R2376):
      // a hull that fights turns its bow to its target and not to its
      // travel. A new slot takes it at once, so a sighting never swings in
      // from east; `advance` turns every other one toward it.
      const fx = Math.fround(wx), fy = Math.fround(wy);
      const hx = fx - this.sx[i], hy = fy - this.sy[i];
      if (this.bowHeading) {
        this.bow[i] = bowOfWire(w.headingMrad);
        if (fresh) this.heading[i] = this.bow[i];
      } else if (hx !== 0 || hy !== 0) this.heading[i] = Math.atan2(hy, hx);
      this.sx[i] = fx; this.sy[i] = fy;
      let f = this.flags[i]
        & ~(F_FADING | F_OWN | F_SLOWED | F_DEEP | F_CLASSIFIED | F_SHARING
          | F_DEEP_RUN | F_SHAFT | F_AUTO_FIELD_OFF | F_HOLD_FIRE | F_ALL_ROUND | F_HEARD | F_COURSE | F_FORMATION
          | F_PRESSURE);
      if (w.own) f |= F_OWN;
      // The broadcast bit reaches every commander of the faction; the
      // countdown is on the owner's own hulls alone, in map seconds (R453,
      // `render/fleets.ts:604-607`).
      if (w.sonarShare) f |= F_SHARING;
      if (w.slowed) f |= F_SLOWED;
      if (w.deep) f |= F_DEEP;
      if (w.deepRun) f |= F_DEEP_RUN;
      if (w.shaft) f |= F_SHAFT;
      if (w.autoFieldOff) f |= F_AUTO_FIELD_OFF;
      if (w.holdFire) f |= F_HOLD_FIRE;
      if (w.heard) f |= F_HEARD;
      if (w.own && w.legs.length > 0) f |= F_COURSE;
      if (w.own && w.formation) f |= F_FORMATION;
      if (w.own && underPressure(w.conditions)) f |= F_PRESSURE;
      if (readsBowAllRound(w)) f |= F_ALL_ROUND;
      if (w.cls === ShipClass.SHIP_CLASS_UNSPECIFIED) f |= F_CLASSIFIED;
      this.flags[i] = f;
      this.faction[i] = w.faction;
      this.cls[i] = w.cls;
      // A hull plus rises when a TENDED own hull's percent goes up on the
      // wire (R725, figure honesty R686). The bit is the one the last frame
      // set, which is what makes the mark say "the Tender did that" rather
      // than "something healed". A hull the frame has just added carries no
      // bit, so a first sighting never fires one.
      if (w.own && w.hullPct > this.hullPct[i] && (this.flags[i] & F_TENDED) !== 0) {
        this.onHeal(w.id, this.x[i], this.y[i], 'hull', w.faction);
      }
      this.hullPct[i] = w.hullPct;
      this.shieldPct[i] = w.shieldPct;
      this.mission[i] = w.mission;
      this.captainId[i] = w.captainId;
      this.cargoCap[i] = w.cargoCap;
      this.captureLeftSec[i] = w.captureLeftSec;
      // When the anchor state began (R1125). A hull's first frame starts
      // its state at "never seen", so no landing beat fires on a sighting.
      if (Number.isNaN(this.anchorSince[i])) this.anchorSince[i] = -ANCHOR_SINCE_UNSEEN;
      else if (w.anchor !== this.anchor[i]) this.anchorSince[i] = this.time;
      this.anchor[i] = w.anchor;
      this.anchorLeftSec[i] = w.anchorLeftSec;
      this.beamLeftSec[i] = w.beamLeftSec;
      this.escorting[i] = w.escorting;
      this.formationOf[i] = w.own ? w.formationOf : 0;
      this.focus[i] = w.focus;
      this.chase[i] = w.own ? w.chase : 0;
      this.pledgeSite[i] = w.pledgeSite;
      this.tending[i] = w.tending;
      this.aura[i] = w.aura;
      this.weapType[i] = w.weapType;
      this.shieldType[i] = w.shieldType;
      // The load by kind (R2017): the total aboard and the kind the next
      // shot fires, from `rounds` and `active_round`.
      this.torpedoes[i] = totalOf(w.rounds);
      // The shot and the reload clock of the tube reload look (R1474). A
      // reload that grows is a shot; a slot's first frame is no shot, so a
      // sighting never flashes a muzzle.
      if (Number.isNaN(this.firedAt[i])) this.firedAt[i] = -RELOAD_UNSEEN;
      else if (w.reloadSec > this.reloadSec[i]) this.firedAt[i] = this.time;
      this.reloadAt[i] = this.time;
      this.reloadFull[i] = w.reloadFullSec;
      this.reloadSec[i] = w.reloadSec;
      this.torpedoRange[i] = w.torpedoRange;
      this.firingKind[i] = firingKind(w.rounds, w.activeRound, this.roundOrder);
      this.ownerPlayer[i] = w.ownerPlayer;
      this.effect[i] = w.effect;
      this.livery[i] = w.livery;
      this.npc[i] = w.npc;
      this.brotherhood[i] = w.brotherhood;
      this.noteBoarding(i, w, fresh);
      this.readinessPct[i] = w.readinessPct;
      this.readinessFloorPct[i] = w.readinessFloorPct;
      this.noiseMilli[i] = w.noiseMilli;
      this.regenIn[i] = w.regenInSec;
      this.regenAt[i] = this.time;
      this.scanReadySec[i] = w.scanReadySec;
      this.decoyReadySec[i] = w.decoyReadySec;
      this.sonarPx[i] = w.sonarPx;
      this.shareSec[i] = w.sonarShareLeftSec;
      const c = this.cold.get(w.id)!;
      c.hold = w.hold;
      c.hullMax = w.hullMax;
      c.shieldMax = w.shieldMax;
      c.rounds = w.rounds;
      c.activeRound = w.activeRound;
      c.approach = this.bowHeading ? approachOf(w, c.approach) : null;
      c.conditions = w.conditions;
      c.legs = w.legs;
      c.leg = w.leg;
      c.pass = w.pass;
      c.depthRating = w.depthRating;
      c.searchHint = w.searchHint;
      // A frame with the same figure came inside the same second, so the
      // older stamp stays and the count does not jump back.
      // The wire states the full time of the work beside the time left, so
      // a client that joins in the middle of a work reads the true share
      // (R2833, R2834).
      c.workFull = w.workLeftSec > 0 ? w.workFullSec : 0;
      if (w.workLeftSec !== c.workLeft) { c.workLeft = w.workLeftSec; c.workAt = this.time; }
      if (w.own && w.workLeftSec > 0) this.working.add(w.id); else this.working.delete(w.id);
      // The Sapper (R2654): the same stamp rule for its two timers.
      c.stow = w.stow;
      if (w.layReadySec !== c.layReady) { c.layReady = w.layReadySec; c.layAt = this.time; }
      if (w.loudLeftSec !== c.loudLeft) { c.loudLeft = w.loudLeftSec; c.loudAt = this.time; }
      this.hold[i] = holdTotal(w.hold);
      // A delta frame that carries no string must not erase the one we hold.
      // The flag rides with the name, so a delta without one keeps both.
      if (w.name) { c.name = w.name; c.customName = w.customName; }
      if (w.ownerName) c.ownerName = w.ownerName;
      if (w.own) {
        this.tx[i] = w.txMpx / MPX_PER_PX;
        this.ty[i] = w.tyMpx / MPX_PER_PX;
        this.pxx[i] = w.pxMpx / MPX_PER_PX;
        this.pyy[i] = w.pyMpx / MPX_PER_PX;
      }
      this.dress(i);
      if (fresh && this.taken.length > 0 && w.npc === NpcKind.NPC_KIND_PIRATE) this.bindTaken(i);
    }
    this.markTended();
  }

  // -------------------------------------------------------------------------
  // The boarding (round 111 card 01, v2 §8.6, R2721)
  // -------------------------------------------------------------------------

  /**
   * The hulls this layer took out because a pirate group took them: the
   * last pose, the class cell and the ink, until the new hull of the NPC
   * side arrives or `TAKEN_WAIT_SEC` passes (ruling R-g).
   */
  private readonly taken: TakenHull[] = [];
  /** The hand-over of a taken hull, by the fleet id of the NEW hull. */
  private readonly handovers = new Map<number, Handover>();
  /** The boardings that stopped, while their snap runs. */
  private readonly snaps: BoardingSnap[] = [];
  /** The pirate cell key of each class and brotherhood, built once. */
  private readonly pirateKeys = new Map<number, string>();
  /** Whether the pirate lights move this frame: `!reducedMotion()`, read
   *  once a frame at the first light, so the hulls take one `matchMedia`
   *  read between them. `packHulls` clears it. */
  private lightMotion: boolean | undefined;
  /** The pirates that board each hull this frame, in slot order. Scratch. */
  private readonly boarders = new Map<number, number[]>();

  /** The share of the boarding of hull `i` done now, 0 to 1. */
  private shareAt(i: number): number {
    const elapsed = (this.time - this.boardAt[i]) * this.mapSecPerWallSec() * 1000;
    return boardingShare(this.boardMilli[i], this.boardLeft[i], elapsed);
  }

  /**
   * Is hull `i` taken, if the wire drops it now? A hull that a pirate
   * boards and whose share has passed `TAKEN_SHARE` is taken: the server
   * sends the share at each fifth, so the last frame before the take
   * carries four fifths at least.
   */
  private takenAt(i: number): boolean {
    return this.boardedBy[i] !== 0 && this.shareAt(i) >= TAKEN_SHARE;
  }

  /**
   * True when the wire drops fleet `fleetId` now because a pirate group
   * took it (ruling R-g). The feed reads it before the frame applies, so
   * the card of the old fleet id closes in the frame of the drop.
   */
  isBeingTaken(fleetId: number): boolean {
    const i = this.idToIndex.get(fleetId);
    return i !== undefined && this.takenAt(i);
  }

  /**
   * Forgets a taken hull at once: no fade, because the hand-over takes its
   * place, and no selection, because the old fleet id is gone for good.
   */
  private forgetTaken(i: number): void {
    const cls = KNOWN_CLASSES.has(this.cls[i]) ? this.cls[i] : ShipClass.SHIP_CLASS_UNSPECIFIED;
    const own = (this.flags[i] & F_OWN) !== 0;
    this.taken.push({
      x: this.sx[i], y: this.sy[i], cls, pirate: this.boardedBy[i], at: this.time,
      key: this.hullKeys.get(cls * 2) ?? '',
      color: own && this.look.ownFleet === 'white-core' ? 0xffffff : this.inkAt(i),
    });
    if (this.selectedId === this.id[i]) this.selectedId = 0;
    this.removeAt(i);
  }

  /**
   * Gives a new pirate hull the hand-over of the taken hull it replaces:
   * the same class, close to where the taken hull was. The new hull shows
   * at once, because the wipe is its arrival.
   */
  private bindTaken(i: number): void {
    for (let k = 0; k < this.taken.length; k++) {
      const t = this.taken[k];
      if (t.cls !== this.cls[i] || Math.hypot(t.x - this.sx[i], t.y - this.sy[i]) > TAKEN_MATCH_PX) continue;
      this.handovers.set(this.id[i], { key: t.key, color: t.color, pirate: t.pirate, at: this.time });
      this.alpha[i] = 1;
      this.taken.splice(k, 1);
      return;
    }
  }

  /**
   * Reads the boarding of one wire row. A boarding the look sees start
   * shoots its grapples; one it first sees under way does not. A boarding
   * that stops while both hulls sail on snaps: on the pirate the lines,
   * on the boarded hull the ring.
   */
  private noteBoarding(i: number, w: FleetState, fresh: boolean): void {
    const was = this.boardTarget[i], wasBy = this.boardedBy[i];
    const target = w.boardingTarget, by = w.boardedBy;
    const id = this.id[i];
    if ((target !== 0 && target !== was) || (by !== 0 && by !== wasBy)) {
      this.boardSince[i] = fresh || w.boardingMilli > 0 ? this.time - BOARDING_LOOK.strikeSec : this.time;
    }
    if (was !== 0 && target === 0 && this.idToIndex.has(was)) {
      this.snaps.push({ pirate: id, target: was, p: this.shareAt(i), at: this.time, ring: false });
    }
    if (wasBy !== 0 && by === 0) {
      this.snaps.push({ pirate: wasBy, target: id, p: this.shareAt(i), at: this.time, ring: true });
    }
    this.boardTarget[i] = target;
    this.boardedBy[i] = by;
    this.boardMilli[i] = w.boardingMilli;
    this.boardLeft[i] = w.boardingLeftMs;
    this.boardAt[i] = this.time;
  }

  /** The pose of hull `i`, or null when the layer does not draw it. */
  private poseAt(i: number | undefined, into: Pose): Pose | null {
    if (i === undefined || (this.flags[i] & (F_HIDDEN | F_HULL_HIDDEN)) !== 0) return null;
    into.x = this.x[i]; into.y = this.y[i]; into.h = this.heading[i];
    return into;
  }

  private readonly poseA: Pose = { x: 0, y: 0, h: 0 };
  private readonly poseB: Pose = { x: 0, y: 0, h: 0 };

  /**
   * The boarding look of this frame (card 01): the ring of every boarded
   * hull, the grapples of every pirate that boards, the snaps and the
   * hand-overs. The figures are map lengths, so the mark scale does not
   * move them.
   */
  private packBoarding(): void {
    const segs = this.boardSegs, rings = this.boardRings;
    const ink = NPC_INK.color, flash = 0xffffff;
    const L = BOARDING_LOOK;
    const b = this.boarders;
    for (const list of b.values()) list.length = 0;
    for (let i = 0; i < this.n; i++) {
      if (this.boardedBy[i] !== 0) {
        const p = this.poseAt(i, this.poseB);
        if (p) packRing(rings, p, this.shareAt(i), this.alpha[i], ink);
      }
      const t = this.boardTarget[i];
      if (t === 0) continue;
      let list = b.get(t);
      if (!list) { list = []; b.set(t, list); }
      list.push(i);
    }
    for (const [target, list] of b) {
      // A hull nobody boards now leaves the scratch, so the map holds the
      // boarded hulls of this frame and not every hull ever boarded (R3102).
      if (list.length === 0) { b.delete(target); continue; }
      const tp = this.poseAt(this.idToIndex.get(target), this.poseB);
      if (!tp) continue;
      // One pirate fires both grapples; two or more fire one each.
      for (let k = 0; k < 2; k++) {
        const i = list.length === 1 ? list[0] : list[k];
        const pp = this.poseAt(i, this.poseA);
        if (!pp) continue;
        packGrapple(segs, rings, pp, tp, k, this.time - this.boardSince[i], this.shareAt(i), this.alpha[i], ink, flash);
      }
    }
    for (let k = this.snaps.length - 1; k >= 0; k--) {
      const s = this.snaps[k];
      const stop = (this.time - s.at) / L.snapSec;
      if (stop >= 1) { this.snaps.splice(k, 1); continue; }
      const tp = this.poseAt(this.idToIndex.get(s.target), this.poseB);
      if (!tp) continue;
      if (s.ring) { packRing(rings, tp, drainedShare(s.p, stop), 1, ink); continue; }
      const pp = this.poseAt(this.idToIndex.get(s.pirate), this.poseA);
      if (!pp) continue;
      for (let g = 0; g < 2; g++) packSnap(segs, rings, pp, tp, g, stop, ink, flash);
    }
    for (const [id, hand] of this.handovers) {
      const h = (this.time - hand.at) / L.handSec;
      // An ended hand-over goes here (R3102), whether its hull drew this frame or
      // not: a hull off the glass or gone never ends it in `packPirate`.
      if (h >= 1) { this.handovers.delete(id); continue; }
      const tp = this.poseAt(this.idToIndex.get(id), this.poseB);
      if (!tp) continue;
      const fade = 1 - ease(h / L.handFade);
      if (fade > 0) {
        packRing(rings, tp, 1, fade, ink);
        const pp = this.poseAt(this.idToIndex.get(hand.pirate), this.poseA);
        if (pp) for (let g = 0; g < 2; g++) packGrapple(segs, rings, pp, tp, g, L.strikeSec, 1, fade, ink, flash);
      }
      if (h < L.edgeEnd) packWipeEdge(segs, tp, wipeX(h), flash);
    }
    for (let k = this.taken.length - 1; k >= 0; k--) {
      if (this.time - this.taken[k].at > TAKEN_WAIT_SEC) this.taken.splice(k, 1);
    }
  }

  /**
   * One pirate hull (round 113, R2720): the lean swallowtail with its
   * class part and the mark of its brotherhood, in the NPC ink. The cell
   * stands on the hull origin, so a mark astern does not move the hull
   * forward. The lights of card 01 and card 11 draw at an alpha each
   * frame. While a taken hull hands over, the swallowtail wipes over the
   * class mark from the bow to the stern (round 111 card 01).
   */
  private packPirate(i: number): void {
    const cls = KNOWN_CLASSES.has(this.cls[i]) ? this.cls[i] : ShipClass.SHIP_CLASS_UNSPECIFIED;
    const bro = this.brotherhood[i];
    const slot = cls * 8 + bro;
    let key = this.pirateKeys.get(slot);
    if (key === undefined) { key = pirateHullKey(cls, bro); this.pirateKeys.set(slot, key); }
    const e = this.sprite(key);
    const q = this.h.markQuads;
    const x = this.x[i], y = this.y[i], hd = this.heading[i], a = this.alpha[i];
    const hand = this.handovers.get(this.id[i]);
    if (hand !== undefined) {
      const h = (this.time - hand.at) / BOARDING_LOOK.handSec;
      if (h < 1) {
        const X = wipeX(h);
        if (hand.key !== '') this.packCrop(q, x, y, hd, this.sprite(hand.key), hand.color, a, -Infinity, X);
        this.packCrop(q, x, y, hd, e, NPC_INK.color, a, X, Infinity);
        return;
      }
    }
    const light = pirateLightOf(bro);
    if (light === 'knot') this.packLights(i, bro, cls, light);
    q.sprite(x, y, hd, e, NPC_INK.color, a, 1, 0, false);
    if (light === 'lantern') this.packLights(i, bro, cls, light);
  }

  /** The lights of a brotherhood mark on pirate hull `i`. */
  private packLights(i: number, bro: number, cls: number, light: PirateLight): void {
    const e = this.sprite(PIRATE_LIGHT_KEYS[light]);
    const xs = pirateLightXs(bro, cls);
    const c = Math.cos(this.heading[i]), s = Math.sin(this.heading[i]);
    // The phase of the hull, so the hulls of a group do not blink in step.
    const seed = (this.id[i] * 0.618) % 1;
    const motion = (this.lightMotion ??= !reducedMotion());
    for (let k = 0; k < xs.length; k++) {
      const al = pirateLightAlpha(bro, k, this.time, light === 'lantern' ? seed * Math.PI * 2 : seed, motion);
      this.h.markQuads.sprite(this.x[i] + xs[k] * c, this.y[i] + xs[k] * s, this.heading[i], e,
        NPC_INK.color, this.alpha[i] * al);
    }
  }

  /** The part of cell `e` between the local x `from` and `to`, on the
   *  hull origin. */
  private packCrop(
    q: QuadSink, x: number, y: number, hd: number, e: SpriteEntry,
    color: number, a: number, from: number, to: number,
  ): void {
    const c = cropOf(e, from, to);
    if (c) q.quad(x, y, c.cx, e.oy, c.w, e.h, hd, 0, color, a, c.u0, e.v0, c.u1, e.v1);
  }

  /**
   * Re-reads the two Tender marks over the whole array (R725).
   *
   * `tending` names the PATIENT, so the bit lands on another slot than the
   * one the wire wrote; and frames are deltas, so a Tender that stopped
   * tending may not be in this frame at all. The walk is therefore over
   * every fleet and not over the frame.
   */
  private markTended(): void {
    for (let i = 0; i < this.n; i++) this.flags[i] &= ~(F_TENDED | F_AURA);
    for (let i = 0; i < this.n; i++) {
      if (this.tending[i] !== 0) {
        const patient = this.idToIndex.get(this.tending[i]);
        if (patient !== undefined) this.flags[patient] |= F_TENDED;
      }
      if (this.aura[i] !== 0) this.flags[i] |= F_AURA;
    }
  }

  /**
   * Puts every hull where the wire says it is, now (R531).
   *
   * The layer eases toward the last record over about 90 ms, which is what
   * hides the 10 Hz update grid on the live map. A replay film that seeks
   * has no grid to hide: the time jumped, so the picture must jump with it
   * rather than slide across the window for a fifth of a second.
   *
   * The trail goes with the position. A scar left behind would be drawn
   * from where the hull was before the seek to where it is after it, which
   * is a course no hull ever sailed.
   */
  snap(): void {
    for (let i = 0; i < this.n; i++) {
      this.x[i] = this.sx[i];
      this.y[i] = this.sy[i];
      if (this.bowHeading) this.heading[i] = this.bow[i];
      this.alpha[i] = (this.flags[i] & F_FADING) !== 0 ? 0 : 1;
      this.speed[i] = 0;
      this.wakeLen[i] = 0;
      this.wakeTimer[i] = 0;
    }
  }

  /** Marks a contact as lost; it fades rather than vanishing. */
  private drop(fleetId: number): void {
    const i = this.idToIndex.get(fleetId);
    if (i === undefined) return;
    if (this.takenAt(i)) { this.forgetTaken(i); return; }
    this.flags[i] |= F_FADING;
  }

  /**
   * The one bit that says whose a hull is (`fleets.ts:547-567`): a squadmate
   * wears the hollow master, an own hull the filled one, white and a little
   * larger. Every path that can change it — a state frame, a late squad
   * roster — comes through here, so no two of them can disagree.
   */
  private dress(i: number): void {
    const own = (this.flags[i] & F_OWN) !== 0;
    const owner = this.ownerPlayer[i];
    const mate = !own && owner !== 0 && this.squadIds.has(owner);
    if (mate) this.flags[i] |= F_SQUAD;
    else this.flags[i] &= ~F_SQUAD;
  }

  /**
   * Names the viewer's squadmates by account id and re-dresses every hull it
   * changes. The squad comes off the clan frame, which lands after the hello.
   *
   * `slots` is the squad slot of each mate (R185): the 1-based rank inside
   * the viewer's squad, which nothing on the wire carries. The squad slot
   * numeral of a friendly mark value prints it (`packSlotNumeral`, R830).
   */
  setSquadIds(ids: Set<number>, slots?: ReadonlyMap<number, number>): void {
    this.squadIds = ids;
    this.squadSlots = slots ?? null;
    for (let i = 0; i < this.n; i++) this.dress(i);
  }

  /**
   * Lifts the hulls of the reader's NEW squad toward white, one time, and
   * lets them settle (R1571, §6.1). The feed calls it after `setSquadIds`
   * when a clan frame moved the reader to another squad, and never under
   * reduced motion. The lift starts at `squad_change.lift` and falls to 0
   * in `squad_change.life_sec` seconds of the layer clock.
   */
  liftSquad(): void {
    this.squadLiftAt = this.time;
  }

  /** How far a squadmate's ink is lifted this frame by a squad change. */
  private squadLiftNow(): number {
    const c = FLEET_MARKS.squadChange;
    const u = (this.time - this.squadLiftAt) / c.lifeSec;
    return u >= 0 && u < 1 ? c.lift * (1 - u) : 0;
  }

  /** Names the group every own hull holds, fleet id to slot (R403). */
  setGroups(m: ReadonlyMap<number, number>): void {
    this.groups = m;
    for (let i = 0; i < this.n; i++) this.groupBadge[i] = m.get(this.id[i]) ?? 0;
  }

  // -------------------------------------------------------------------------
  // The lookups. None of them reads the cull bit.
  // -------------------------------------------------------------------------

  private infoBuf: FleetInfo[] = [];

  /**
   * Snapshot for the sonar and wake layers. The array and its objects are
   * reused between calls (R131): every field is overwritten and the length
   * is trimmed to the live count, so a consumer must read it inside the same
   * frame and must not keep the reference.
   */
  info(): FleetInfo[] {
    const out = this.infoBuf;
    for (let i = 0; i < this.n; i++) {
      let o = out[i];
      if (!o) {
        o = {
          id: 0, faction: 0, x: 0, y: 0, alpha: 0, own: false,
          cls: ShipClass.SHIP_CLASS_UNSPECIFIED, speed: 0, classified: false, sonarPx: 0,
          sharing: false,
        };
        out[i] = o;
      }
      o.id = this.id[i];
      o.faction = this.faction[i];
      o.x = this.x[i]; o.y = this.y[i];
      o.alpha = this.alpha[i];
      o.own = (this.flags[i] & F_OWN) !== 0;
      o.cls = this.cls[i];
      o.speed = this.speed[i];
      o.classified = (this.flags[i] & F_CLASSIFIED) !== 0;
      o.sonarPx = this.sonarPx[i];
      // The Oracle's broadcast (R453, R456). The sonar layer breathes the
      // coverage ring of a hull that lends its array, so a reader tells a
      // lent array from a plain one with no legend and no second colour.
      o.sharing = (this.flags[i] & F_SHARING) !== 0;
    }
    out.length = this.n;
    return out;
  }

  private chartBuf: ChartFleet[] = [];

  /**
   * Snapshot for the chart furniture (R548, §17).
   *
   * It is a second reused buffer beside `info()` and not a widening of it.
   * `info()` is the hot path of the sonar and the wake layers, and the
   * chart needs six more fields — the order, its target, the patrol's other
   * end, the two clocks a reveal and a share run on — that neither of those
   * two layers reads. The buffer and its objects are reused (R131): a
   * reader must use them inside the frame and must not keep the reference.
   */
  chartFleets(): readonly ChartFleet[] {
    const out = this.chartBuf;
    for (let i = 0; i < this.n; i++) {
      let o = out[i];
      if (!o) {
        o = {
          id: 0, faction: 0, x: 0, y: 0, alpha: 0, heading: 0, own: false,
          squad: false, ownerPlayer: 0, classified: false, cls: 0, speed: 0,
          mission: 0, tx: 0, ty: 0, px: 0, py: 0, escorting: 0, focus: 0, chase: 0,
          pledgeSite: 0, commissioned: false, anchor: 0, sonarPx: 0, scanReadySec: 0, sharing: false,
          torpedoRange: 0, firingKind: 0, effect: 0, npc: 0, course: false,
        };
        out[i] = o;
      }
      const f = this.flags[i];
      o.id = this.id[i];
      o.faction = this.faction[i];
      o.npc = this.npc[i];
      o.x = this.x[i]; o.y = this.y[i];
      o.alpha = this.alpha[i];
      o.heading = this.heading[i];
      o.own = (f & F_OWN) !== 0;
      o.squad = (f & F_SQUAD) !== 0;
      o.ownerPlayer = this.ownerPlayer[i];
      o.classified = (f & F_CLASSIFIED) !== 0;
      o.cls = this.cls[i];
      o.speed = this.speed[i];
      o.mission = this.mission[i];
      o.tx = this.tx[i]; o.ty = this.ty[i];
      o.px = this.pxx[i]; o.py = this.pyy[i];
      o.escorting = this.escorting[i];
      o.focus = this.focus[i];
      o.chase = this.chase[i];
      o.pledgeSite = this.pledgeSite[i];
      o.commissioned = (f & F_OWN) !== 0 && this.commissioned.has(o.id);
      o.anchor = this.anchor[i];
      o.sonarPx = this.sonarPx[i];
      o.scanReadySec = this.scanReadySec[i];
      o.sharing = (f & F_SHARING) !== 0;
      o.torpedoRange = this.torpedoRange[i];
      o.firingKind = this.firingKind[i];
      o.effect = this.effect[i];
      o.course = (f & F_COURSE) !== 0;
    }
    out.length = this.n;
    return out;
  }

  /** One fleet in the UI's shape. */
  private describeAt(i: number): UIFleet {
    const fleetId = this.id[i];
    const c = this.cold.get(fleetId)!;
    return {
      id: fleetId, name: c.name || `Fleet ${fleetId}`, customName: c.customName, cls: this.cls[i],
      hullPct: this.hullPct[i], shieldPct: this.shieldPct[i],
      mission: this.mission[i], captainId: this.captainId[i],
      hold: c.hold, cargoCap: this.cargoCap[i],
      captureLeftSec: this.captureLeftSec[i], anchor: this.anchor[i],
      anchorLeftSec: this.anchorLeftSec[i],
      beamLeftSec: this.beamLeftSec[i],
      escorting: this.escorting[i], focus: this.focus[i],
      weapType: this.weapType[i], shieldType: this.shieldType[i],
      torpedoes: this.torpedoes[i], reloadSec: this.reloadSec[i], reloadFullSec: this.reloadFull[i],
      torpedoRange: this.torpedoRange[i], firingKind: this.firingKind[i],
      rounds: c.rounds, activeRound: c.activeRound,
      slowed: (this.flags[i] & F_SLOWED) !== 0, deep: (this.flags[i] & F_DEEP) !== 0,
      deepRun: (this.flags[i] & F_DEEP_RUN) !== 0,
      shaft: (this.flags[i] & F_SHAFT) !== 0,
      autoFieldOff: (this.flags[i] & F_AUTO_FIELD_OFF) !== 0,
      holdFire: (this.flags[i] & F_HOLD_FIRE) !== 0,
      heard: (this.flags[i] & F_HEARD) !== 0,
      x: this.x[i], y: this.y[i],
      readinessPct: this.readinessPct[i],
      readinessFloorPct: this.readinessFloorPct[i],
      noiseMilli: this.noiseMilli[i],
      regenInSec: this.regenIn[i] > 0
        ? Math.max(0, this.regenIn[i] - (this.time - this.regenAt[i]) * this.mapSecPerWallSec())
        : 0,
      scanReadySec: this.scanReadySec[i],
      decoyReadySec: this.decoyReadySec[i],
      // The SHARE button's own cooldown (R453). The shared DOM chrome draws
      // the button and reads the countdown from here.
      shareSec: this.shareSec[i],
      ownerPlayer: this.ownerPlayer[i], ownerName: c.ownerName,
      hullMax: c.hullMax, shieldMax: c.shieldMax,
      ...(c.approach ? { approach: { ...c.approach } } : {}),
      ...(c.conditions.length > 0 ? { conditions: c.conditions } : {}),
      ...(c.legs.length > 0 ? { course: { legs: c.legs, leg: c.leg, pass: c.pass } } : {}),
      ...((this.flags[i] & F_FORMATION) !== 0 ? { formation: true } : {}),
      ...(this.formationOf[i] !== 0 ? { formationOf: this.formationOf[i] } : {}),
      ...(c.depthRating !== 0 ? { depthRating: c.depthRating } : {}),
      ...(c.searchHint !== 0 ? { searchHint: c.searchHint } : {}),
      ...(c.workLeft > 0
        ? { workLeftSec: Math.max(0, c.workLeft - (this.time - c.workAt) * this.mapSecPerWallSec()) }
        : {}),
      ...(this.npc[i] !== NpcKind.NPC_KIND_NONE ? { npc: this.npc[i] } : {}),
      ...(this.brotherhood[i] !== 0 ? { brotherhood: this.brotherhood[i] } : {}),
      ...(this.boardTarget[i] !== 0 ? { boardingTarget: this.boardTarget[i], boardingShare: this.shareAt(i) } : {}),
      ...(this.boardedBy[i] !== 0 ? { boardedBy: this.boardedBy[i], boardingShare: this.shareAt(i) } : {}),
      ...(c.stow.length > 0 ? { stow: c.stow } : {}),
      ...(c.layReady > 0 ? { layReadySec: Math.max(0, c.layReady - (this.time - c.layAt) * this.mapSecPerWallSec()) } : {}),
      ...(c.loudLeft > 0 ? { loudLeftSec: Math.max(0, c.loudLeft - (this.time - c.loudAt) * this.mapSecPerWallSec()) } : {}),
    };
  }

  /** Own fleets, for the DOM panels. */
  own(): UIFleet[] {
    const out: UIFleet[] = [];
    for (let i = 0; i < this.n; i++) {
      if ((this.flags[i] & F_OWN) === 0) continue;
      out.push(this.describeAt(i));
    }
    return out.sort((a, b) => a.id - b.id);
  }

  /**
   * Where every hull the layer holds stands, for the siege window of the
   * chrome (`ui/siegewindow.ts`): the side near a target and the readiness
   * of a squad mate. A contact is here only while the reader's faction
   * hears it, so the list gives the chrome nothing the map does not show.
   */
  presence(): FleetPresence[] {
    const out: FleetPresence[] = [];
    for (let i = 0; i < this.n; i++) {
      out.push({
        x: this.x[i], y: this.y[i], faction: this.faction[i],
        own: (this.flags[i] & F_OWN) !== 0, owner: this.ownerPlayer[i], pledgeSite: this.pledgeSite[i],
      });
    }
    return out;
  }

  /**
   * One fleet, whoever owns it, plus whose it is. The server strips an enemy
   * contact's composition before it reaches us, so for a contact the fields
   * are simply zero and `classified` says so.
   */
  describe(fleetId: number): (UIFleet & { own: boolean; faction: number; classified: boolean }) | null {
    const i = this.idToIndex.get(fleetId);
    if (i === undefined) return null;
    return {
      ...this.describeAt(i),
      own: (this.flags[i] & F_OWN) !== 0,
      faction: this.faction[i],
      classified: (this.flags[i] & F_CLASSIFIED) !== 0,
    };
  }

  at(fleetId: number): { x: number; y: number } | null {
    const i = this.idToIndex.get(fleetId);
    return i === undefined ? null : { x: this.x[i], y: this.y[i] };
  }

  /** The point and the order point of an own hull, or null for a hull that
   *  is no own one: the on-ramp offer asks it (R3075). */
  orderOf(fleetId: number): { x: number; y: number; tx: number; ty: number } | null {
    const i = this.idToIndex.get(fleetId);
    if (i === undefined || (this.flags[i] & F_OWN) === 0) return null;
    return { x: this.x[i], y: this.y[i], tx: this.tx[i], ty: this.ty[i] };
  }

  /** The bow, the speed and the order of an own hull at sea, for the helm
   *  keys of the map (R2707), or null for a hull that is no own one. */
  helmOf(fleetId: number): { headingRad: number; speedPxS: number; mission: number; cls: number; deep: boolean } | null {
    const i = this.idToIndex.get(fleetId);
    if (i === undefined || (this.flags[i] & F_OWN) === 0) return null;
    return {
      headingRad: this.bowHeading ? this.bow[i] : this.heading[i], speedPxS: this.speed[i],
      mission: this.mission[i], cls: this.cls[i], deep: (this.flags[i] & F_DEEP) !== 0,
    };
  }

  /**
   * The hull class of one contact, or nothing when the layer does not hold
   * it. The sinking cue asks this and allocates nothing for the answer
   * (R547); `describe` builds a whole object and is for the chrome.
   */
  classOf(fleetId: number): ShipClass | undefined {
    const i = this.idToIndex.get(fleetId);
    return i === undefined ? undefined : this.cls[i];
  }

  /**
   * The fleets that work a commission (R2833), from `Commission.fleet_id` of
   * the Empire frame. Their order wears the commission ink.
   */
  setCommissioned(ids: ReadonlySet<number>): void { this.commissioned = ids; }

  /**
   * Every own fleet that works a SEARCH at its point now
   * (R2833): its pose, the share of the work done and what it works at,
   * for the cache work look. The share and the wall seconds left count
   * the map seconds left down between two frames, as the card does. The
   * list and its entries are
   * reused, so a caller reads it inside the frame.
   */
  ownWorks(): readonly OwnWork[] {
    const out = this.worksBuf;
    let n = 0;
    for (const id of this.working) {
      const i = this.idToIndex.get(id), c = this.cold.get(id);
      // A hull that fades out works no more: the server dropped it.
      if (i === undefined || !c || c.workLeft <= 0 || (this.flags[i] & F_FADING) !== 0) { this.working.delete(id); continue; }
      const m = this.mission[i];
      if (m !== Mission.MISSION_SEARCH) continue;
      const rate = this.mapSecPerWallSec();
      const left = Math.max(0, c.workLeft - (this.time - c.workAt) * rate);
      const w = (this.worksPool[n] ??= { id: 0, x: 0, y: 0, head: 0, share: 0, wallLeft: 0 });
      out[n] = w;
      w.id = id; w.x = this.x[i]; w.y = this.y[i]; w.head = this.heading[i];
      w.share = c.workFull > 0 ? Math.min(1, Math.max(0, 1 - left / c.workFull)) : 0;
      w.wallLeft = rate > 0 ? left / rate : left;
      n++;
    }
    out.length = n;
    return out;
  }

  /**
   * One hull's pose, for the layer that draws a hull of its own (R1007).
   *
   * The gun-shot layer needs the place, the heading, the faction and the class
   * to redraw a silhouette a final-blow look owns. It is a SECOND reader of the
   * same columns `describe` reads, and it allocates nothing: one object is
   * reused, so a caller must read it inside the frame.
   */
  combatPose(fleetId: number): CombatHullPose | null {
    const i = this.idToIndex.get(fleetId);
    if (i === undefined) return null;
    const p = this.combatPoseBuf;
    p.x = this.x[i]; p.y = this.y[i];
    p.head = this.heading[i];
    p.faction = this.faction[i];
    p.cls = this.cls[i];
    return p;
  }

  private readonly combatPoseBuf: CombatHullPose =
    { x: 0, y: 0, head: 0, faction: 0, cls: 0 };

  /**
   * What one fleet's shields still hold, 0 to 100, or −1 where the layer does
   * not hold the fleet (R1011).
   *
   * A shield hit mark states what is LEFT of the shield, and it asks on every
   * frame. The answer is one map read and one array read, and it allocates
   * nothing; `describe` builds a whole object and is for the chrome.
   */
  shieldOf(fleetId: number): number {
    const i = this.idToIndex.get(fleetId);
    return i === undefined ? -1 : this.shieldPct[i];
  }

  /** Which account owns one fleet, or 0 where the layer does not hold it. */
  ownerOf(fleetId: number): number {
    const i = this.idToIndex.get(fleetId);
    return i === undefined ? 0 : this.ownerPlayer[i];
  }

  /**
   * The slot of the nearest visible hull to a world point inside `radius`,
   * or -1. Faint contacts are not clickable (§5). The cull bit is never read.
   */
  private nearest(x: number, y: number, radius: number): number {
    let bestI = -1;
    let bestD = radius * radius;
    for (let i = 0; i < this.n; i++) {
      if (this.alpha[i] < FAINT_ALPHA) continue;
      const dx = this.x[i] - x, dy = this.y[i] - y;
      const d = dx * dx + dy * dy;
      if (d < bestD) { bestD = d; bestI = i; }
    }
    return bestI;
  }

  /**
   * The nearest visible hull to a world point inside `radius`, with its class.
   *
   * The gun-shot layer reads the shooter off it (R1009). A faint contact is not
   * a reading, the same gate `pick` holds, and one object is reused.
   */
  nearestClass(x: number, y: number, radius: number): NearestClass | null {
    const bestI = this.nearest(x, y, radius);
    if (bestI < 0) return null;
    const out = this.nearestBuf;
    out.id = this.id[bestI];
    out.cls = this.cls[bestI];
    return out;
  }

  private readonly nearestBuf: NearestClass = { id: 0, cls: 0 };

  /**
   * The nearest OWN hull to a world point, at any distance, or null when
   * the layer holds no own hull. The touch log states it beside a missed
   * press (R1498). A faint own hull counts and says so, because `pick`
   * skips it and that alone explains a miss. It is a second reader beside
   * `nearest` and leaves `pick` alone. One object is reused.
   */
  nearestOwn(x: number, y: number): OwnHull | null {
    let bestI = -1;
    let bestD = Infinity;
    for (let i = 0; i < this.n; i++) {
      if ((this.flags[i] & F_OWN) === 0) continue;
      const dx = this.x[i] - x, dy = this.y[i] - y;
      const d = dx * dx + dy * dy;
      if (d < bestD) { bestD = d; bestI = i; }
    }
    if (bestI < 0) return null;
    const out = this.ownBuf;
    out.id = this.id[bestI];
    out.x = this.x[bestI];
    out.y = this.y[bestI];
    out.faint = this.alpha[bestI] < FAINT_ALPHA;
    return out;
  }

  private readonly ownBuf: OwnHull = { id: 0, x: 0, y: 0, faint: false };

  /**
   * The hulls another layer draws itself, which this layer then leaves out
   * (R1007).
   *
   * A final-blow look owns the hull it sinks: it draws the descent, so the
   * plain mark must not stand under it. The set is the gun-shot layer's own,
   * read every frame through `F_HIDDEN`, which gates the mark, the bars, the
   * cargo lane, the captain's chevron, the laden ring, the badge and the
   * group mark (R3128).
   */
  setSuppressed(ids: ReadonlySet<number> | null): void {
    this.suppressed = ids;
  }

  private suppressed: ReadonlySet<number> | null = null;

  /**
   * The hulls another layer draws the MARK of, while the fleet is alive
   * (R1012).
   *
   * A hull hit and a shield hit may redraw the silhouette, and the plain mark
   * must not stand under it. The fleet is still in the fight, so it keeps its
   * pair of bars, its badge, its group mark, its wake and its bracket: only
   * the silhouette and its own bloom stand down, through `F_HULL_HIDDEN`.
   */
  setHullSuppressed(ids: { has(id: number): boolean } | null): void {
    this.hullSuppressed = ids;
  }

  private hullSuppressed: { has(id: number): boolean } | null = null;

  /**
   * The hulls whose LADEN RING another layer draws itself (R1120).
   *
   * The `hold-ring` harvesting look turns the laden ring into the gauge of
   * the hold, so this layer's own plain ring must not stand under it. The set
   * is the gather layer's own, and it holds the hulls that cut now.
   */
  setLadenSuppressed(ids: ReadonlySet<number> | null): void {
    this.ladenSuppressed = ids;
  }

  private ladenSuppressed: ReadonlySet<number> | null = null;

  /**
   * Every hull the viewer may read a MISSION for, for the gather tracker
   * (R1120, R1121).
   *
   * That is an own hull and a hull of the own faction: `mission` reaches the
   * whole faction on the wire and stops there, and the hold and the shaft
   * flag reach the own player alone. A contact is left out, because a contact
   * carries none of the three.
   *
   * The walk writes into ONE reused record (R131), so the caller reads it
   * inside the callback and keeps no reference.
   */
  gatherers(fn: (h: GatherHull) => void): void {
    const h = this.gatherBuf;
    for (let i = 0; i < this.n; i++) {
      const f = this.flags[i];
      const own = (f & F_OWN) !== 0;
      const faction = own
        || (this.viewerFaction !== 0 && this.faction[i] === this.viewerFaction);
      if (!faction) continue;
      h.id = this.id[i];
      h.x = this.x[i];
      h.y = this.y[i];
      h.head = this.heading[i];
      h.own = own;
      h.gathering = this.mission[i] === Mission.MISSION_GATHER;
      h.shaft = (f & F_SHAFT) !== 0;
      // The hold and its cap reach the own player alone. A faction hull
      // answers 0, which the tracker reads as "not readable" (R1121).
      h.hold = own ? this.hold[i] : 0;
      h.cargoCap = own ? this.cargoCap[i] : 0;
      h.ink = this.inkAt(i);
      h.hidden = (f & F_HIDDEN) !== 0;
      fn(h);
    }
  }

  /**
   * The gather tracker, for the two pieces every player sees (R1120).
   *
   * The `gather` layer owns the tracker and this layer only READS it: the
   * live cargo lane and the lot figures are drawn on every hull that cuts,
   * whatever harvesting look the theme picked, so they are not recipes and
   * they need the state rather than the look. The scene hands the tracker
   * over once, and `null` is a page that draws no cut at all.
   */
  setGatherState(state: GatherReader | null): void {
    this.gatherState = state;
  }

  private gatherState: GatherReader | null = null;

  /** The one record `gatherers` walks with (R131). */
  private readonly gatherBuf: GatherHull = {
    id: 0, x: 0, y: 0, head: 0, own: false, gathering: false, shaft: false,
    hold: 0, cargoCap: 0, ink: 0xffffff, hidden: false,
  };

  /** Nearest visible fleet to a world point, within radius. */
  pick(x: number, y: number, radius: number): { id: number; own: boolean; faction: number; name: string } | null {
    // A hull the mark scale grows takes a wider pick; a hull it shrinks
    // keeps the finger's radius, and the nearest hull still wins (R1981).
    const bestI = this.nearest(x, y, radius * Math.max(1, this.pivot.k));
    if (bestI < 0) return null;
    const fleetId = this.id[bestI];
    return {
      id: fleetId,
      own: (this.flags[bestI] & F_OWN) !== 0,
      faction: this.faction[bestI],
      name: this.cold.get(fleetId)?.name ?? '',
    };
  }

  // -------------------------------------------------------------------------
  // The frame
  // -------------------------------------------------------------------------

  update(frame: FrameInfo): void {
    this.advance(frame.dtMs, frame.cull, zoomOf(frame.cam.zoom));
  }

  /**
   * Advances every fleet, then packs the instances of the ones that draw
   * (R192, §10.7).
   *
   * The interpolation, the reveal easing and the wake ledger run for every
   * fleet, culled or not: they are plain numbers, and a hull that stopped
   * moving off screen would slide into place when the camera came back.
   *
   * Three things are never culled by the hull's own position: an own fleet,
   * whose order line can reach anywhere on the map; the selected fleet,
   * whose bracket must not vanish; and a wake trail that reaches into the
   * picture, which is why the trail tests its own points.
   */
  private advance(deltaMS: number, cull: ViewRect, zoom = 1): void {
    this.zoom = zoom;
    // The stack count is a non-visual walk, so it is throttled (§10.5) and
    // runs BEFORE the walk that packs the frame: the marks it reads
    // moved by less than a pixel since the last frame, and the digit it
    // writes is a slow reading. A roster that gained or lost a mark is
    // counted at once, because a swap-remove moves a fleet into another
    // fleet's slot.
    this.stackAgeMS += deltaMS;
    if (this.stackAgeMS >= FLEET_STACK.everyMs || this.stackN !== this.n) {
      this.stackAgeMS = 0;
      this.countStacks();
    }
    const dt = Math.min(deltaMS, 100) / 1000;
    // The layer's own clock, for the beam's breath and the wave's period
    // (R725). The step is the clamped one, so a stalled tab does not throw a
    // ring past a hull without ever drawing it.
    this.timePrev = this.time;
    this.time += dt;
    // Exponential approach hides the 10 Hz update grid at any frame rate.
    const k = 1 - Math.exp(-deltaMS / 90);
    const { x0, y0, x1, y1 } = cull;
    const pc = this.packCull;
    pc.x0 = x0; pc.y0 = y0; pc.x1 = x1; pc.y1 = y1;
    for (let i = 0; i < this.n; i++) {
      const fading = (this.flags[i] & F_FADING) !== 0;
      const target = fading ? 0 : 1;
      this.alpha[i] += (target - this.alpha[i]) * Math.min(1, 3 * dt);
      if (target === 0 && this.alpha[i] < 0.02) {
        if (this.selectedId === this.id[i]) this.selectedId = 0;
        this.removeAt(i);
        i--; // the swap put a fleet that has not been advanced into this slot
        continue;
      }
      const dx = this.sx[i] - this.x[i], dy = this.sy[i] - this.y[i];
      this.x[i] += dx * k;
      this.y[i] += dy * k;
      // The bow turns with the same ease, the shortest way round (R2376).
      if (this.bowHeading) this.heading[i] = turnToward(this.heading[i], this.bow[i], k);
      // The rendered speed, low-passed (R101): a fast hull must leave a
      // longer scar than a slow one, not a noisier one.
      if (dt > 0) {
        const inst = Math.hypot(dx * k, dy * k) / dt;
        this.speed[i] += (inst - this.speed[i]) * Math.min(1, FLEET_SPEED_SMOOTH_PER_SEC * dt);
      }
      const own = (this.flags[i] & F_OWN) !== 0;
      const selected = this.id[i] === this.selectedId;
      if (selected) this.flags[i] |= F_SELECTED; else this.flags[i] &= ~F_SELECTED;
      // A hull another layer draws itself is hidden whatever the cull says
      // (R1007), because the mark must not come back when the camera moves.
      const hidden = (this.suppressed !== null && this.suppressed.has(this.id[i]))
        || (!own && !selected && outside(this.x[i], this.y[i], x0, y0, x1, y1));
      if (hidden) this.flags[i] |= F_HIDDEN; else this.flags[i] &= ~F_HIDDEN;
      // A hull whose MARK another layer draws keeps everything else (R1012).
      const markGone = this.hullSuppressed !== null
        && this.hullSuppressed.has(this.id[i]);
      if (markGone) this.flags[i] |= F_HULL_HIDDEN;
      else this.flags[i] &= ~F_HULL_HIDDEN;
      if (this.alpha[i] < FAINT_ALPHA) {
        this.wakeLen[i] = 0; // a faint contact shows no wake (§5)
      } else {
        this.wakeTimer[i] += dt;
        if (this.wakeTimer[i] > WAKE_INTERVAL_S && Math.hypot(dx, dy) > WAKE_MIN_STEP_PX) {
          this.wakeTimer[i] = 0;
          this.pushWake(i, this.x[i], this.y[i]);
        }
      }
      // A trail is walked only for a hull that is already off screen, so a
      // hull in the picture costs nothing here.
      if (hidden && this.trailOffScreen(i, x0, y0, x1, y1)) this.flags[i] |= F_WAKE_HIDDEN;
      else this.flags[i] &= ~F_WAKE_HIDDEN;
    }
    this.detectWaves();
    this.detectWards();
    this.stepGatherFigures(dt);
    this.pack();
  }

  /**
   * The gather figures of this frame (card 09, R1123, §6.1.4).
   *
   * The step is the CLAMPED one, so a tab that stalled for a minute does not
   * print a minute of figures when it wakes.
   *
   * The walk is over the own hulls alone: the hold of a faction hull is −1 on
   * the wire and a contact carries none at all (R1121). It runs before the
   * pack so a figure raised this frame draws on the frame it was raised.
   */
  private stepGatherFigures(dt: number): void {
    const state = this.gatherState;
    this.gatherFigures.begin(dt);
    if (state !== null) {
      for (let i = 0; i < this.n; i++) {
        if ((this.flags[i] & F_OWN) === 0) continue;
        const cut = state.get(this.id[i]);
        if (cut !== null) this.gatherFigures.note(cut);
      }
    }
    this.gatherFigures.end();
  }

  /** Names the map's held Coral Wards (R1082). The array is kept, never copied. */
  setWards(list: readonly WardAura[]): void {
    this.wards = list;
  }

  /**
   * Rises a shield plus on every own hull a regen ring passed this frame
   * (R725).
   *
   * The ring is the clock, so the plus keeps the wave's period and a reader
   * sees the ring reach the hull and the mark appear together. The walk is
   * over the Tenders and not over the hulls, because a map holds few
   * Tenders and the whole thing costs nothing when it holds none.
   */
  private detectWaves(): void {
    const r1 = this.tenderAuraPx;
    if (r1 <= 0) return;
    const wave = FLEET_TENDER.wave;
    for (let i = 0; i < this.n; i++) {
      if (!this.isSetTender(i)) continue;
      const id = this.id[i];
      const phase = wavePhase(id);
      const tx = this.x[i], ty = this.y[i];
      for (let k = 0; k < wave.rings; k++) {
        const uPrev = waveU(this.timePrev, k, wave.rings, wave.periodSec, phase);
        const uNow = waveU(this.time, k, wave.rings, wave.periodSec, phase);
        const rPrev = ANCHOR_R + (r1 - ANCHOR_R) * uPrev;
        const rNow = ANCHOR_R + (r1 - ANCHOR_R) * uNow;
        for (let j = 0; j < this.n; j++) {
          if ((this.flags[j] & F_OWN) === 0 || this.aura[j] !== id) continue;
          const d = Math.hypot(this.x[j] - tx, this.y[j] - ty);
          if (!ringPassed(rPrev, rNow, d)) continue;
          this.onHeal(this.id[j], this.x[j], this.y[j], 'shield', this.faction[j]);
        }
      }
    }
  }

  /**
   * Raises a shield plus on every own hull standing inside a held Coral
   * Ward (R1082).
   *
   * A Ward throws no wave, so it has no ring to act as the clock the
   * Tender's plus keeps (`detectWaves`). The cadence is the wave's own
   * period instead, and each hull takes the phase its own id gives it, so
   * a line of hulls inside one aura does not flash in step.
   *
   * The walk is over the WARDS and not over the hulls, because a map holds
   * one Ward and the whole pass costs nothing while nobody holds it.
   */
  private detectWards(): void {
    const r = this.wardAuraPx;
    if (r <= 0 || this.wards.length === 0) return;
    const periodSec = FLEET_TENDER.wave.periodSec;
    for (const w of this.wards) {
      // A public Ward of an arcade map mends every hull (R2029), so every
      // own hull in its aura takes the plus.
      if (w.faction === 0 && !w.public) continue;
      for (let i = 0; i < this.n; i++) {
        if ((this.flags[i] & F_OWN) === 0 || (!w.public && this.faction[i] !== w.faction)) continue;
        const d = Math.hypot(this.x[i] - w.x, this.y[i] - w.y);
        if (d > r) continue;
        const phase = wavePhase(this.id[i]);
        const before = Math.floor(this.timePrev / periodSec + phase);
        const now = Math.floor(this.time / periodSec + phase);
        if (before === now) continue;
        this.onHeal(this.id[i], this.x[i], this.y[i], 'shield', this.faction[i]);
      }
    }
  }

  /** A Tender whose class is on the frame and whose anchor is down (R725). */
  private isSetTender(i: number): boolean {
    return this.cls[i] === ShipClass.SHIP_CLASS_TENDER
      && this.anchor[i] === AnchorState.ANCHOR_STATE_SET;
  }

  private pushWake(i: number, x: number, y: number): void {
    const base = i * WAKE_POINTS * 2;
    const h = this.wakeHead[i];
    this.wake[base + h * 2] = x;
    this.wake[base + h * 2 + 1] = y;
    this.wakeHead[i] = (h + 1) % WAKE_POINTS;
    if (this.wakeLen[i] < WAKE_POINTS) this.wakeLen[i]++;
  }

  /** The `j`-th trail point of fleet `i`, oldest first, into `out`. */
  private wakeAt(i: number, j: number, out: { x: number; y: number }): void {
    const len = this.wakeLen[i];
    const start = (this.wakeHead[i] - len + WAKE_POINTS * 2) % WAKE_POINTS;
    const at = i * WAKE_POINTS * 2 + ((start + j) % WAKE_POINTS) * 2;
    out.x = this.wake[at];
    out.y = this.wake[at + 1];
  }

  private wp0 = { x: 0, y: 0 };
  private wp1 = { x: 0, y: 0 };

  /**
   * The one adapter a bar style writes through (R829, §10.4).
   *
   * A style measures from the hull's own origin, so the adapter carries
   * the hull it is placed at and adds it. It is built once and rewritten
   * per hull, because a frame must allocate nothing.
   */
  private barSink: RectSink & { q: SegmentSink | null; x: number; y: number; a: number } = {
    q: null, x: 0, y: 0, a: 1,
    rect: (left, top, w, h, color, alpha, dashOn = 0, dashOff = 0) => {
      const s = this.barSink;
      if (s.q) this.rect(s.q, s.x, s.y, left, top, w, h, color, alpha * s.a, dashOn, dashOff);
    },
  };

  /** What the readout of one hull says, rewritten per hull. */
  private barValues: BarValues = { hullPct: 0, shieldPct: 0, hullColor: 0, faction: 0 };

  /**
   * Is a point off the glass by more than `pad` (§10.7)? An own hull is never
   * culled by its own place, so a look that reaches `pad` past it asks here.
   */
  private offGlass(x: number, y: number, pad: number): boolean {
    const v = this.packCull;
    return outside(x, y, v.x0 - pad, v.y0 - pad, v.x1 + pad, v.y1 + pad);
  }

  private trailOffScreen(i: number, x0: number, y0: number, x1: number, y1: number): boolean {
    const len = this.wakeLen[i];
    for (let j = 0; j < len; j++) {
      this.wakeAt(i, j, this.wp0);
      if (!outside(this.wp0.x, this.wp0.y, x0, y0, x1, y1)) return false;
    }
    return true;
  }

  // -------------------------------------------------------------------------
  // Packing
  // -------------------------------------------------------------------------

  /**
   * The alpha of the readouts of own hull `i`: the bars, the cargo lane, the
   * captain's chevron and the laden ring (R3128). A lost hull fades out, and
   * its readouts fade with it. A hull that fades in keeps them at full
   * alpha, as before, so the steady look stays the same to the bit.
   */
  private readoutAlpha(i: number): number {
    return (this.flags[i] & F_FADING) !== 0 ? this.alpha[i] : 1;
  }

  /**
   * Whether this own hull shows its pair of bars (`fleets.ts:1075-1078`, R425).
   * A hull under a final-blow look (`F_HIDDEN`) shows none (R3128).
   */
  private ownBarsShown(i: number): boolean {
    return (this.flags[i] & (F_OWN | F_HIDDEN)) === F_OWN
      && (this.alwaysShowOwnHullBar || this.hullPct[i] < 100
        || this.shieldPct[i] < 100 || this.id[i] === this.selectedId);
  }

  /**
   * Whether this hull is a contact that carries a condition to draw (R425).
   * The FACTION decides who is a contact, never `own`; `hullPct > 0` is the
   * carrier test (R198); and a culled contact draws nothing (R192).
   *
   * `showAllBars` is the replay film's one exception (R531): a film has no
   * viewer in it, so every hull of the window carries its pair of bars.
   */
  private contactBarsShown(i: number): boolean {
    const contact = this.showAllBars
      || (this.viewerFaction !== 0 && this.faction[i] !== this.viewerFaction);
    return contact && this.hullPct[i] > 0 && (this.flags[i] & F_HIDDEN) === 0;
  }

  /**
   * One flat rectangle, drawn as a thick line with butt caps.
   *
   * A butt-capped segment of thickness `t` is an axis-aligned rectangle of
   * thickness `t`, and the segment shader ramps its edge over one device px
   * (`gfx/segments.ts`). The quad this replaced sampled a solid white texel
   * to the quad edge, so the edge was geometry and MSAA was the only thing
   * that made it smooth.
   *
   * The SHORT side becomes the width of the segment, so the hairline rule
   * holds the 1 px pieces of the contact frame at one device px and fades
   * them instead of letting them sparkle.
   */
  private rect(
    q: SegmentSink, x: number, y: number, left: number, top: number,
    w: number, h: number, color: number, alpha: number,
    dashOn = 0, dashOff = 0,
  ): void {
    // A lane at 0 % has no rectangle. The quad drew nothing at zero size;
    // a segment of zero length would still ramp a faint mark at its end.
    if (w <= 0 || h <= 0) return;
    if (w >= h) {
      const cy = y + top + h / 2;
      q.line(x + left, cy, x + left + w, cy, h, color, alpha, 0, dashOn, dashOff);
      return;
    }
    const cx = x + left + w / 2;
    q.line(cx, y + top, cx, y + top + h, w, color, alpha, 0, dashOn, dashOff);
  }

  /** Writes every instance of the frame, in the paint order of A.5 row 12. */
  private pack(): void {
    this.bloom = this.sprite('bloom');
    this.chevron = this.sprite('chevron');
    this.pip = this.sprite('pip');
    this.h = this.pivot.k === 1 ? this.raw : (this.scaled ??= pivotSinks(this.raw, this.pivot));
    for (let i = 0; i < this.sinks.length; i++) this.sinks[i].begin();
    this.packWakes();
    this.packAuras();
    this.packBeams();
    this.packBlooms();
    this.packOrders();
    this.packLaden();
    this.packPressure();
    this.packAnchors();
    this.packHoldFire();
    this.packReload();
    this.packReach();
    this.packDeadZone();
    this.packRearZone();
    if (this.labels) this.packBars();
    this.packChevrons();
    if (this.labels) {
      this.packBadges();
      this.packStacks();
    }
    this.packFleetMarks();
    this.packHulls();
    this.packBoarding();
    // The aspect chevrons paint over every hull and its charge pip, so a
    // neighbour hull never covers them and a hit flash always shows (R2417).
    this.packAspect();
    this.packBracket();
    // The gather figures ride over every mark of the layer, because a number
    // a hull covered states nothing (R1123).
    if (this.labels) this.packGatherFigures();
  }

  /** Eight trail segments a hull, fading toward the tail (`fleets.ts:955-971`). */
  private packWakes(): void {
    const s = this.wakeSegs;
    for (let i = 0; i < this.n; i++) {
      if ((this.flags[i] & F_WAKE_HIDDEN) !== 0) continue;
      const len = this.wakeLen[i];
      if (len < 2) continue;
      const color = this.inkAt(i);
      // CRASH DIVE dots the wake of an own hull past half its fade (R2408).
      const dots = this.diveShare(i) > 0.5;
      for (let j = 1; j < len; j++) {
        this.wakeAt(i, j - 1, this.wp0);
        this.wakeAt(i, j, this.wp1);
        if (dots) {
          s.line(this.wp0.x, this.wp0.y, this.wp1.x, this.wp1.y,
            WAKE_WIDTH * this.pivot.k, color, 0.05 + 0.28 * (j / WAKE_POINTS),
            F_ROUND, SIGNATURE.diveDotOnPx, SIGNATURE.diveDotOffPx);
          continue;
        }
        s.line(this.wp0.x, this.wp0.y, this.wp1.x, this.wp1.y,
          WAKE_WIDTH * this.pivot.k, color, 0.05 + 0.28 * (j / WAKE_POINTS));
      }
    }
  }

  /**
   * The Tender shield aura (R725, R831, §6.1): the mark a set Tender lays
   * over the water it covers.
   *
   * The world theme names which of the seven auras draws, and
   * `render/aura-styles.ts` holds every one of them, the AUTO regen wave
   * included. This method is the whole of the state: which Tenders throw
   * an aura, where the reach ends, which ink it wears, and what a style may
   * walk.
   *
   * The aura draws for **every** set Tender whose class is on the frame,
   * the way the anchor ring draws: a support class is public, so an enemy
   * berth reads as a berth. The gate is the window widened by the reach and
   * not the hull's own cull bit, because a Tender off the glass still
   * throws its aura into the picture.
   */
  private packAuras(): void {
    const R = auraR(this.tenderAuraPx);
    if (R <= 0) return;
    const id = this.look.aura;
    const style = AURA_STYLES[id];
    const wave = FLEET_TENDER.wave;
    const a = this.auraCtx;
    a.R = R;
    a.t = this.time;
    // One Map get a frame, and only for the style that draws the mark. The
    // pip is the one `pack` already fetched.
    if (id === 'hex-cells') {
      a.hex = this.sprite('hex');
      a.hexRim = this.sprite('hexrim');
    } else if (id === 'spokes') {
      a.pip = this.pip;
    }
    for (let i = 0; i < this.n; i++) {
      if (!this.isSetTender(i)) continue;
      if ((this.flags[i] & F_OWN) === 0 && this.alpha[i] < FAINT_ALPHA) continue;
      const x = this.x[i], y = this.y[i];
      if (this.offGlass(x, y, R)) continue;
      this.auraHost = i;
      a.x = x;
      a.y = y;
      a.id = this.id[i];
      a.phase = wavePhase(a.id);
      a.color = inkOf(wave.ink, this.faction[i], wave.verdictColor, factionColor);
      a.alpha = this.alpha[i];
      a.hidden = (this.flags[i] & F_HIDDEN) !== 0;
      style(a);
    }
  }

  /**
   * Every own hull of the host Tender's berth, the Tender itself left out
   * (`FleetState.aura`, R723).
   *
   * The membership is the wire's, not a distance: the server names the
   * berth, and the client measures nothing (§6.1). `k` counts the hulls in
   * the layer's own order, which is what staggers the spokes.
   */
  private eachAuraMember(
    fn: (hx: number, hy: number, k: number, hidden: boolean) => void,
  ): void {
    const i = this.auraHost;
    const id = this.id[i];
    let k = 0;
    for (let j = 0; j < this.n; j++) {
      if (j === i) continue;
      if ((this.flags[j] & F_AURA) === 0 || this.aura[j] !== id) continue;
      if ((this.flags[j] & F_OWN) === 0) continue;
      fn(this.x[j], this.y[j], k, (this.flags[j] & F_HIDDEN) !== 0);
      k++;
    }
  }

  /**
   * Every map cell whose centre lies inside the reach AND on the frame.
   *
   * `auraCells` answers the first half, which is geometry alone. The
   * second half is this layer's, because only it holds the window: a cell
   * outside the plain window is off the glass, even though the Tender that
   * covers it is inside the widened one.
   */
  private eachAuraCell(fn: (cx: number, cy: number, d: number) => void): void {
    const i = this.auraHost;
    const buf = this.auraCellBuf;
    const n = auraCells(this.x[i], this.y[i], this.auraCtx.R, buf);
    const c = this.packCull;
    for (let k = 0; k < n; k++) {
      const cx = buf[k * 3], cy = buf[k * 3 + 1];
      if (outside(cx, cy, c.x0, c.y0, c.x1, c.y1)) continue;
      fn(cx, cy, buf[k * 3 + 2]);
    }
  }

  /**
   * The heal beams (R725, R827, §6.1 and §6.1.2): the mark that runs from a
   * Tender to the hull it tends.
   *
   * The SHAPE is the look's, and `render/beam-styles.ts` holds all six of
   * them. This method owns what every value shares: which pairs draw, the
   * ink of the Tender, the fainter of the two ends, and the cull. A beam
   * whose two ends are both off the glass draws nothing; one end inside the
   * window still draws, because the line crosses it.
   */
  private packBeams(): void {
    const draw = BEAM_STYLES[this.look.beam];
    const beam = FLEET_TENDER.beam;
    const ctx = this.beamCtx;
    ctx.t = this.time;
    ctx.pip = this.pip;
    for (let i = 0; i < this.n; i++) {
      if (this.tending[i] === 0) continue;
      const patient = this.idToIndex.get(this.tending[i]);
      if (patient === undefined) continue;
      // A beam whose two ends are both out of the picture draws nothing; one
      // end inside it still draws, because the line crosses the window.
      if ((this.flags[i] & F_HIDDEN) !== 0 && (this.flags[patient] & F_HIDDEN) !== 0) continue;
      ctx.ax = this.x[i];
      ctx.ay = this.y[i];
      ctx.bx = this.x[patient];
      ctx.by = this.y[patient];
      ctx.ends = Math.min(this.alpha[i], this.alpha[patient]);
      ctx.color = inkOf(beam.ink, this.faction[i], beam.verdictColor, factionColor);
      draw(ctx);
    }
  }

  /**
   * The own-hull bloom (card A05), one sprite an own fleet.
   *
   * The bloom belongs to the `white-core` value alone (R830). Every own
   * ALTERNATE replaces the whole AUTO treatment — the white tint, the 1.05
   * scale and this bloom — with a mark beside a plain faction hull, because
   * a value must never stack on the drawing it replaces.
   */
  private packBlooms(): void {
    if (this.look.ownFleet !== 'white-core') return;
    const e = this.bloom;
    for (let i = 0; i < this.n; i++) {
      if ((this.flags[i] & F_OWN) === 0) continue;
      // The bloom belongs to the hull mark, so it goes where the mark goes:
      // with the fleet (`F_HIDDEN`) and with the mark alone (R1012).
      if ((this.flags[i] & (F_HIDDEN | F_HULL_HIDDEN)) !== 0) continue;
      this.pin(i);
      this.h.bloomQuads.sprite(this.x[i], this.y[i], this.heading[i], e,
        this.inkAt(i), this.alpha[i]);
    }
  }

  /**
   * The standing orders of every own fleet, always on screen (R43): a
   * half-transparent dashed line a mission, colour-coded, with the target
   * ringed. The lines go to the segment batch and the rings to the circle
   * batch, so a whole lane is ONE instance and a ring is one more.
   */
  private packOrders(): void {
    // The handover of §6.1: while the chart draws its lines of bearing the
    // layer packs neither a line nor a target ring, and the flag is spent
    // by this pack (R548). The legs of a course stay, and the piece draws
    // the line from the hull to the leg it sails now (R2704).
    if (this.ordersOff) { this.ordersOff = false; this.packCourses(false); return; }
    // A pledging move draws in the commit gold (R1208), so the commit
    // style is its own pass, after the plain moves.
    for (const style of ORDER_PASSES) {
      this.orderColor = style.color;
      this.orderAlpha = style.alpha;
      for (let i = 0; i < this.n; i++) {
        if ((this.flags[i] & (F_OWN | F_COURSE)) !== F_OWN) continue;
        const m = this.mission[i];
        if (orderStyleOf(m, this.pledgeSite[i] !== 0, this.commissioned.has(this.id[i])) !== style) continue;
        const x = this.x[i], y = this.y[i];
        const tx = this.tx[i], ty = this.ty[i];
        switch (m) {
          case Mission.MISSION_PATROL: {
            // A patrol that chases draws the line to its prey (R2754): the
            // course end is then the stand-off point of the chase and no
            // end of the lane.
            const p = this.preyOf(i);
            if (p !== undefined) {
              this.orderHull(x, y, p);
              this.orderRing(this.pxx[i], this.pyy[i], 9);
              break;
            }
            // The whole lane, both ends ringed — not just this leg.
            this.orderLeg(this.pxx[i], this.pyy[i], tx, ty, 4, 7);
            this.orderRing(tx, ty, 9);
            this.orderRing(this.pxx[i], this.pyy[i], 9);
            break;
          }
          case Mission.MISSION_GATHER:
            this.orderLeg(x, y, tx, ty);
            this.orderRing(tx, ty, 11);
            if (this.home) this.orderLeg(tx, ty, this.home.x, this.home.y, 3, 10);
            break;
          case Mission.MISSION_RETURN:
            if (this.home) {
              this.orderLeg(x, y, this.home.x, this.home.y);
              this.orderRing(this.home.x, this.home.y, 11);
            }
            break;
          case Mission.MISSION_HOME:
            if (this.home) {
              this.orderLeg(x, y, this.home.x, this.home.y);
              this.orderRing(this.home.x, this.home.y, 9);
            }
            break;
          case Mission.MISSION_ESCORT: {
            // A member of a formation draws its tie in `packFormation`.
            if (this.inFormation(i)) break;
            const t = this.idToIndex.get(this.escorting[i]);
            if (t !== undefined) this.orderHull(x, y, t);
            break;
          }
          case Mission.MISSION_ANCHOR:
            this.orderRing(x, y, 20);
            break;
          // A SEARCH rings its work point as a gather rings its field
          // (R2833): the fleet works there.
          case Mission.MISSION_SEARCH:
            this.orderLeg(x, y, tx, ty);
            this.orderRing(tx, ty, 11);
            break;
          default: {
            // A named hostile is a focus, so the line goes to that hull and
            // not to the point the order carries (R544). The prey of an
            // attack-move chase takes the same line (R2754): the course end
            // is the stand-off point of the intercept course, ahead of the
            // hull. It keeps the attack ink an attack-move already wears,
            // and it wears the escort's wider ring, because both mark a
            // hull and not a place.
            const f = this.preyOf(i);
            if (f !== undefined) {
              this.orderHull(x, y, f);
              break;
            }
            this.orderLeg(x, y, tx, ty);
            this.orderRing(tx, ty, 7);
          }
        }
      }
    }
    // The courses of v2 draw last, in their own two inks (R2589).
    this.packCourses(true);
    this.packFormation();
  }

  /** The slot of the hostile the own hull at `i` chases, its focus first,
   *  when the layer holds that hull; undefined otherwise (R544, R2754). */
  private preyOf(i: number): number | undefined {
    const id = this.focus[i] !== 0 ? this.focus[i] : this.chase[i];
    return id === 0 ? undefined : this.idToIndex.get(id);
  }

  /** The own hull keeps a station in a formation whose command ship the
   *  layer holds (R2617). */
  private inFormation(i: number): boolean {
    return (this.flags[i] & F_OWN) !== 0 && this.formationOf[i] !== 0
      && this.idToIndex.has(this.formationOf[i]);
  }

  /**
   * The formation of an own command ship (v2 §16.7, R2617), in the escort
   * ink and the order batches, so it costs no batch of its own. A member
   * is an escort of the command ship, and its tie is the escort leg to the
   * command ship. The command ship wears the escort's ring ONE time, where
   * every member of the escort order of before laid one more ring on the
   * same place. A member that stands off its station also wears the ring
   * of an order point at the station, which is the order point the wire
   * sends for it (`FleetState.tx`, the station that `keepStation` steers
   * to); on its station that ring would stand under the hull, so it goes.
   */
  private packFormation(): void {
    this.orderColor = OC.escort.color;
    this.orderAlpha = OC.escort.alpha;
    for (let i = 0; i < this.n; i++) {
      if ((this.flags[i] & (F_OWN | F_FORMATION)) === (F_OWN | F_FORMATION)) {
        this.orderRing(this.x[i], this.y[i], HULL_RING_R);
        continue;
      }
      if (!this.inFormation(i)) continue;
      const t = this.idToIndex.get(this.formationOf[i])!;
      const x = this.x[i], y = this.y[i];
      this.orderLeg(x, y, this.x[t], this.y[t], 3, 5);
      const sx = this.tx[i], sy = this.ty[i];
      if (Math.hypot(sx - x, sy - y) > POINT_RING_R) this.orderRing(sx, sy, POINT_RING_R);
    }
  }

  /**
   * The course of an own fleet (v2 §7, R2589): every leg from leg 1 on, a
   * ring at each leg, the leg back of each loop part to its opening point
   * with the pass count at its middle, and the line from the hull to the
   * leg it sails to now. The run from leg 0 (the order point) to leg 1 is
   * the joining run and no part of the plot (R2729): the line from the
   * hull draws it while the fleet sails it, and it draws no more after.
   * Only a course of a snapshot from before R2729 holds a loop part that
   * opens at leg 0, and its legs still draw. A leg of a loop part wears
   * the PATROL ink and every other leg the MOVE ink, in the order dashes of
   * §6.1. The wire list is held by reference, so the pack allocates
   * nothing; the count words are built once for each count.
   *
   * The course stands at all times (R2704): `hullLeg` is false while the
   * chart's lines of bearing hold the handover, and the piece then draws
   * the line from the hull. The fleet the plot line plots for draws no
   * course, because its draft stands in the plot layer.
   */
  private packCourses(hullLeg: boolean): void {
    for (let i = 0; i < this.n; i++) {
      if ((this.flags[i] & (F_OWN | F_COURSE)) !== (F_OWN | F_COURSE)) continue;
      if (this.id[i] === this.plotFleet) continue;
      const c = this.cold.get(this.id[i]);
      if (!c || c.legs.length === 0) continue;
      const legs = c.legs;
      const now = Math.min(Math.max(c.leg, 0), legs.length - 1);
      // The lane run of a gather run or a Return wears its mission ink
      // (R3221).
      const run = LANE_RUN_MISSIONS.has(this.mission[i])
        ? orderStyleOf(this.mission[i], false, this.commissioned.has(this.id[i]))
        : undefined;
      // The lit stretches ahead of the hull wear the band (R3075).
      const lit = this.courseLit?.(legs);
      if (lit) this.packLit(lit, now, this.x[i], this.y[i]);
      // The hull to the leg it sails to now.
      this.courseInk(legs[now].loop, run);
      if (hullLeg) this.orderLeg(this.x[i], this.y[i], mpxToPx(legs[now].xMpx), mpxToPx(legs[now].yMpx));
      let open = 0;
      let inPart = false;
      for (let k = 1; k < legs.length; k++) {
        const a = legs[k - 1], b = legs[k];
        const bx = mpxToPx(b.xMpx), by = mpxToPx(b.yMpx);
        if (b.loop && !inPart) { inPart = true; open = k - 1; }
        this.courseInk(b.loop, run);
        if (k > 1 || b.loop) this.orderLeg(mpxToPx(a.xMpx), mpxToPx(a.yMpx), bx, by);
        this.orderRing(bx, by, 7);
        const next = legs[k + 1];
        if (b.loop && (b.passes > 0 || !next || !next.loop)) {
          const o = legs[open];
          const ox = mpxToPx(o.xMpx), oy = mpxToPx(o.yMpx);
          this.orderLeg(bx, by, ox, oy, 3, 5);
          this.courseOpts.zoom = this.zoom;
          this.glyphs.run(this.h.markQuads, passWord(b.passes), (bx + ox) / 2, (by + oy) / 2,
            'mono', COURSE_WORD_PX, this.orderColor, ORDER_WORD_ALPHA, this.courseOpts);
          inPart = false;
        }
        if (!b.loop) inPart = false;
      }
    }
  }

  /**
   * The band of the faction ink under the lit stretches of a course ahead
   * of the hull (R3075): every run of a leg after the one it sails now,
   * and of the leg it sails now the part past the hull. `lit[k - 1]` holds
   * the runs of leg k.
   */
  private packLit(lit: readonly (readonly number[])[], now: number, hx: number, hy: number): void {
    const c = FLEET_COURSE;
    for (let k = Math.max(now, 1); k <= lit.length; k++) {
      const r = lit[k - 1];
      for (let j = 0; j + 3 < r.length; j += 4) {
        let x0 = r[j], y0 = r[j + 1];
        const x1 = r[j + 2], y1 = r[j + 3];
        if (k === now) {
          // Clip the run to the part past the hull's foot on it.
          const dx = x1 - x0, dy = y1 - y0, l2 = dx * dx + dy * dy;
          const u = l2 > 0 ? ((hx - x0) * dx + (hy - y0) * dy) / l2 : 0;
          if (u >= 1) continue;
          if (u > 0) { x0 += dx * u; y0 += dy * u; }
        }
        this.orderSegs.line(x0, y0, x1, y1, c.litWidth, this.courseLitInk, c.litAlpha, F_SCREEN);
      }
    }
  }

  /** The ink of a leg of a course: PATROL in a loop part, MOVE elsewhere,
   *  and the mission ink of a lane run (R3221). */
  private courseInk(loop: boolean, run?: OrderStyle): void {
    const ink = run ?? (loop ? OC.patrol : OC.move);
    this.orderColor = ink.color;
    this.orderAlpha = ink.alpha;
  }

  /** The ink of the order style `packOrders` is on. */
  private orderColor = 0;
  private orderAlpha = 0;
  /** The run options of a pass count, rewritten per count (§10.4). */
  private readonly courseOpts: RunOpts = { anchor: 'centre', screen: true, zoom: 1 };

  /** One dashed leg of an order. A leg under one px draws nothing. */
  private orderLeg(x0: number, y0: number, x1: number, y1: number, on = 6, off = 6): void {
    if (Math.hypot(x1 - x0, y1 - y0) < 1) return;
    this.orderSegs.line(x0, y0, x1, y1, ORDER_WIDTH,
      this.orderColor, this.orderAlpha, 0, on, off);
  }

  /** The leg from `(x, y)` to the hull at slot `t` and the hull's ring:
   *  an order that names a hull and not a place (R544). */
  private orderHull(x: number, y: number, t: number): void {
    this.orderLeg(x, y, this.x[t], this.y[t], 3, 5);
    this.orderRing(this.x[t], this.y[t], HULL_RING_R);
  }

  /** The dashed ring round the target of an order. */
  private orderRing(x: number, y: number, r: number): void {
    this.rings.dashed(x, y, r, ORDER_WIDTH, ORDER_DASHES, ORDER_DASH_FILL,
      this.orderColor, this.orderAlpha);
  }

  /**
   * A laden hauler wears a ring in the cargo brown: it carries something
   * that only counts if it gets home (game-design §6).
   */
  private packLaden(): void {
    const off = this.ladenSuppressed;
    for (let i = 0; i < this.n; i++) {
      // A hull under a final-blow look shows no ring (R3128).
      if ((this.flags[i] & (F_OWN | F_HIDDEN)) !== F_OWN || this.hold[i] <= 0) continue;
      // A harvesting look that gauges the hold draws this ring itself
      // (R1120).
      if (off !== null && off.has(this.id[i])) continue;
      this.pin(i);
      // The ring fades with the hull (R3128).
      this.h.rings.ring(this.x[i], this.y[i], LADEN_R, LADEN_WIDTH,
        FLEET_CARGO_COLOR, LADEN_ALPHA * this.readoutAlpha(i));
    }
  }

  /**
   * The tick arc of the selected own hull below its depth rating (card 04
   * of mockup round 088, R3129, §6.1.16): a short arc in the bad ink at the
   * foot of the hull, under the selection bracket, with ticks out of it.
   * Only the selected hull wears it, as the card draws it. It is an own
   * readout, so it fades with a lost hull and stands down under a
   * final-blow look (R3128).
   */
  private packPressure(): void {
    const a = FLEET_PRESSURE_ARC;
    const want = F_OWN | F_PRESSURE | F_SELECTED;
    const r0 = a.r, r1 = a.r + a.tickPx;
    for (let i = 0; i < this.n; i++) {
      if ((this.flags[i] & (want | F_HIDDEN)) !== want) continue;
      const x = this.x[i], y = this.y[i];
      const alpha = a.alpha * this.readoutAlpha(i);
      this.pin(i);
      this.h.rings.arc(x, y, r0, a.width, PRESSURE_FROM, PRESSURE_SWEEP, a.color, alpha);
      for (let t = 0; t < PRESSURE_TICKS.length; t += 2) {
        const c = PRESSURE_TICKS[t], sn = PRESSURE_TICKS[t + 1];
        this.h.bracketSegs.line(x + c * r0, y + sn * r0, x + c * r1, y + sn * r1,
          a.tickWidth, a.color, alpha, F_ROUND);
      }
    }
  }

  /**
   * The anchoring look of every anchor hull on screen (numbers.md §16, R114,
   * R1125, §6.1.5).
   *
   * The World theme field `anchoring` names the look, and
   * `render/anchor-styles.ts` answers it; `anchor-ring` is the r19 ring of
   * R114, figure for figure. This method is the whole of the state: which
   * hulls draw, the share of cable out, the beam order, the time a look
   * prints, the ink, and when the state began. Your own cables are gold;
   * everyone else's wear the faction ink at the fainter alpha, so an enemy
   * cable reads as an enemy's. A contact still fading in shows none. The
   * context carries no hull class, so a look is class-blind.
   */
  private packAnchors(): void {
    const look = anchorLook(this.anchorForced !== '' ? this.anchorForced : this.look.anchoring);
    const c = this.anchorCtx;
    c.row = look.row;
    c.t = this.time;
    c.z = this.zoom;
    c.quads = this.h.anchorQuads;
    c.segs = this.h.anchorSegs;
    c.rings = this.h.rings;
    const pad = ANCHOR_LIMITS.cullPadPx;
    for (let i = 0; i < this.n; i++) {
      const st = this.anchor[i];
      if (st === AnchorState.ANCHOR_STATE_UNSPECIFIED) continue;
      const own = (this.flags[i] & F_OWN) !== 0;
      if (!own && this.alpha[i] < FAINT_ALPHA) continue;
      if ((this.flags[i] & F_HIDDEN) !== 0) continue;
      const x = this.x[i], y = this.y[i];
      // An own hull is never culled by its own place (§10.7), so its look
      // is culled here, off the glass by the widest look's reach.
      if (own && this.offGlass(x, y, pad)) continue;
      const set = st === AnchorState.ANCHOR_STATE_SET;
      c.phase = set ? 'set' : st === AnchorState.ANCHOR_STATE_SETTING ? 'setting' : 'weighing';
      c.p = anchorFrac(st, this.anchorLeftSec[i], this.anchorCycleSec);
      // The beam order comes off the wire, never off the class (R2858): a
      // SET Beacon with its beam on carries the time left, and a tower or
      // an ended beam carries none.
      const beam = set ? this.beamLeftSec[i] : 0;
      c.beam = beam > 0 && this.beaconBeamSec > 0
        ? Math.min(1, beam / this.beaconBeamSec) : -1;
      c.leftSec = this.worldSecOf(set ? beam : this.anchorLeftSec[i]);
      c.since = this.time - this.anchorSince[i];
      c.own = own;
      c.ink = own ? FLEET_ANCHOR_GOLD : this.inkAt(i);
      c.a = anchorAlpha(own);
      c.seed = this.id[i];
      c.x = x;
      c.y = y;
      c.head = this.heading[i];
      this.pin(i);
      look.recipe(c);
    }
  }

  /**
   * The hold-fire look of every own hull on screen that holds fire (R1437,
   * R1439, §6.1.7).
   *
   * The World theme field `holdFire` names the look, and
   * `render/holdfire-styles.ts` answers it. The flag rides to the owner
   * alone, and the gate reads `F_OWN` too, so a look never draws on a
   * contact. Every hull reads the one layer clock, so hulls that hold fire
   * move in step. The look draws at every zoom where the hull draws.
   */
  private packHoldFire(): void {
    const look = holdFireLook(this.holdForced !== '' ? this.holdForced : this.look.holdFire);
    const c = this.holdCtx;
    c.row = look.row;
    c.t = this.time;
    c.z = this.zoom;
    c.segs = this.h.holdSegs;
    c.rings = this.h.holdRings;
    const pad = HOLD_FIRE_LIMITS.cullPadPx;
    for (let i = 0; i < this.n; i++) {
      const f = this.flags[i];
      if ((f & F_HOLD_FIRE) === 0 || (f & F_OWN) === 0 || (f & F_HIDDEN) !== 0) continue;
      const x = this.x[i], y = this.y[i];
      // An own hull is never culled by its own place (§10.7), so its look
      // is culled here, off the glass by the widest look's reach.
      if (this.offGlass(x, y, pad)) continue;
      c.x = x;
      c.y = y;
      c.selected = this.id[i] === this.selectedId;
      this.pin(i);
      look.recipe(c);
    }
  }

  /**
   * The tube reload look of every own hull on screen with tubes (R1474,
   * §6.3.4).
   *
   * The World theme field `reload` names the look, and
   * `render/reload-styles.ts` answers it. The wire sends `reload_sec`,
   * `torpedoes` and `reload_full_sec` to the owner alone, and the gate
   * reads `F_OWN` too, so a look never draws on a contact; a hull whose
   * whole reload is 0 has no tubes and draws nothing. The layer runs the
   * reload down on its own clock between two frames at the map's pace, and
   * it notes the ready moment of every such hull, on screen or not, so a
   * hull that scrolls into view flashes no stale ready.
   */
  private packReload(): void {
    const look = reloadLook(this.reloadForced !== '' ? this.reloadForced : this.look.reload);
    const c = this.reloadCtx;
    c.row = look.row;
    c.z = this.zoom;
    const h = this.h;
    if (look.row.under) {
      c.ground = h.reloadUnderRings; c.rings = h.reloadUnderRings; c.segs = h.reloadUnderSegs;
    } else {
      c.ground = h.reloadGround; c.rings = h.reloadRings; c.segs = h.reloadSegs;
    }
    const rate = this.mapSecPerWallSec();
    const pad = RELOAD_LIMITS.cullPadPx;
    for (let i = 0; i < this.n; i++) {
      const f = this.flags[i];
      const full = this.reloadFull[i];
      if ((f & F_OWN) === 0 || full <= 0) continue;
      const count = this.torpedoes[i];
      const left = Math.max(0, this.reloadSec[i] - (this.time - this.reloadAt[i]) * rate);
      const reloading = count > 0 && left > 0;
      if (this.wasReloading[i] === 1 && !reloading && count > 0) this.readyAt[i] = this.time;
      this.wasReloading[i] = reloading ? 1 : 0;
      if (!this.reloadOn || (f & F_HIDDEN) !== 0) continue;
      const x = this.x[i], y = this.y[i];
      // An own hull is never culled by its own place (§10.7), so its look
      // is culled here, off the glass by the widest look's reach.
      if (this.offGlass(x, y, pad)) continue;
      const round = this.roundAt(i);
      if (round.mag <= 0) continue;
      c.x = x;
      c.y = y;
      c.head = this.heading[i];
      c.count = Math.min(count, round.mag);
      c.mag = round.mag;
      c.empty = count <= 0;
      c.reloading = reloading;
      c.ready = !c.empty && !reloading;
      c.p = reloading ? clamp01(1 - left / full) : 1;
      c.leftSec = Math.ceil(this.worldSecOf(left));
      c.shape = shapeOf(round.kind);
      this.fillShapes(i, c);
      c.name = round.name;
      c.letter = round.name.charAt(0);
      c.firedAgo = this.time - this.firedAt[i];
      c.readyAgo = this.time - this.readyAt[i];
      this.pin(i);
      look.recipe(c);
    }
  }

  /**
   * The shape of each round aboard, in the order the hull fires them
   * (R2031, §6.3.4): the firing kind first, then the next kinds of the
   * fixed order, which wraps. It rewrites the context's one list in place
   * and allocates nothing (R131). A kind outside the order (an old
   * snapshot) is left out; its pip takes the firing kind's shape.
   */
  private fillShapes(i: number, c: ReloadCtx): void {
    const out = c.shapes;
    out.length = 0;
    const load = this.cold.get(this.id[i])?.rounds ?? [];
    const order = this.roundOrder;
    const at = order.indexOf(this.firingKind[i]);
    for (let j = 0; j < order.length; j++) {
      const k = order[at < 0 ? j : (at + j) % order.length];
      const n = countOf(load, k);
      if (n === 0) continue;
      const shape = shapeOf(k);
      for (let m = 0; m < n && out.length < c.mag; m++) out.push(shape);
    }
  }

  /**
   * One run of an anchoring look's numerals (R1125): the damage figure's own
   * glyph cells, left aligned at a screen offset, the way `putFigureText`
   * sets a gather figure. The face is monospaced, so the layer measures
   * nothing.
   */
  private putAnchorText(
    text: string, x: number, y: number, dx: number, dy: number,
    px: number, ink: number, alpha: number,
  ): void {
    const adv = damageAdvancePx(px);
    const scale = px / DAMAGE_FIGURE.sizePx;
    const z = this.zoom;
    for (let i = 0; i < text.length; i++) {
      // A space between a span's two units is an advance and no cell (R2741).
      if (text[i] === ' ') continue;
      this.h.markQuads.sprite(
        x + (dx + adv * (i + 0.5)) / z, y + dy / z, 0,
        this.sprite(anchorGlyphKey(text[i])), ink, alpha, scale, F_SCREEN,
      );
    }
  }

  /**
   * The reach circles (R386, §6.1 and §6.3): the armed launcher's aim ring
   * with its half-range mark, and the gun reach of the selected own fleet.
   * The launcher is an aiming fleet, or it is the home base.
   */
  private packReach(): void {
    const s = FLEET_REACH_RING;
    const aim = this.idToIndex.get(this.aimingId);
    if (aim !== undefined && this.torpedoRange[aim] > 0) {
      this.packAimRing(this.x[aim], this.y[aim], this.torpedoRange[aim]);
    } else if (this.home) {
      const reach = this.baseAimRange();
      if (reach > 0) this.packAimRing(this.home.x, this.home.y, reach);
    }
    const sel = this.idToIndex.get(this.selectedId);
    const selOwn = sel !== undefined && (this.flags[sel] & F_OWN) !== 0;
    this.packHoverReach(selOwn ? sel : undefined);
    if (sel === undefined || !selOwn) return;
    const gun = this.gunRangeOf(this.cls[sel], this.anchor[sel], this.orderActive(sel));
    // The gun ring wears the attack red, not the hostile red of the aim
    // rings: it is the reach of the one order that promises damage (R533).
    if (gun > 0) this.rings.ring(this.x[sel], this.y[sel], gun, s.gunWidth, s.gunColor, s.gunAlpha);
    // The collect ring of a Sapper (R2711): the reach of COLLECT, drawn as
    // the gun ring is, in the neutral white, because a collect harms
    // nothing. A Sapper carries no gun, so the two rings never meet.
    if (this.cls[sel] === ShipClass.SHIP_CLASS_SAPPER && this.collectPx > 0) {
      this.rings.ring(this.x[sel], this.y[sel], this.collectPx,
        SEA_OBJECTS.collectRingWidth, MARKER_NEUTRAL, SEA_OBJECTS.collectRingAlpha);
    }
    // The heal beam ring of a Tender (R2738): the reach inside which its
    // beam finds a hull to mend, drawn as the gun ring is, in the green
    // the heal beam wears as a verdict. A Tender carries no gun.
    if (this.cls[sel] === ShipClass.SHIP_CLASS_TENDER && this.beamPx > 0) {
      this.rings.ring(this.x[sel], this.y[sel], this.beamPx, s.gunWidth, s.beamColor, s.gunAlpha);
    }
  }

  /**
   * The primary reach of the hull under the pointer (R2738, §6.1): the gun
   * reach of a warship or a Sentinel, the heal beam reach of a Tender and
   * the collect reach of a Sapper, dashed as the hovered sonar ring is.
   * A hull whose class the wire withholds draws none, so a hover tells
   * the viewer nothing that the server did not send. The selected own
   * hull already wears its solid ring, so a hover over it adds nothing.
   */
  private packHoverReach(selOwn: number | undefined): void {
    const i = this.idToIndex.get(this.hoverId);
    if (i === undefined || i === selOwn) return;
    if ((this.flags[i] & (F_FADING | F_CLASSIFIED)) !== 0 || !KNOWN_CLASSES.has(this.cls[i])) return;
    const kind = primaryReachOf(this.cls[i]);
    if (kind === 'none') return;
    const gun = kind === 'gun' ? this.gunRangeOf(this.cls[i], this.anchor[i], this.orderActive(i)) : 0;
    const r = primaryReachPx(kind, gun, this.beamPx, this.collectPx);
    if (r <= 0) return;
    const s = FLEET_REACH_RING;
    this.rings.dashed(this.x[i], this.y[i], r, s.hoverWidth, HOVER_DASHES, HOVER_DASH_FILL,
      reachInk(kind), s.hoverAlpha);
  }

  /**
   * The dead zone of the selected own hull (R1472, §6.1): a disc of
   * radius `min_range`, hatched at 45 degrees, in the attack red of the
   * gun ring. Each hatch line is the chord of the disc at its offset, so
   * no line ends outside the rim. The `deadzone` pass draws it.
   */
  private packDeadZone(): void {
    const sel = this.idToIndex.get(this.selectedId);
    if (sel === undefined || (this.flags[sel] & F_OWN) === 0) return;
    const r = this.deadZoneOf(this.cls[sel]);
    if (r <= 0) return;
    const s = FLEET_REACH_RING;
    const x = this.x[sel], y = this.y[sel];
    this.deadRings.ring(x, y, r, s.deadWidth, s.gunColor, s.deadAlpha);
    if (this.packIris(sel, x, y, r)) return;
    // The line runs along (1, -1) / sqrt 2 and steps along its normal
    // (1, 1) / sqrt 2, so the hatch leans the way "/" leans.
    const step = s.deadHatchStep;
    const n = Math.floor(r / step);
    for (let k = -n; k <= n; k++) {
      const t = k * step;
      const h = Math.sqrt(r * r - t * t);
      if (h <= 0) continue;
      const cx = x + t * Math.SQRT1_2, cy = y + t * Math.SQRT1_2;
      const dx = h * Math.SQRT1_2, dy = -h * Math.SQRT1_2;
      this.deadSegs.line(cx - dx, cy - dy, cx + dx, cy + dy,
        s.deadHatchWidth, s.gunColor, s.deadHatchAlpha);
    }
  }

  /**
   * The rear dead zone of the selected own hull (R2391, §6.1): the part of
   * the gun-ring disc outside the gun arc, hatched as the artillery disc
   * is. The bow is the drawn heading, so the hatch turns with the hull.
   * The artillery disc keeps its own hatch, so the rear hatch leaves out
   * the disc of `min_range` and no part is drawn twice.
   */
  private packRearZone(): void {
    if (!this.bowHeading) return;
    const sel = this.idToIndex.get(this.selectedId);
    if (sel === undefined || (this.flags[sel] & F_OWN) === 0) return;
    const active = this.orderActive(sel);
    const arc = this.gunArcOf(this.cls[sel], this.anchor[sel], active,
      this.signature?.digClose(this.id[sel]) ?? -1);
    if (arc <= 0 || arc >= 360) return;
    const r = this.gunRangeOf(this.cls[sel], this.anchor[sel], active);
    if (r <= 0) return;
    const rMin = Math.min(this.deadZoneOf(this.cls[sel]), r);
    const s = FLEET_REACH_RING;
    const x = this.x[sel], y = this.y[sel];
    const e = rearEdges(this.heading[sel], arc, this.rearEdge);
    this.deadRings.arc(x, y, r, s.deadWidth, e.start, e.sweep, s.gunColor, s.deadAlpha);
    this.rearRadius(x, y, e.left, rMin, r);
    this.rearRadius(x, y, e.right, rMin, r);
    rearHatch(x, y, r, rMin, this.heading[sel], arc, s.deadHatchStep, this.rearLine);
  }

  /** The signature order of hull `i` holds now (`signature_active`, R2376). */
  private orderActive(i: number): boolean {
    return this.cold.get(this.id[i])?.approach?.active ?? false;
  }

  /** One edge of the rear zone: the radius at angle `a` from `rMin` to `r`. */
  private rearRadius(x: number, y: number, a: number, rMin: number, r: number): void {
    const s = FLEET_REACH_RING;
    const ux = Math.cos(a), uy = Math.sin(a);
    this.deadSegs.line(x + ux * rMin, y + uy * rMin, x + ux * r, y + uy * r,
      s.deadWidth, s.gunColor, s.deadAlpha);
  }

  /** One rear hatch line into the dead zone batch, bound once (§10.4). */
  private readonly rearLine = (x0: number, y0: number, x1: number, y1: number): void => {
    const s = FLEET_REACH_RING;
    this.deadSegs.line(x0, y0, x1, y1, s.deadHatchWidth, s.gunColor, s.deadHatchAlpha);
  };

  private readonly rearEdge: RearEdges = { left: 0, right: 0, start: 0, sweep: 0 };

  /** The aim ring of an armed launcher, with its half-range mark. */
  private packAimRing(x: number, y: number, reach: number): void {
    const s = FLEET_REACH_RING;
    this.rings.ring(x, y, reach, s.aimWidth, s.color, s.aimAlpha);
    this.rings.ring(x, y, reach * 0.5, s.aimHalfWidth, s.color, s.aimHalfAlpha);
  }

  /**
   * The readouts (mockup round twenty-one, R425, R829, §6.1).
   *
   * Your own hull carries a stack over it: the cargo lane, the shield lane
   * and the hull lane. A contact carries a readout of its own, under the
   * hull or over it, so a screen of hulls never puts two readouts of
   * different meanings on one line.
   *
   * The `shield` field of the Marks theme picks the drawing.
   * `BAR_STYLES` of `render/bar-styles.ts` holds one entry per value and
   * `FLEET_BARS` holds every figure they share. The style writes through
   * ONE reusable adapter over the segment batch, so a frame allocates
   * nothing here (§10.4). The colour grouping the PixiJS layer needed is
   * gone with it: the batch is one instanced draw whose colour rides each
   * instance, so a hull writes its whole readout in one place.
   */
  private packBars(): void {
    const q = this.h.barSegs;
    const f = FLEET_BARS;
    const style = BAR_STYLES[this.look.shield];
    const sink = this.barSink;
    const v = this.barValues;
    sink.q = q;
    for (let i = 0; i < this.n; i++) {
      if (!this.ownBarsShown(i)) continue;
      this.pin(i);
      sink.x = this.x[i];
      sink.y = this.y[i];
      // The readout of an own hull fades with the hull (R3128).
      sink.a = this.readoutAlpha(i);
      v.hullPct = this.hullPct[i];
      v.shieldPct = this.shieldPct[i];
      v.hullColor = HULL_BAND_COLOR[hullBand(this.hullPct[i])];
      style.own(sink, v, f);
    }
    // The cargo lane is the one lane every style shares. Only its offset
    // is the style's, because a style that lifts the shield lane must
    // lift the cargo lane clear of it.
    this.packCargoLanes(q, f, style.cargoY(f));
    // A contact's hull lane takes the FLAT faction colour and never the
    // traffic light: that light is the viewer's own alarm.
    sink.a = 1;
    for (let i = 0; i < this.n; i++) {
      if (!this.contactBarsShown(i)) continue;
      this.pin(i);
      sink.x = this.x[i];
      sink.y = this.y[i];
      v.hullPct = this.hullPct[i];
      v.shieldPct = this.shieldPct[i];
      v.faction = this.inkAt(i);
      style.contact(sink, v, f);
    }
  }

  /**
   * THE LIVE CARGO LANE (card 14 of mockup round 055, R1122, §6.1.1).
   *
   * The cargo lane of an OWN hull is the one lane that comes alive. It is not
   * a gather recipe: the owner made it on for every player under every gather
   * look, so no look may take it away and it reads the tracker rather than
   * the theme.
   *
   * At rest it is the lane of today — one rectangle in the cargo brown, and
   * nothing at all while the hold is empty. While the cut runs it grows by
   * `liftPx`, a ground fades in under it, diagonal dashes march through the
   * fill toward its end, and a bright leading edge stands at the end of the
   * fill. `GatherCut.work` is the envelope, so every piece fades in and out
   * with the cut rather than snapping on.
   *
   * The SHAFT (R715) says itself apart by SHAPE: its dashes stand
   * `shaftGapK` times as far apart, its march is that much slower because it
   * also carries the cut rate, and its leading edge is a hollow box AHEAD of
   * the fill rather than a bright bar at the end of it.
   *
   * Every piece is an instance of the readout batch, so the lane stays ONE
   * instanced draw and the frame allocates nothing (§10.4).
   */
  private packCargoLanes(q: SegmentSink, f: FleetBars, cargoY: number): void {
    const L = GATHER_LIVE_LANE;
    // The dash ink is one colour for the whole map, so it is mixed once.
    const dashInk = lighten(f.cargoColor, L.dashLighten);
    for (let i = 0; i < this.n; i++) {
      // A hull under a final-blow look shows no lane (R3128).
      if ((this.flags[i] & (F_OWN | F_HIDDEN)) !== F_OWN || this.cargoCap[i] === 0) continue;
      const t = this.hold[i];
      const cut = this.gatherState?.get(this.id[i]) ?? null;
      // The rig is the envelope of the cut, and it is 0 on a hull under way:
      // a Dredger that carries its load home draws the lane of today.
      const rig = cut !== null && cut.atRest ? cut.work : 0;
      const full = cut !== null ? cut.full : 0;
      if (t === 0 && rig <= 0 && full <= 0) continue;
      const lift = L.liftPx * rig;
      const top = cargoY - lift / 2;
      const h = f.h + lift;
      const w = f.w * Math.min(1, t / this.cargoCap[i]);
      const x = this.x[i], y = this.y[i];
      // The lane fades with the hull (R3128).
      const a = this.readoutAlpha(i);
      this.pin(i);
      // The ground. The resting lane has none, so it arrives with the work.
      if (rig > 0) {
        this.rect(q, x, y, f.x, top, f.w, h, f.ground, f.groundAlpha * rig * a);
      }
      this.rect(q, x, y, f.x, top, w, h, f.cargoColor, f.cargoAlpha * a);
      if (rig > 0 && cut !== null) {
        this.packLaneDashes(q, x, y, f, cut, top, h, w, dashInk, rig * a);
        this.packLaneEdge(q, x, y, f, cut, top, h, w, rig, a);
      }
      // The full-hold beat: the whole lane goes bright one time.
      if (full > 0) {
        const o = L.flashOverPx;
        this.rect(q, x, y, f.x - o, top - o, f.w + 2 * o, h + 2 * o,
          L.bright, L.flashAlpha * full * a);
      }
    }
  }

  /**
   * The march: light diagonal dashes that walk through the FILL toward its
   * end (card 14).
   *
   * The page clips them with a clip path. A batch has none, so each diagonal
   * is cut against the four sides of the fill rectangle by arithmetic
   * (`clipSegment`), which is exact and costs no state change.
   */
  private packLaneDashes(
    q: SegmentSink, x: number, y: number, f: FleetBars, cut: GatherCut,
    top: number, h: number, w: number, ink: number, rig: number,
  ): void {
    if (w <= 0) return;
    const L = GATHER_LIVE_LANE;
    const per = L.dashGapPx * (cut.shaft ? L.shaftGapK : 1);
    if (per <= 0) return;
    // The march carries the cut RATE as well as the gap, so a shaft walks at
    // a quarter of the pace over twice the gap.
    const off = ((cut.tw * cut.rate * L.dashSpeedPxPerSec) % per + per) % per;
    // The diagonal leans one lane height plus a px, the way the card cuts it.
    const lean = h + 1;
    const alpha = L.dashAlpha * rig;
    const c = this.laneClip;
    c.left = f.x; c.right = f.x + w; c.top = top; c.bottom = top + h;
    for (let sx = f.x - per + off; sx < f.x + w; sx += per) {
      if (!clipSegment(c, sx, top + h + 0.5, sx + lean, top - 0.5)) continue;
      q.line(x + c.x0, y + c.y0, x + c.x1, y + c.y1,
        L.dashWidthPx, ink, alpha, 0);
    }
  }

  /**
   * The leading edge of the live lane (card 14).
   *
   * A GATHER puts a bright bar at the end of the FILL, one `edgeOverPx`
   * past each end of the lane, and its ink breathes once a lot. A SHAFT puts
   * a hollow box AHEAD of the fill instead, held inside the lane, so the two
   * read apart by shape and never by colour alone (R715).
   */
  private packLaneEdge(
    q: SegmentSink, x: number, y: number, f: FleetBars, cut: GatherCut,
    top: number, h: number, w: number, rig: number, a: number,
  ): void {
    const L = GATHER_LIVE_LANE;
    const p = (L.edgeAlpha + L.edgePulse * (1 - cut.lot)) * rig * a;
    if (!cut.shaft) {
      const xe = Math.max(f.x + L.edgeMinPx, f.x + w);
      this.rect(q, x, y, xe - L.edgeWidthPx, top - L.edgeOverPx,
        L.edgeWidthPx, h + 2 * L.edgeOverPx, L.bright, p);
      return;
    }
    const bw = L.shaftBoxWPx;
    const bx = f.x + Math.min(w, f.w - bw);
    const btop = top - L.shaftBoxOverPx;
    const bh = h + 2 * L.shaftBoxOverPx;
    this.rect(q, x, y, bx, btop, bw, bh, f.ground, rig * a);
    // The frame stands inside the box, from the inset to its right end.
    const s = L.shaftBoxStrokePx;
    const fx = bx + L.shaftBoxInsetPx;
    const fw = bw - L.shaftBoxInsetPx;
    this.rect(q, x, y, fx, btop, fw, s, L.bright, p);
    this.rect(q, x, y, fx, btop + bh - s, fw, s, L.bright, p);
    this.rect(q, x, y, fx, btop + s, s, bh - 2 * s, L.bright, p);
    this.rect(q, x, y, fx + fw - s, btop + s, s, bh - 2 * s, L.bright, p);
  }

  /** The one box a dash is cut against, rewritten per hull (R131). */
  private readonly laneClip: ClipBox = {
    left: 0, right: 0, top: 0, bottom: 0, x0: 0, y0: 0, x1: 0, y1: 0,
  };

  /**
   * The gather figures of the own hulls (card 09, R1123).
   *
   * The accumulator is the layer's own, because the figures are no look: a
   * player who picked another gather look still reads what came aboard.
   */
  private readonly gatherFigures = new GatherFigures();

  /** Takes every gather figure off the water and forgets every hold (a
   *  hello, a season roll). The hold of the world that is going away says
   *  nothing about the hold of the one that arrives. */
  clearGatherFigures(): void { this.gatherFigures.clear(); }

  /** How many gather figures stand. A test reads it. */
  get gatherFigureCount(): number { return this.gatherFigures.count; }

  /** The camera scale of this frame, for the SCREEN-px figures. */
  private zoom = 1;

  /** The captain's chevron: a gold mark above the hull (`fleets.ts:1224-1230`). */
  private packChevrons(): void {
    const e = this.chevron;
    for (let i = 0; i < this.n; i++) {
      // A hull under a final-blow look shows no chevron (R3128).
      if ((this.flags[i] & (F_OWN | F_HIDDEN)) !== F_OWN || this.captainId[i] === 0) continue;
      // The mark is baked at its own place above the origin, so the entry's
      // offset carries the placement and the quad is origin-relative. It
      // fades with the hull (R3128).
      this.pin(i);
      this.h.markQuads.sprite(this.x[i], this.y[i], 0, e, FLEET_ANCHOR_GOLD,
        CHEVRON_ALPHA * this.readoutAlpha(i), 1, 0, false);
    }
  }

  /**
   * One digit at the bottom-left corner of every own hull that holds a
   * control group (R403, R427). It stands OUTSIDE the selection bracket,
   * carries no rotation, and rides on every assigned hull rather than on the
   * selected one alone — the point of a group is to find the hull you are
   * not looking at.
   */
  private packBadges(): void {
    if (this.groups.size === 0) return;
    for (let i = 0; i < this.n; i++) {
      if ((this.flags[i] & (F_OWN | F_HIDDEN)) !== F_OWN) continue;
      const slot = this.groupBadge[i];
      const key = this.badgeKeys[slot];
      if (!key) continue; // a slot with no master draws no badge
      const e = this.sprite(key);
      this.pin(i);
      this.h.markQuads.sprite(
        this.x[i] - BRACKET_R - FLEET_GROUP_BADGE_GAP - e.w / 2,
        this.y[i] + BRACKET_R,
        0, e, 0xffffff, this.alpha[i] * FLEET_GROUP_BADGE_ALPHA,
      );
    }
  }

  /**
   * Counts the friendly marks that rest on one another (R1004).
   *
   * The separation rule of the sim holds two friendly fleets one
   * `separationPx` apart, and it stands aside where the course end is an
   * action point — gatherers on one deposit, hulls in the pens — so a
   * stack still happens there. This is the fallback that says how many.
   *
   * The walk is single pass and claims as it goes: the FIRST mark of a
   * stack in array order leads it and carries the digit, and every mark it
   * claims carries one, which draws nothing. A mark under way is not part
   * of a stack, and neither is a mark the reader cannot see.
   *
   * It allocates nothing: the counts ride a typed array the layer already
   * owns, and the throttle in `advance` keeps the walk off most frames.
   */
  private countStacks(): void {
    const c = this.stackCount;
    this.stackN = this.n;
    c.fill(0, 0, this.n);
    const r = FLEET_STACK.separationPx;
    const r2 = r * r;
    for (let i = 0; i < this.n; i++) {
      if (c[i] !== 0 || !this.stacksHere(i)) continue;
      let n = 1;
      for (let j = i + 1; j < this.n; j++) {
        if (c[j] !== 0 || !this.stacksHere(j)) continue;
        const dx = this.x[j] - this.x[i], dy = this.y[j] - this.y[i];
        if (dx * dx + dy * dy > r2) continue;
        c[j] = 1; // a member: the leader carries the whole count
        n++;
      }
      c[i] = n > 1 ? Math.min(n, FLEET_STACK.max) : 1;
    }
  }

  /** Is this mark an own or friendly hull that RESTS and can be seen? */
  private stacksHere(i: number): boolean {
    if ((this.flags[i] & F_HIDDEN) !== 0) return false;
    const own = (this.flags[i] & F_OWN) !== 0;
    const mate = this.viewerFaction !== 0 && this.faction[i] === this.viewerFaction;
    return (own || mate) && this.speed[i] <= FLEET_STACK.restSpeedPxPerSec;
  }

  /**
   * One digit beside the mark that leads a stack (R1004, §6.1).
   *
   * It draws the damage figure's own glyph cell, scaled to the box the
   * figures state, the way the squad slot of card B09 does. The cell is
   * centred, so the quad's centre goes half a glyph past the offset.
   */
  private packStacks(): void {
    const f = FLEET_STACK;
    const k = f.h / DAMAGE_FIGURE.sizePx;
    for (let i = 0; i < this.n; i++) {
      const n = this.stackCount[i];
      if (n < 2) continue;
      this.pin(i);
      this.packNumeral(i, STACK_KEYS[n - 2], f.dx, f.dy, k, f.color, f.alpha);
    }
  }

  /**
   * The friendly marks (R185, R830, §6.1): what an own hull and a squadmate
   * hull wear beside the silhouette.
   *
   * Two values write nothing here. `white-core` (the own AUTO value) is the
   * white tint and the scale of `packHulls` with the bloom of `packBlooms`
   * under it, and `hollow-hull` is the hollow master; both are hull
   * treatments. Every
   * other value draws its mark here, UNDER the hulls and over the badge, so
   * a hull always sits on top of the thing that marks it.
   *
   * The gate is `F_HIDDEN`, the gate `packHulls` holds: a mark that is not
   * on a hull the reader can see marks nothing.
   */
  private packFleetMarks(): void {
    const ownStyle = OWN_FLEET_MARKS[this.look.ownFleet];
    const mateStyle = SQUAD_FLEET_MARKS[this.look.squadFleet];
    if (!marksAnything(ownStyle) && !marksAnything(mateStyle)) return;
    // A squad change lifts the mark of a new mate with its hull (R1571).
    const changeLift = this.squadLiftNow();
    for (let i = 0; i < this.n; i++) {
      if ((this.flags[i] & F_HIDDEN) !== 0) continue;
      const own = (this.flags[i] & F_OWN) !== 0;
      const mate = !own && (this.flags[i] & F_SQUAD) !== 0;
      if (!own && !mate) continue;
      const style = own ? ownStyle : mateStyle;
      const ink = this.inkAt(i);
      const color = mate && changeLift > 0 ? lift(ink, changeLift) : ink;
      const cells = style.cells;
      this.pin(i);
      for (let c = 0; c < cells.length; c++) {
        const cell = cells[c];
        this.h.markQuads.sprite(
          this.x[i], this.y[i],
          cell.square === 'course' ? this.heading[i] : 0,
          this.sprite(cell.key),
          cell.tint === 'white' ? 0xffffff : color,
          this.alpha[i] * cell.alpha, 1, 0, false,
        );
      }
      if (style.slot) this.packSlotNumeral(i, style.slot, color);
    }
  }

  /**
   * The squad slot beside a squadmate's hull (card B09, R830).
   *
   * It draws the damage figure's own glyph cell, scaled to the height the
   * group badge is set at, so the map bakes no second numeral face. The
   * cell is centred, so the quad's centre goes half a glyph past the card's
   * left edge. A squad of more than nine members prints the highest slot on
   * every member past it, which the doc states.
   *
   * A mate whose slot the clan frame never named draws nothing.
   */
  private packSlotNumeral(i: number, f: SlotNumeral, color: number): void {
    const slot = this.squadSlots?.get(this.ownerPlayer[i]) ?? 0;
    if (slot <= 0) return;
    const k = FLEET_GROUP_BADGE_H / DAMAGE_FIGURE.sizePx;
    this.packNumeral(i, SLOT_KEYS[Math.min(slot, SLOT_NUMERAL_MAX) - 1], f.dx, f.dy, k, color, f.alpha);
  }

  /**
   * One damage figure glyph beside hull `i`, upright, at scale `k`. The
   * cell is centred, so the quad's centre goes half a glyph past `dx`.
   */
  private packNumeral(
    i: number, key: string, dx: number, dy: number, k: number, color: number, alpha: number,
  ): void {
    const e = this.sprite(key);
    this.h.markQuads.sprite(
      this.x[i] + dx + (e.w * k) / 2, this.y[i] + dy,
      0, e, color, this.alpha[i] * alpha, k,
    );
  }

  /**
   * The hulls, last of the layer (§6.1). The silhouette follows `cls` and
   * nothing else (R426): a contact carries a class only while a scan reveal
   * holds it, so an unrevealed contact wears the dart. Your own hull burns
   * white and a little larger; everything else keeps the flat faction
   * colour, a clanmate included.
   */
  private packHulls(): void {
    // The two hull treatments (R185, R830). An own ALTERNATE
    // keeps the plain faction colour and the plain scale, because its mark
    // replaces the white core; a squad ALTERNATE takes the FILLED master,
    // because the hollow one IS the `hollow-hull` value.
    const core = this.look.ownFleet === 'white-core';
    const squadStyle = SQUAD_FLEET_MARKS[this.look.squadFleet];
    const hollowMaster = this.look.squadFleet === 'hollow-hull';
    // The charge pip of card 02 (R1568): on a hull of another faction, at
    // the place `factionmarks.toml` gives astern, upright on the screen.
    const pips = this.look.factionHull === 'pip';
    const astern = FACTION_MARKS.hull.asternPx;
    // The lift of a squad change (R1571): the hulls of the new squad
    // start part of the way toward white and settle, over the value's own
    // lift, so the eye finds them. It is 0 on every other frame.
    const changeLift = this.squadLiftNow();
    this.lightMotion = undefined;
    for (let i = 0; i < this.n; i++) {
      // A hull another layer draws is left out here, whether the fleet is gone
      // (`F_HIDDEN`) or alive with its mark borrowed (`F_HULL_HIDDEN`, R1012).
      if ((this.flags[i] & (F_HIDDEN | F_HULL_HIDDEN)) !== 0) continue;
      const own = (this.flags[i] & F_OWN) !== 0;
      const mate = (this.flags[i] & F_SQUAD) !== 0;
      const hollow = mate && hollowMaster;
      // A contact whose class the server withheld wears the dart of the AUTO
      // set: the dart is the same shape in every set (R835).
      // A hull of the NPC side wears the mark of its kind in the NPC ink,
      // whatever its class (R2637): no livery, no pip, no squad.
      if (this.npc[i] !== NpcKind.NPC_KIND_NONE) {
        this.pin(i);
        if (this.npc[i] === NpcKind.NPC_KIND_PIRATE) { this.packPirate(i); continue; }
        this.h.markQuads.sprite(this.x[i], this.y[i], this.heading[i],
          this.sprite(npcKey(this.npc[i])), NPC_INK.color, this.alpha[i]);
        continue;
      }
      const cls = KNOWN_CLASSES.has(this.cls[i]) ? this.cls[i] : ShipClass.SHIP_CLASS_UNSPECIFIED;
      const e = this.sprite(this.hullKeys.get(cls * 2 + (hollow ? 1 : 0))!);
      const faction = this.inkAt(i);
      let color = faction;
      if (own && core) color = 0xffffff;
      else if (mate && squadStyle.lift !== undefined) color = lift(color, squadStyle.lift);
      if (mate && changeLift > 0) color = lift(color, changeLift);
      const scale = own && core ? FLEET_OWN_HULL_SCALE : 1;
      // CRASH DIVE fades an own hull to a third (R2408).
      const a = this.alpha[i] * this.diveAlpha(i);
      const q = this.h.markQuads;
      this.pin(i);
      if (pips && wearsPip(this.faction[i], this.viewerFaction, own)) {
        const key = this.pipKeys[this.faction[i]];
        if (key !== undefined) {
          const h = this.heading[i];
          q.sprite(this.x[i] - Math.cos(h) * astern, this.y[i] - Math.sin(h) * astern,
            0, this.sprite(key), 0xffffff, this.alpha[i]);
        }
      }
      const row = liveryOfWire(this.livery[i]);
      if (row === null) {
        q.sprite(this.x[i], this.y[i], this.heading[i], e, color, a, scale);
        continue;
      }
      // The livery (R1252, §6.1.6): a dark step and a hollow hull bake into
      // the cell itself; a light step and your own white hull draw the
      // plain master and one mask quad over it, on the same origin.
      const plan = liveryPlan(row, hollow, own && core, color, faction);
      const base = plan.liveried ? this.sprite(this.liveryKey(row.wire, cls, hollow ? 1 : 0, row.code)) : e;
      q.sprite(this.x[i], this.y[i], this.heading[i], base, color, a, scale);
      if (!plan.mask) continue;
      const m = this.sprite(this.liveryKey(row.wire, cls, 2, row.code));
      // A quad is centred on its cell's ink box, and the mask's box is not
      // the hull's: move the mask by the gap between the two box centres,
      // turned with the hull.
      const dx = (m.ox - base.ox) * scale, dy = (m.oy - base.oy) * scale;
      const c = Math.cos(this.heading[i]), s = Math.sin(this.heading[i]);
      q.sprite(
        this.x[i] + dx * c - dy * s, this.y[i] + dx * s + dy * c, this.heading[i],
        m, plan.maskColor, a * plan.maskAlpha, scale,
      );
    }
  }

  /**
   * One liveried key (R1252): master 0 is the filled cell, 1 the hollow cell
   * and 2 the mask cell. The set is the one `rebuildKeys` chose for the class,
   * and the dart stays on the AUTO set.
   */
  private liveryKey(wire: number, cls: number, master: number, code: string): string {
    const slot = (wire * 64 + cls) * 3 + master;
    let key = this.liveryKeys.get(slot);
    if (key === undefined) {
      const style = shipStyleOf(this.look, cls);
      key = master === 2 ? liveryMaskKey(style, cls, code) : liveryHullKey(style, cls, master === 1, code);
      this.liveryKeys.set(slot, key);
    }
    return key;
  }

  // -------------------------------------------------------------------------
  // The aspect chevrons (R2407, §6.1.11, §6.3.3)
  // -------------------------------------------------------------------------

  /** The eased alpha of the rest chevrons, by fleet id. */
  private readonly aspectA = new Map<number, number>();
  /**
   * The gun reach of each slot for the reach gate, or −1 to skip it. A
   * scratch of this frame and no column (R2212): the swap-remove does not
   * move it, because each pack writes it whole before it reads it.
   */
  private readonly aspectReach: number[] = [];
  /** The live hit flashes, by fleet id: the arc, the side and the start. */
  private readonly aspectFlash = new Map<number, AspectFlash>();
  /** The layer clock of the last pack, for the ease of the alpha. */
  private aspectAt = 0;

  /**
   * The chevrons of the struck arc flash (§6.3.3): a primary gun hit on
   * `victim` struck arc `aspect`, fired from (sx, sy). The flash starts
   * after `waitSec`, when the first round of the salvo lands. A bow hit
   * and a hit with no arc flash nothing.
   */
  flashAspect(victim: number, aspect: HitAspect, sx: number, sy: number, waitSec = 0): void {
    if (chevronCount(aspect) === 0) return;
    const i = this.idToIndex.get(victim);
    if (i === undefined) return;
    const side = beamSide(this.heading[i], this.x[i], this.y[i], sx, sy);
    const at = this.time + Math.max(0, waitSec);
    const f = this.aspectFlash.get(victim);
    if (f) { f.aspect = aspect; f.side = side; f.at = at; return; }
    this.aspectFlash.set(victim, { aspect, side, at });
  }

  /**
   * Hull `id` draws its rest chevrons at full alpha now: the gates of
   * `packAspect` with a hostile in reach. The contact card states the
   * aspect under this test alone, so the word and the chevrons show
   * together (R2457).
   */
  chevronsShown(id: number): boolean {
    const i = this.idToIndex.get(id);
    if (i === undefined || !this.bowHeading) return false;
    if (!chevronsReadAt(this.zoom, this.pivot.k)) return false;
    if ((this.flags[i] & (F_HIDDEN | F_ALL_ROUND | F_FADING)) !== 0) return false;
    this.fillAspectReach();
    return inHostileReach(i, this.n, this.x, this.y, this.faction, this.aspectReach);
  }

  /**
   * Packs the chevrons of every hull that draws them: the rest set as one
   * quad that turns with the bow, and the arc of a live flash as one quad
   * a chevron. A hull that reads the bow all round draws none, and under
   * the zoom gate no hull draws any and every rest alpha starts again
   * from 0.
   */
  private packAspect(): void {
    const dt = this.time - this.aspectAt;
    this.aspectAt = this.time;
    if (!this.bowHeading || this.n === 0) return;
    // Under the zoom gate no chevron draws, and every rest alpha drops to
    // 0, so the chevrons ease in when the zoom comes back (R3128).
    if (!chevronsReadAt(this.zoom, this.pivot.k)) { this.aspectA.clear(); return; }
    const a = FLEET_ASPECT;
    const ease = 1 - Math.exp(-dt * 1000 / a.easeMs);
    this.fillAspectReach();
    for (let i = 0; i < this.n; i++) {
      const fl = this.flags[i];
      if ((fl & (F_HIDDEN | F_ALL_ROUND | F_FADING)) !== 0) continue;
      const id = this.id[i];
      const own = (fl & F_OWN) !== 0;
      const threat = inHostileReach(i, this.n, this.x, this.y, this.faction, this.aspectReach);
      const target = chevronTarget(own && id === this.selectedId, threat);
      let rest = this.aspectA.get(id) ?? 0;
      rest += (target - rest) * ease;
      if (target === 0 && rest < 0.01) rest = 0;
      if (rest > 0) this.aspectA.set(id, rest); else this.aspectA.delete(id);
      const flash = this.liveFlash(id);
      if (rest === 0 && flash === null) continue;
      const friendly = own || (this.viewerFaction !== 0 && this.faction[i] === this.viewerFaction);
      const ink = chevronInk(friendly, this.inkAt(i));
      const restA = rest * this.alpha[i];
      const q = this.h.markQuads;
      this.pin(i);
      const gap = flash === null ? 'none' : gapOf(flash.aspect, flash.side);
      if (restA > 0) q.sprite(this.x[i], this.y[i], this.heading[i], this.sprite(CHEVRON_SET_KEYS[gap]), ink, restA);
      if (flash !== null) this.packAspectFlash(i, flash, ink, restA);
    }
    this.pruneAspect();
  }

  /** The flash of `id` while it runs, or null; a finished one is dropped. */
  private liveFlash(id: number): AspectFlash | null {
    const f = this.aspectFlash.get(id);
    if (f === undefined) return null;
    const u = (this.time - f.at) / FLEET_ASPECT.flashSec;
    if (u >= 1) { this.aspectFlash.delete(id); return null; }
    return u >= 0 ? f : null;
  }

  /**
   * The chevrons of the struck arc while its flash runs: each one stands
   * `flashStepPx` nearer the hull at the hit and eases back out, in the
   * shield white over its rest ink, as card 10 draws them.
   */
  private packAspectFlash(i: number, f: AspectFlash, ink: number, restA: number): void {
    const a = FLEET_ASPECT;
    const k = 1 - (this.time - f.at) / a.flashSec;
    const m = this.heading[i] + arcCentre(f.aspect, f.side);
    const ux = Math.cos(m), uy = Math.sin(m);
    const q = this.h.markQuads;
    const n = chevronCount(f.aspect);
    const r0 = firstChevronR(f.aspect);
    for (let c = 0; c < n; c++) {
      const r = r0 + c * a.stepR - a.flashStepPx * k;
      const x = this.x[i] + ux * r, y = this.y[i] + uy * r;
      if (restA > 0) q.sprite(x, y, m + Math.PI, this.sprite(CHEVRON_KEY), ink, restA);
      q.sprite(x, y, m + Math.PI, this.sprite(CHEVRON_FLASH_KEY), a.flashColor,
        a.flashAlpha * k * this.alpha[i]);
    }
  }

  /**
   * The gun reach of every slot for the reach gate: the hook's reach for a
   * class the viewer knows, the widest reach of the catalog for one it does
   * not, and −1 for a contact that fades out.
   */
  private fillAspectReach(): void {
    let widest = -1;
    for (let i = 0; i < this.n; i++) {
      if ((this.flags[i] & F_FADING) !== 0) { this.aspectReach[i] = -1; continue; }
      // The all-round bit stands for DIG IN here: only a fixture class
      // anchors, and `gunRange` answers a SET anchor before the order.
      const active = (this.flags[i] & F_ALL_ROUND) !== 0;
      const known = (this.flags[i] & F_CLASSIFIED) === 0 && KNOWN_CLASSES.has(this.cls[i]);
      if (known) {
        this.aspectReach[i] = this.gunRangeOf(this.cls[i], this.anchor[i], active);
        continue;
      }
      if (widest < 0) widest = this.widestReach();
      this.aspectReach[i] = widest;
    }
  }

  /** The widest gun reach of the catalog, a planted one and DIG IN included. */
  private widestReach(): number {
    let r = 0;
    for (const cls of KNOWN_CLASSES) {
      r = Math.max(r,
        this.gunRangeOf(cls, AnchorState.ANCHOR_STATE_UNSPECIFIED, true),
        this.gunRangeOf(cls, AnchorState.ANCHOR_STATE_SET, true));
    }
    return r;
  }

  /** Drops the eased alphas and the flashes of hulls the layer no longer holds. */
  private pruneAspect(): void {
    if (this.aspectA.size + this.aspectFlash.size <= 2 * this.n + 16) return;
    for (const id of this.aspectA.keys()) if (!this.idToIndex.has(id)) this.aspectA.delete(id);
    for (const id of this.aspectFlash.keys()) if (!this.idToIndex.has(id)) this.aspectFlash.delete(id);
  }

  /**
   * THE GATHER FIGURES (card 09 of mockup round 055, R1123, §6.1.4).
   *
   * A small figure rises beside the readout lanes of an own Dredger and
   * fades: the chip of the commodity, then `+N` in the commodity hue. The
   * shaft wears the HOLLOW chip and a smaller, dimmer figure, so it reads
   * apart by shape (R715). `FULL` prints one time in the cargo brown.
   *
   * It is the cousin of the damage figure and it rides that machinery
   * (R538): the same atlas glyph cells, one quad a glyph, and the SCREEN-px
   * rule — the figure holds its size at every zoom, so every quad carries
   * `F_SCREEN` and the offsets divide by the zoom the shader then undoes.
   * `xPx` and `yPx` are WORLD lengths against the hull, so the figure keeps
   * its place beside the lanes as the map scales.
   *
   * The figure FOLLOWS its hull while the layer still holds it, the way a
   * damage figure follows its victim; a hull that went off the roster keeps
   * the place it was raised at.
   */
  private packGatherFigures(): void {
    const g = GATHER_FIGURE;
    const n = this.gatherFigures.count;
    if (n === 0) return;
    const items = this.gatherFigures.items();
    const zoom = this.zoom;
    for (let i = 0; i < n; i++) {
      const it = items[i];
      const u = it.age / it.life;
      if (u < 0 || u >= 1) continue;
      const full = it.kind === GatherFigureKind.Full;
      const out = full ? g.fullFadeOut : g.fadeOut;
      const alpha = Math.min(1, u / g.fadeIn) * Math.min(1, (1 - u) / out);
      if (alpha <= 0) continue;
      // The rise: the card's own ease, which starts fast and settles.
      const k = easeOutQuad(u);
      const at = this.idToIndex.get(it.fleetId);
      const x = at === undefined ? it.x : this.x[at];
      const y = at === undefined ? it.y : this.y[at];
      // The place beside the lanes follows the mark scale (R1981); the
      // figure keeps its screen size, as a damage figure does.
      const mk = this.pivot.k;
      const dx = g.xPx * zoom * mk;
      const dy = g.yPx * zoom * mk - g.risePx * k;
      if (full) {
        this.putFigureText(x, y, zoom, dx + g.fullDxPx, dy, FULL_KEYS,
          g.sizePx, FLEET_CARGO_COLOR, alpha * g.alpha);
        continue;
      }
      const shaft = it.shaft;
      const px = shaft ? g.shaftSizePx : g.sizePx;
      const ink = depositInk(it.resource);
      const a = alpha * (shaft ? g.shaftAlpha : g.alpha);
      // The chip stands before the digits, at the middle of the line.
      const chip = this.sprite(shaft ? GATHER_CHIP_HOLLOW_KEY : GATHER_CHIP_KEY);
      this.markQuads.sprite(
        x + dx / zoom, y + dy / zoom, 0, chip, ink, alpha * g.chipAlpha,
        chip.h > 0 ? (g.chipPx * (px / g.sizePx)) / chip.h : 1, F_SCREEN,
      );
      this.putFigureText(x, y, zoom, dx + g.textGap, dy,
        this.figureKeys(it.n), px, ink, a);
    }
  }

  /**
   * One string of the figure alphabet, left aligned at a screen offset.
   *
   * The face is monospaced, so the layout is arithmetic on
   * `damageAdvancePx` and the layer measures nothing (R538).
   */
  private putFigureText(
    x: number, y: number, zoom: number, dx: number, dy: number,
    keys: readonly string[], px: number, ink: number, alpha: number,
  ): void {
    const adv = damageAdvancePx(px);
    const scale = px / DAMAGE_FIGURE.sizePx;
    for (let i = 0; i < keys.length; i++) {
      this.markQuads.sprite(
        x + (dx + adv * (i + 0.5)) / zoom, y + dy / zoom, 0,
        this.sprite(keys[i]), ink, alpha, scale, F_SCREEN,
      );
    }
  }

  /**
   * The glyph keys of `+N`, cached by the number.
   *
   * A figure is raised at most once a wall second a field, so the cache is
   * small; it keeps the pack itself free of string work (§10.4).
   */
  private figureKeys(n: number): readonly string[] {
    let keys = this.figureCache.get(n);
    if (keys === undefined) {
      keys = glyphKeys(DAMAGE_FIGURE.weight, `+${n}`);
      this.figureCache.set(n, keys);
    }
    return keys;
  }

  private readonly figureCache = new Map<number, readonly string[]>();

  // -- the signature order looks (R2408, ui-map-interface.md §6.1.12) -------

  /**
   * One hull as the signature layer draws it: its place, its drawn bow, its
   * faction, its class, the own bit, its sonar reach, the course end of an
   * own hull, the mark scale and the hollow hull cell of its class,
   * written into `p`. It answers false when the layer holds no such hull.
   */
  signaturePose(fleetId: number, p: SignaturePose): boolean {
    const i = this.idToIndex.get(fleetId);
    if (i === undefined) return false;
    const cls = KNOWN_CLASSES.has(this.cls[i]) ? this.cls[i] : ShipClass.SHIP_CLASS_UNSPECIFIED;
    p.x = this.x[i]; p.y = this.y[i]; p.head = this.heading[i];
    p.faction = this.faction[i]; p.cls = this.cls[i];
    p.own = (this.flags[i] & F_OWN) !== 0;
    p.alpha = this.alpha[i];
    p.sonarPx = this.sonarPx[i];
    p.tx = this.tx[i]; p.ty = this.ty[i];
    p.k = this.pivot.k;
    p.cell = this.hullKeys.get(cls * 2 + 1) ?? '';
    return true;
  }

  /**
   * The nearest visible hull of another faction than `faction` inside
   * `radius` of a point, or null. CANISTER's iris and BROADSIDE's ports
   * read it (R2408). One object is reused.
   */
  nearestFoe(x: number, y: number, faction: number, radius: number): { x: number; y: number } | null {
    let bestI = -1;
    let bestD = radius * radius;
    for (let i = 0; i < this.n; i++) {
      if (this.faction[i] === faction || this.alpha[i] < FAINT_ALPHA) continue;
      const dx = this.x[i] - x, dy = this.y[i] - y;
      const d = dx * dx + dy * dy;
      if (d < bestD) { bestD = d; bestI = i; }
    }
    if (bestI < 0) return null;
    this.foeBuf.x = this.x[bestI]; this.foeBuf.y = this.y[bestI];
    return this.foeBuf;
  }

  private readonly foeBuf = { x: 0, y: 0 };

  /**
   * Every visible hull of another faction than `faction` inside `radius`
   * of a point, as x, y pairs into `out`, in the order of the layer, at
   * most `out.length / 2`. It answers the count. The Thrummer's discharge
   * draws an arc to each one, as the server pulse hits every hull in its
   * reach (R2719). It allocates nothing.
   */
  foesWithin(x: number, y: number, faction: number, radius: number, out: Float32Array): number {
    const max = out.length >> 1;
    const r2 = radius * radius;
    let n = 0;
    for (let i = 0; i < this.n && n < max; i++) {
      if (this.faction[i] === faction || this.alpha[i] < FAINT_ALPHA) continue;
      const dx = this.x[i] - x, dy = this.y[i] - y;
      if (dx * dx + dy * dy > r2) continue;
      out[2 * n] = this.x[i];
      out[2 * n + 1] = this.y[i];
      n++;
    }
    return n;
  }

  /** The share of the CRASH DIVE fade of hull `i`, 0 to 1; 0 off an own hull. */
  private diveShare(i: number): number {
    if (this.signature === null || (this.flags[i] & F_OWN) === 0) return 0;
    return this.signature.diveShare(this.id[i]);
  }

  /** The alpha factor CRASH DIVE puts on hull `i`: 1 down to `dive_hull_alpha`. */
  private diveAlpha(i: number): number {
    const d = this.diveShare(i);
    return d > 0 ? 1 - (1 - SIGNATURE.diveHullAlpha) * d : 1;
  }

  /**
   * CANISTER's iris (card 13): the hatch of the dead zone disc parts in a
   * wedge toward the nearest hostile inside the disc, and the rim of the
   * wedge lights in the faction ink. The wedge stays open for the whole
   * window (R2856) and keeps its last bearing while it closes at the end.
   * It answers whether it drew the hatch.
   */
  private packIris(sel: number, x: number, y: number, r: number): boolean {
    const open = this.signature?.irisOpen(this.id[sel]) ?? 0;
    if (!(open > 0)) { this.irisAng = NaN; return false; }
    const foe = this.nearestFoe(x, y, this.faction[sel], r);
    if (foe !== null && (foe.x !== x || foe.y !== y)) this.irisAng = Math.atan2(foe.y - y, foe.x - x);
    if (Number.isNaN(this.irisAng)) return false;
    const sg = SIGNATURE, s = FLEET_REACH_RING;
    const half = sg.irisHalfDeg * open * Math.PI / 180;
    const ang = this.irisAng;
    rearHatch(x, y, r, 0, ang, (half * 360) / Math.PI, s.deadHatchStep, this.rearLine);
    const ink = this.inkAt(sel);
    this.deadRings.arc(x, y, r, sg.irisRimWidthPx, ang - half, 2 * half, ink, sg.irisRimAlpha * open);
    for (let side = -1; side <= 1; side += 2) {
      const e = ang + side * half;
      this.deadSegs.line(x, y, x + Math.cos(e) * r, y + Math.sin(e) * r,
        sg.irisEdgeWidthPx, ink, sg.irisEdgeAlpha * open);
    }
    return true;
  }

  /** The last bearing of CANISTER's wedge, NaN when no iris is open. */
  private irisAng = NaN;

  /** The four corner brackets of the selected hull (`fleets.ts:1269-1275`). */
  private packBracket(): void {
    const sel = this.idToIndex.get(this.selectedId);
    if (sel === undefined) return;
    this.pin(sel);
    cornerBrackets(this.h.bracketSegs, this.x[sel], this.y[sel], BRACKET_R, BRACKET_ARM,
      BRACKET_WIDTH, 0xffffff, BRACKET_ALPHA, F_ROUND);
  }

  draw(ctx: DrawCtx): void {
    this.wakeSegs.flush(ctx.cam);
    this.auraQuads.flush(ctx.cam);
    this.tenderSegs.flush(ctx.cam);
    this.tenderQuads.flush(ctx.cam);
    // A tube reload look whose card draws under the hull stands under the
    // bloom, the card's order (R1474).
    this.reloadUnderRings.flush(ctx.cam);
    this.reloadUnderSegs.flush(ctx.cam);
    this.bloomQuads.flush(ctx.cam);
    // The hold-fire look draws over the bloom and under the bracket, the
    // card's order (R1439).
    this.holdRings.flush(ctx.cam);
    this.holdSegs.flush(ctx.cam);
    this.orderSegs.flush(ctx.cam);
    this.rings.flush(ctx.cam);
    this.anchorQuads.flush(ctx.cam);
    this.anchorSegs.flush(ctx.cam);
    this.bracketSegs.flush(ctx.cam);
    this.barSegs.flush(ctx.cam);
    this.markQuads.flush(ctx.cam);
    // The boarding look draws over the hulls, the order of card 01 (R2721).
    this.boardSegs.flush(ctx.cam);
    this.boardRings.flush(ctx.cam);
    // A tube reload look whose card draws over the hull stands over the
    // hull, the bracket, the badge and the bars, the card's order (R1474).
    this.reloadGround.flush(ctx.cam);
    this.reloadSegs.flush(ctx.cam);
    this.reloadRings.flush(ctx.cam);
    this.reloadQuads.flush(ctx.cam);
  }
}

/**
 * The `deadzone` slot (R1472, §6.1): the dead zone disc of the selected own
 * hull, under every other mark of the map.
 *
 * It holds no state of its own. The fleet layer packs its two sinks in its
 * own `update`, and the engine runs every `update` before any `draw`, so
 * the disc stands where the hull stands this frame. The pass is visible
 * only while the fleet layer is as well, so a hidden fleet layer never
 * leaves a stale disc on the map. Its own switch is the film's: a film
 * borrows the `fleets` slot and hides this one (R1472).
 */
export class DeadZonePass implements Layer {
  readonly name = 'deadzone';
  private shown = true;

  constructor(
    private readonly owner: FleetLayer,
    private readonly rings: CircleSink,
    private readonly segs: SegmentSink,
  ) {}

  get visible(): boolean { return this.shown && this.owner.visible; }
  setVisible(v: boolean): void { this.shown = v; }

  update(_frame: FrameInfo): void { /* the fleet layer packs the sinks */ }

  draw(ctx: DrawCtx): void {
    this.rings.flush(ctx.cam);
    this.segs.flush(ctx.cam);
  }
}
